import React__default, { createContext, useReducer, useEffect, useRef, useContext, useState, createElement, Fragment as Fragment$1 } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import { useTranslation, initReactI18next, I18nextProvider } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import SvgIcon from '@mui/material/SvgIcon';
import dayjs from 'dayjs';
import pt from 'dayjs/locale/pt-br';
import es from 'dayjs/locale/es';
import DialogActions from '@mui/material/DialogActions';
import OutsideClickHandler from 'react-outside-click-handler';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (it) return (it = it.call(o)).next.bind(it);
  if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
    if (it) o = it;
    var i = 0;
    return function () {
      if (i >= o.length) return {
        done: true
      };
      return {
        done: false,
        value: o[i++]
      };
    };
  }
  throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var initialStore = {
  config: {},
  breakpoint: '',
  view_mode: 'default',
  quotation: {
    destination: {
      value: 0,
      label: ''
    },
    dates: {
      date_from: '',
      date_to: ''
    },
    paxs: {
      ages_1: 0,
      ages_2: 0
    },
    submitted: false,
    tooltip: {
      visibility: false,
      title: ''
    }
  }
};
var types = {
  setConfig: 'config/set',
  setBreakpoint: 'breakpoint/set',
  setViewMode: 'view_mode/set',
  setQuotation: 'quotation/set',
  setQuotationSubmitted: 'quotation/submitted/set',
  setQuotationTooltip: 'quotation/tooltip/set'
};
var storeReducer = function storeReducer(state, action) {
  switch (action.type) {
    case types.setConfig:
      return _extends({}, state, {
        config: action.payload
      });
    case types.setBreakpoint:
      return _extends({}, state, {
        breakpoint: action.payload
      });
    case types.setQuotation:
      return _extends({}, state, {
        quotation: action.payload
      });
    default:
      return state;
  }
};

var StoreContext = createContext();
var StoreProvider = function StoreProvider(_ref) {
  var children = _ref.children;
  var _useReducer = useReducer(storeReducer, initialStore),
    store = _useReducer[0],
    dispatch = _useReducer[1];
  return /*#__PURE__*/React__default.createElement(StoreContext.Provider, {
    value: [store, dispatch]
  }, children);
};

/*
Copyright (c) 2014, Yahoo! Inc. All rights reserved.
Copyrights licensed under the New BSD License.
See the accompanying LICENSE file for terms.
*/

var match = matchQuery;
var parse = parseQuery;

// -----------------------------------------------------------------------------

var RE_MEDIA_QUERY     = /(?:(only|not)?\s*([^\s\(\)]+)(?:\s*and)?\s*)?(.+)?/i,
    RE_MQ_EXPRESSION   = /\(\s*([^\s\:\)]+)\s*(?:\:\s*([^\s\)]+))?\s*\)/,
    RE_MQ_FEATURE      = /^(?:(min|max)-)?(.+)/,
    RE_LENGTH_UNIT     = /(em|rem|px|cm|mm|in|pt|pc)?$/,
    RE_RESOLUTION_UNIT = /(dpi|dpcm|dppx)?$/;

function matchQuery(mediaQuery, values) {
    return parseQuery(mediaQuery).some(function (query) {
        var inverse = query.inverse;

        // Either the parsed or specified `type` is "all", or the types must be
        // equal for a match.
        var typeMatch = query.type === 'all' || values.type === query.type;

        // Quit early when `type` doesn't match, but take "not" into account.
        if ((typeMatch && inverse) || !(typeMatch || inverse)) {
            return false;
        }

        var expressionsMatch = query.expressions.every(function (expression) {
            var feature  = expression.feature,
                modifier = expression.modifier,
                expValue = expression.value,
                value    = values[feature];

            // Missing or falsy values don't match.
            if (!value) { return false; }

            switch (feature) {
                case 'orientation':
                case 'scan':
                    return value.toLowerCase() === expValue.toLowerCase();

                case 'width':
                case 'height':
                case 'device-width':
                case 'device-height':
                    expValue = toPx(expValue);
                    value    = toPx(value);
                    break;

                case 'resolution':
                    expValue = toDpi(expValue);
                    value    = toDpi(value);
                    break;

                case 'aspect-ratio':
                case 'device-aspect-ratio':
                case /* Deprecated */ 'device-pixel-ratio':
                    expValue = toDecimal(expValue);
                    value    = toDecimal(value);
                    break;

                case 'grid':
                case 'color':
                case 'color-index':
                case 'monochrome':
                    expValue = parseInt(expValue, 10) || 1;
                    value    = parseInt(value, 10) || 0;
                    break;
            }

            switch (modifier) {
                case 'min': return value >= expValue;
                case 'max': return value <= expValue;
                default   : return value === expValue;
            }
        });

        return (expressionsMatch && !inverse) || (!expressionsMatch && inverse);
    });
}

function parseQuery(mediaQuery) {
    return mediaQuery.split(',').map(function (query) {
        query = query.trim();

        var captures    = query.match(RE_MEDIA_QUERY),
            modifier    = captures[1],
            type        = captures[2],
            expressions = captures[3] || '',
            parsed      = {};

        parsed.inverse = !!modifier && modifier.toLowerCase() === 'not';
        parsed.type    = type ? type.toLowerCase() : 'all';

        // Split expressions into a list.
        expressions = expressions.match(/\([^\)]+\)/g) || [];

        parsed.expressions = expressions.map(function (expression) {
            var captures = expression.match(RE_MQ_EXPRESSION),
                feature  = captures[1].toLowerCase().match(RE_MQ_FEATURE);

            return {
                modifier: feature[1],
                feature : feature[2],
                value   : captures[2]
            };
        });

        return parsed;
    });
}

// -- Utilities ----------------------------------------------------------------

function toDecimal(ratio) {
    var decimal = Number(ratio),
        numbers;

    if (!decimal) {
        numbers = ratio.match(/^(\d+)\s*\/\s*(\d+)$/);
        decimal = numbers[1] / numbers[2];
    }

    return decimal;
}

function toDpi(resolution) {
    var value = parseFloat(resolution),
        units = String(resolution).match(RE_RESOLUTION_UNIT)[1];

    switch (units) {
        case 'dpcm': return value / 2.54;
        case 'dppx': return value * 96;
        default    : return value;
    }
}

function toPx(length) {
    var value = parseFloat(length),
        units = String(length).match(RE_LENGTH_UNIT)[1];

    switch (units) {
        case 'em' : return value * 16;
        case 'rem': return value * 16;
        case 'cm' : return value * 96 / 2.54;
        case 'mm' : return value * 96 / 2.54 / 10;
        case 'in' : return value * 96;
        case 'pt' : return value * 72;
        case 'pc' : return value * 72 / 12;
        default   : return value;
    }
}

var cssMediaquery = {
	match: match,
	parse: parse
};

var staticMatch = cssMediaquery.match;
var dynamicMatch = typeof window !== 'undefined' ? window.matchMedia : null;

// our fake MediaQueryList
function Mql(query, values, forceStatic){
  var self = this;
  var mql;

  // matchMedia will return null in FF when it's called in a hidden iframe
  // ref: https://stackoverflow.com/a/12330568
  if(dynamicMatch && !forceStatic) mql = dynamicMatch.call(window, query);

  if (mql) {
    this.matches = mql.matches;
    this.media = mql.media;
    // TODO: is there a time it makes sense to remove this listener?
    mql.addListener(update);
  } else {
    this.matches = staticMatch(query, values);
    this.media = query;
  }

  this.addListener = addListener;
  this.removeListener = removeListener;
  this.dispose = dispose;

  function addListener(listener){
    if(mql){
      mql.addListener(listener);
    }
  }

  function removeListener(listener){
    if(mql){
      mql.removeListener(listener);
    }
  }

  // update ourselves!
  function update(evt){
    self.matches = evt.matches;
    self.media = evt.media;
  }

  function dispose(){
    if(mql){
      mql.removeListener(update);
    }
  }
}

function matchMedia(query, values, forceStatic){
  return new Mql(query, values, forceStatic);
}

var matchmediaquery = matchMedia;

/* eslint-disable no-var, prefer-template */
var uppercasePattern = /[A-Z]/g;
var msPattern = /^ms-/;
var cache = {};

function toHyphenLower(match) {
  return '-' + match.toLowerCase()
}

function hyphenateStyleName(name) {
  if (cache.hasOwnProperty(name)) {
    return cache[name]
  }

  var hName = name.replace(uppercasePattern, toHyphenLower);
  return (cache[name] = msPattern.test(hName) ? '-' + hName : hName)
}

function shallowEqualObjects(objA, objB) {
  if (objA === objB) {
    return true;
  }
  if (!objA || !objB) {
    return false;
  }
  const aKeys = Object.keys(objA);
  const bKeys = Object.keys(objB);
  const len = aKeys.length;
  if (bKeys.length !== len) {
    return false;
  }
  for (let i = 0; i < len; i++) {
    const key = aKeys[i];
    if (objA[key] !== objB[key] || !Object.prototype.hasOwnProperty.call(objB, key)) {
      return false;
    }
  }
  return true;
}
//# sourceMappingURL=index.modern.mjs.map

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

/** @license React v16.13.1
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var b="function"===typeof Symbol&&Symbol.for,c=b?Symbol.for("react.element"):60103,d=b?Symbol.for("react.portal"):60106,e=b?Symbol.for("react.fragment"):60107,f=b?Symbol.for("react.strict_mode"):60108,g=b?Symbol.for("react.profiler"):60114,h=b?Symbol.for("react.provider"):60109,k=b?Symbol.for("react.context"):60110,l=b?Symbol.for("react.async_mode"):60111,m=b?Symbol.for("react.concurrent_mode"):60111,n=b?Symbol.for("react.forward_ref"):60112,p=b?Symbol.for("react.suspense"):60113,q=b?
Symbol.for("react.suspense_list"):60120,r=b?Symbol.for("react.memo"):60115,t=b?Symbol.for("react.lazy"):60116,v=b?Symbol.for("react.block"):60121,w=b?Symbol.for("react.fundamental"):60117,x=b?Symbol.for("react.responder"):60118,y=b?Symbol.for("react.scope"):60119;
function z(a){if("object"===typeof a&&null!==a){var u=a.$$typeof;switch(u){case c:switch(a=a.type,a){case l:case m:case e:case g:case f:case p:return a;default:switch(a=a&&a.$$typeof,a){case k:case n:case t:case r:case h:return a;default:return u}}case d:return u}}}function A(a){return z(a)===m}var AsyncMode=l;var ConcurrentMode=m;var ContextConsumer=k;var ContextProvider=h;var Element=c;var ForwardRef=n;var Fragment=e;var Lazy=t;var Memo=r;var Portal=d;
var Profiler=g;var StrictMode=f;var Suspense=p;var isAsyncMode=function(a){return A(a)||z(a)===l};var isConcurrentMode=A;var isContextConsumer=function(a){return z(a)===k};var isContextProvider=function(a){return z(a)===h};var isElement=function(a){return "object"===typeof a&&null!==a&&a.$$typeof===c};var isForwardRef=function(a){return z(a)===n};var isFragment=function(a){return z(a)===e};var isLazy=function(a){return z(a)===t};
var isMemo=function(a){return z(a)===r};var isPortal=function(a){return z(a)===d};var isProfiler=function(a){return z(a)===g};var isStrictMode=function(a){return z(a)===f};var isSuspense=function(a){return z(a)===p};
var isValidElementType=function(a){return "string"===typeof a||"function"===typeof a||a===e||a===m||a===g||a===f||a===p||a===q||"object"===typeof a&&null!==a&&(a.$$typeof===t||a.$$typeof===r||a.$$typeof===h||a.$$typeof===k||a.$$typeof===n||a.$$typeof===w||a.$$typeof===x||a.$$typeof===y||a.$$typeof===v)};var typeOf=z;

var reactIs_production_min = {
	AsyncMode: AsyncMode,
	ConcurrentMode: ConcurrentMode,
	ContextConsumer: ContextConsumer,
	ContextProvider: ContextProvider,
	Element: Element,
	ForwardRef: ForwardRef,
	Fragment: Fragment,
	Lazy: Lazy,
	Memo: Memo,
	Portal: Portal,
	Profiler: Profiler,
	StrictMode: StrictMode,
	Suspense: Suspense,
	isAsyncMode: isAsyncMode,
	isConcurrentMode: isConcurrentMode,
	isContextConsumer: isContextConsumer,
	isContextProvider: isContextProvider,
	isElement: isElement,
	isForwardRef: isForwardRef,
	isFragment: isFragment,
	isLazy: isLazy,
	isMemo: isMemo,
	isPortal: isPortal,
	isProfiler: isProfiler,
	isStrictMode: isStrictMode,
	isSuspense: isSuspense,
	isValidElementType: isValidElementType,
	typeOf: typeOf
};

var reactIs_development = createCommonjsModule(function (module, exports) {



if (process.env.NODE_ENV !== "production") {
  (function() {

// The Symbol used to tag the ReactElement-like types. If there is no native Symbol
// nor polyfill, then a plain number is used for performance.
var hasSymbol = typeof Symbol === 'function' && Symbol.for;
var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for('react.element') : 0xeac7;
var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for('react.portal') : 0xeaca;
var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for('react.fragment') : 0xeacb;
var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for('react.strict_mode') : 0xeacc;
var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for('react.profiler') : 0xead2;
var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for('react.provider') : 0xeacd;
var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for('react.context') : 0xeace; // TODO: We don't use AsyncMode or ConcurrentMode anymore. They were temporary
// (unstable) APIs that have been removed. Can we remove the symbols?

var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for('react.async_mode') : 0xeacf;
var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for('react.concurrent_mode') : 0xeacf;
var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for('react.suspense') : 0xead1;
var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for('react.suspense_list') : 0xead8;
var REACT_MEMO_TYPE = hasSymbol ? Symbol.for('react.memo') : 0xead3;
var REACT_LAZY_TYPE = hasSymbol ? Symbol.for('react.lazy') : 0xead4;
var REACT_BLOCK_TYPE = hasSymbol ? Symbol.for('react.block') : 0xead9;
var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for('react.fundamental') : 0xead5;
var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for('react.responder') : 0xead6;
var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for('react.scope') : 0xead7;

function isValidElementType(type) {
  return typeof type === 'string' || typeof type === 'function' || // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
  type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === 'object' && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE || type.$$typeof === REACT_BLOCK_TYPE);
}

function typeOf(object) {
  if (typeof object === 'object' && object !== null) {
    var $$typeof = object.$$typeof;

    switch ($$typeof) {
      case REACT_ELEMENT_TYPE:
        var type = object.type;

        switch (type) {
          case REACT_ASYNC_MODE_TYPE:
          case REACT_CONCURRENT_MODE_TYPE:
          case REACT_FRAGMENT_TYPE:
          case REACT_PROFILER_TYPE:
          case REACT_STRICT_MODE_TYPE:
          case REACT_SUSPENSE_TYPE:
            return type;

          default:
            var $$typeofType = type && type.$$typeof;

            switch ($$typeofType) {
              case REACT_CONTEXT_TYPE:
              case REACT_FORWARD_REF_TYPE:
              case REACT_LAZY_TYPE:
              case REACT_MEMO_TYPE:
              case REACT_PROVIDER_TYPE:
                return $$typeofType;

              default:
                return $$typeof;
            }

        }

      case REACT_PORTAL_TYPE:
        return $$typeof;
    }
  }

  return undefined;
} // AsyncMode is deprecated along with isAsyncMode

var AsyncMode = REACT_ASYNC_MODE_TYPE;
var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
var ContextConsumer = REACT_CONTEXT_TYPE;
var ContextProvider = REACT_PROVIDER_TYPE;
var Element = REACT_ELEMENT_TYPE;
var ForwardRef = REACT_FORWARD_REF_TYPE;
var Fragment = REACT_FRAGMENT_TYPE;
var Lazy = REACT_LAZY_TYPE;
var Memo = REACT_MEMO_TYPE;
var Portal = REACT_PORTAL_TYPE;
var Profiler = REACT_PROFILER_TYPE;
var StrictMode = REACT_STRICT_MODE_TYPE;
var Suspense = REACT_SUSPENSE_TYPE;
var hasWarnedAboutDeprecatedIsAsyncMode = false; // AsyncMode should be deprecated

function isAsyncMode(object) {
  {
    if (!hasWarnedAboutDeprecatedIsAsyncMode) {
      hasWarnedAboutDeprecatedIsAsyncMode = true; // Using console['warn'] to evade Babel and ESLint

      console['warn']('The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 17+. Update your code to use ' + 'ReactIs.isConcurrentMode() instead. It has the exact same API.');
    }
  }

  return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
}
function isConcurrentMode(object) {
  return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
}
function isContextConsumer(object) {
  return typeOf(object) === REACT_CONTEXT_TYPE;
}
function isContextProvider(object) {
  return typeOf(object) === REACT_PROVIDER_TYPE;
}
function isElement(object) {
  return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
}
function isForwardRef(object) {
  return typeOf(object) === REACT_FORWARD_REF_TYPE;
}
function isFragment(object) {
  return typeOf(object) === REACT_FRAGMENT_TYPE;
}
function isLazy(object) {
  return typeOf(object) === REACT_LAZY_TYPE;
}
function isMemo(object) {
  return typeOf(object) === REACT_MEMO_TYPE;
}
function isPortal(object) {
  return typeOf(object) === REACT_PORTAL_TYPE;
}
function isProfiler(object) {
  return typeOf(object) === REACT_PROFILER_TYPE;
}
function isStrictMode(object) {
  return typeOf(object) === REACT_STRICT_MODE_TYPE;
}
function isSuspense(object) {
  return typeOf(object) === REACT_SUSPENSE_TYPE;
}

exports.AsyncMode = AsyncMode;
exports.ConcurrentMode = ConcurrentMode;
exports.ContextConsumer = ContextConsumer;
exports.ContextProvider = ContextProvider;
exports.Element = Element;
exports.ForwardRef = ForwardRef;
exports.Fragment = Fragment;
exports.Lazy = Lazy;
exports.Memo = Memo;
exports.Portal = Portal;
exports.Profiler = Profiler;
exports.StrictMode = StrictMode;
exports.Suspense = Suspense;
exports.isAsyncMode = isAsyncMode;
exports.isConcurrentMode = isConcurrentMode;
exports.isContextConsumer = isContextConsumer;
exports.isContextProvider = isContextProvider;
exports.isElement = isElement;
exports.isForwardRef = isForwardRef;
exports.isFragment = isFragment;
exports.isLazy = isLazy;
exports.isMemo = isMemo;
exports.isPortal = isPortal;
exports.isProfiler = isProfiler;
exports.isStrictMode = isStrictMode;
exports.isSuspense = isSuspense;
exports.isValidElementType = isValidElementType;
exports.typeOf = typeOf;
  })();
}
});

var reactIs = createCommonjsModule(function (module) {

if (process.env.NODE_ENV === 'production') {
  module.exports = reactIs_production_min;
} else {
  module.exports = reactIs_development;
}
});

/*
object-assign
(c) Sindre Sorhus
@license MIT
*/
/* eslint-disable no-unused-vars */
var getOwnPropertySymbols = Object.getOwnPropertySymbols;
var hasOwnProperty = Object.prototype.hasOwnProperty;
var propIsEnumerable = Object.prototype.propertyIsEnumerable;

function toObject(val) {
	if (val === null || val === undefined) {
		throw new TypeError('Object.assign cannot be called with null or undefined');
	}

	return Object(val);
}

function shouldUseNative() {
	try {
		if (!Object.assign) {
			return false;
		}

		// Detect buggy property enumeration order in older V8 versions.

		// https://bugs.chromium.org/p/v8/issues/detail?id=4118
		var test1 = new String('abc');  // eslint-disable-line no-new-wrappers
		test1[5] = 'de';
		if (Object.getOwnPropertyNames(test1)[0] === '5') {
			return false;
		}

		// https://bugs.chromium.org/p/v8/issues/detail?id=3056
		var test2 = {};
		for (var i = 0; i < 10; i++) {
			test2['_' + String.fromCharCode(i)] = i;
		}
		var order2 = Object.getOwnPropertyNames(test2).map(function (n) {
			return test2[n];
		});
		if (order2.join('') !== '0123456789') {
			return false;
		}

		// https://bugs.chromium.org/p/v8/issues/detail?id=3056
		var test3 = {};
		'abcdefghijklmnopqrst'.split('').forEach(function (letter) {
			test3[letter] = letter;
		});
		if (Object.keys(Object.assign({}, test3)).join('') !==
				'abcdefghijklmnopqrst') {
			return false;
		}

		return true;
	} catch (err) {
		// We don't expect any of the above to throw, but better to be safe.
		return false;
	}
}

var objectAssign = shouldUseNative() ? Object.assign : function (target, source) {
	var from;
	var to = toObject(target);
	var symbols;

	for (var s = 1; s < arguments.length; s++) {
		from = Object(arguments[s]);

		for (var key in from) {
			if (hasOwnProperty.call(from, key)) {
				to[key] = from[key];
			}
		}

		if (getOwnPropertySymbols) {
			symbols = getOwnPropertySymbols(from);
			for (var i = 0; i < symbols.length; i++) {
				if (propIsEnumerable.call(from, symbols[i])) {
					to[symbols[i]] = from[symbols[i]];
				}
			}
		}
	}

	return to;
};

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var ReactPropTypesSecret = 'SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED';

var ReactPropTypesSecret_1 = ReactPropTypesSecret;

var has = Function.call.bind(Object.prototype.hasOwnProperty);

var printWarning = function() {};

if (process.env.NODE_ENV !== 'production') {
  var ReactPropTypesSecret$1 = ReactPropTypesSecret_1;
  var loggedTypeFailures = {};
  var has$1 = has;

  printWarning = function(text) {
    var message = 'Warning: ' + text;
    if (typeof console !== 'undefined') {
      console.error(message);
    }
    try {
      // --- Welcome to debugging React ---
      // This error was thrown as a convenience so that you can use this stack
      // to find the callsite that caused this warning to fire.
      throw new Error(message);
    } catch (x) { /**/ }
  };
}

/**
 * Assert that the values match with the type specs.
 * Error messages are memorized and will only be shown once.
 *
 * @param {object} typeSpecs Map of name to a ReactPropType
 * @param {object} values Runtime values that need to be type-checked
 * @param {string} location e.g. "prop", "context", "child context"
 * @param {string} componentName Name of the component for error messages.
 * @param {?Function} getStack Returns the component stack.
 * @private
 */
function checkPropTypes(typeSpecs, values, location, componentName, getStack) {
  if (process.env.NODE_ENV !== 'production') {
    for (var typeSpecName in typeSpecs) {
      if (has$1(typeSpecs, typeSpecName)) {
        var error;
        // Prop type validation may throw. In case they do, we don't want to
        // fail the render phase where it didn't fail before. So we log it.
        // After these have been cleaned up, we'll let them throw.
        try {
          // This is intentionally an invariant that gets caught. It's the same
          // behavior as without this statement except with a better message.
          if (typeof typeSpecs[typeSpecName] !== 'function') {
            var err = Error(
              (componentName || 'React class') + ': ' + location + ' type `' + typeSpecName + '` is invalid; ' +
              'it must be a function, usually from the `prop-types` package, but received `' + typeof typeSpecs[typeSpecName] + '`.' +
              'This often happens because of typos such as `PropTypes.function` instead of `PropTypes.func`.'
            );
            err.name = 'Invariant Violation';
            throw err;
          }
          error = typeSpecs[typeSpecName](values, typeSpecName, componentName, location, null, ReactPropTypesSecret$1);
        } catch (ex) {
          error = ex;
        }
        if (error && !(error instanceof Error)) {
          printWarning(
            (componentName || 'React class') + ': type specification of ' +
            location + ' `' + typeSpecName + '` is invalid; the type checker ' +
            'function must return `null` or an `Error` but returned a ' + typeof error + '. ' +
            'You may have forgotten to pass an argument to the type checker ' +
            'creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and ' +
            'shape all require an argument).'
          );
        }
        if (error instanceof Error && !(error.message in loggedTypeFailures)) {
          // Only monitor this failure once because there tends to be a lot of the
          // same error.
          loggedTypeFailures[error.message] = true;

          var stack = getStack ? getStack() : '';

          printWarning(
            'Failed ' + location + ' type: ' + error.message + (stack != null ? stack : '')
          );
        }
      }
    }
  }
}

/**
 * Resets warning cache when testing.
 *
 * @private
 */
checkPropTypes.resetWarningCache = function() {
  if (process.env.NODE_ENV !== 'production') {
    loggedTypeFailures = {};
  }
};

var checkPropTypes_1 = checkPropTypes;

var printWarning$1 = function() {};

if (process.env.NODE_ENV !== 'production') {
  printWarning$1 = function(text) {
    var message = 'Warning: ' + text;
    if (typeof console !== 'undefined') {
      console.error(message);
    }
    try {
      // --- Welcome to debugging React ---
      // This error was thrown as a convenience so that you can use this stack
      // to find the callsite that caused this warning to fire.
      throw new Error(message);
    } catch (x) {}
  };
}

function emptyFunctionThatReturnsNull() {
  return null;
}

var factoryWithTypeCheckers = function(isValidElement, throwOnDirectAccess) {
  /* global Symbol */
  var ITERATOR_SYMBOL = typeof Symbol === 'function' && Symbol.iterator;
  var FAUX_ITERATOR_SYMBOL = '@@iterator'; // Before Symbol spec.

  /**
   * Returns the iterator method function contained on the iterable object.
   *
   * Be sure to invoke the function with the iterable as context:
   *
   *     var iteratorFn = getIteratorFn(myIterable);
   *     if (iteratorFn) {
   *       var iterator = iteratorFn.call(myIterable);
   *       ...
   *     }
   *
   * @param {?object} maybeIterable
   * @return {?function}
   */
  function getIteratorFn(maybeIterable) {
    var iteratorFn = maybeIterable && (ITERATOR_SYMBOL && maybeIterable[ITERATOR_SYMBOL] || maybeIterable[FAUX_ITERATOR_SYMBOL]);
    if (typeof iteratorFn === 'function') {
      return iteratorFn;
    }
  }

  /**
   * Collection of methods that allow declaration and validation of props that are
   * supplied to React components. Example usage:
   *
   *   var Props = require('ReactPropTypes');
   *   var MyArticle = React.createClass({
   *     propTypes: {
   *       // An optional string prop named "description".
   *       description: Props.string,
   *
   *       // A required enum prop named "category".
   *       category: Props.oneOf(['News','Photos']).isRequired,
   *
   *       // A prop named "dialog" that requires an instance of Dialog.
   *       dialog: Props.instanceOf(Dialog).isRequired
   *     },
   *     render: function() { ... }
   *   });
   *
   * A more formal specification of how these methods are used:
   *
   *   type := array|bool|func|object|number|string|oneOf([...])|instanceOf(...)
   *   decl := ReactPropTypes.{type}(.isRequired)?
   *
   * Each and every declaration produces a function with the same signature. This
   * allows the creation of custom validation functions. For example:
   *
   *  var MyLink = React.createClass({
   *    propTypes: {
   *      // An optional string or URI prop named "href".
   *      href: function(props, propName, componentName) {
   *        var propValue = props[propName];
   *        if (propValue != null && typeof propValue !== 'string' &&
   *            !(propValue instanceof URI)) {
   *          return new Error(
   *            'Expected a string or an URI for ' + propName + ' in ' +
   *            componentName
   *          );
   *        }
   *      }
   *    },
   *    render: function() {...}
   *  });
   *
   * @internal
   */

  var ANONYMOUS = '<<anonymous>>';

  // Important!
  // Keep this list in sync with production version in `./factoryWithThrowingShims.js`.
  var ReactPropTypes = {
    array: createPrimitiveTypeChecker('array'),
    bigint: createPrimitiveTypeChecker('bigint'),
    bool: createPrimitiveTypeChecker('boolean'),
    func: createPrimitiveTypeChecker('function'),
    number: createPrimitiveTypeChecker('number'),
    object: createPrimitiveTypeChecker('object'),
    string: createPrimitiveTypeChecker('string'),
    symbol: createPrimitiveTypeChecker('symbol'),

    any: createAnyTypeChecker(),
    arrayOf: createArrayOfTypeChecker,
    element: createElementTypeChecker(),
    elementType: createElementTypeTypeChecker(),
    instanceOf: createInstanceTypeChecker,
    node: createNodeChecker(),
    objectOf: createObjectOfTypeChecker,
    oneOf: createEnumTypeChecker,
    oneOfType: createUnionTypeChecker,
    shape: createShapeTypeChecker,
    exact: createStrictShapeTypeChecker,
  };

  /**
   * inlined Object.is polyfill to avoid requiring consumers ship their own
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
   */
  /*eslint-disable no-self-compare*/
  function is(x, y) {
    // SameValue algorithm
    if (x === y) {
      // Steps 1-5, 7-10
      // Steps 6.b-6.e: +0 != -0
      return x !== 0 || 1 / x === 1 / y;
    } else {
      // Step 6.a: NaN == NaN
      return x !== x && y !== y;
    }
  }
  /*eslint-enable no-self-compare*/

  /**
   * We use an Error-like object for backward compatibility as people may call
   * PropTypes directly and inspect their output. However, we don't use real
   * Errors anymore. We don't inspect their stack anyway, and creating them
   * is prohibitively expensive if they are created too often, such as what
   * happens in oneOfType() for any type before the one that matched.
   */
  function PropTypeError(message, data) {
    this.message = message;
    this.data = data && typeof data === 'object' ? data: {};
    this.stack = '';
  }
  // Make `instanceof Error` still work for returned errors.
  PropTypeError.prototype = Error.prototype;

  function createChainableTypeChecker(validate) {
    if (process.env.NODE_ENV !== 'production') {
      var manualPropTypeCallCache = {};
      var manualPropTypeWarningCount = 0;
    }
    function checkType(isRequired, props, propName, componentName, location, propFullName, secret) {
      componentName = componentName || ANONYMOUS;
      propFullName = propFullName || propName;

      if (secret !== ReactPropTypesSecret_1) {
        if (throwOnDirectAccess) {
          // New behavior only for users of `prop-types` package
          var err = new Error(
            'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
            'Use `PropTypes.checkPropTypes()` to call them. ' +
            'Read more at http://fb.me/use-check-prop-types'
          );
          err.name = 'Invariant Violation';
          throw err;
        } else if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
          // Old behavior for people using React.PropTypes
          var cacheKey = componentName + ':' + propName;
          if (
            !manualPropTypeCallCache[cacheKey] &&
            // Avoid spamming the console because they are often not actionable except for lib authors
            manualPropTypeWarningCount < 3
          ) {
            printWarning$1(
              'You are manually calling a React.PropTypes validation ' +
              'function for the `' + propFullName + '` prop on `' + componentName + '`. This is deprecated ' +
              'and will throw in the standalone `prop-types` package. ' +
              'You may be seeing this warning due to a third-party PropTypes ' +
              'library. See https://fb.me/react-warning-dont-call-proptypes ' + 'for details.'
            );
            manualPropTypeCallCache[cacheKey] = true;
            manualPropTypeWarningCount++;
          }
        }
      }
      if (props[propName] == null) {
        if (isRequired) {
          if (props[propName] === null) {
            return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required ' + ('in `' + componentName + '`, but its value is `null`.'));
          }
          return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required in ' + ('`' + componentName + '`, but its value is `undefined`.'));
        }
        return null;
      } else {
        return validate(props, propName, componentName, location, propFullName);
      }
    }

    var chainedCheckType = checkType.bind(null, false);
    chainedCheckType.isRequired = checkType.bind(null, true);

    return chainedCheckType;
  }

  function createPrimitiveTypeChecker(expectedType) {
    function validate(props, propName, componentName, location, propFullName, secret) {
      var propValue = props[propName];
      var propType = getPropType(propValue);
      if (propType !== expectedType) {
        // `propValue` being instance of, say, date/regexp, pass the 'object'
        // check, but we can offer a more precise error message here rather than
        // 'of type `object`'.
        var preciseType = getPreciseType(propValue);

        return new PropTypeError(
          'Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + preciseType + '` supplied to `' + componentName + '`, expected ') + ('`' + expectedType + '`.'),
          {expectedType: expectedType}
        );
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createAnyTypeChecker() {
    return createChainableTypeChecker(emptyFunctionThatReturnsNull);
  }

  function createArrayOfTypeChecker(typeChecker) {
    function validate(props, propName, componentName, location, propFullName) {
      if (typeof typeChecker !== 'function') {
        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside arrayOf.');
      }
      var propValue = props[propName];
      if (!Array.isArray(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an array.'));
      }
      for (var i = 0; i < propValue.length; i++) {
        var error = typeChecker(propValue, i, componentName, location, propFullName + '[' + i + ']', ReactPropTypesSecret_1);
        if (error instanceof Error) {
          return error;
        }
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createElementTypeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      if (!isValidElement(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement.'));
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createElementTypeTypeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      if (!reactIs.isValidElementType(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement type.'));
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createInstanceTypeChecker(expectedClass) {
    function validate(props, propName, componentName, location, propFullName) {
      if (!(props[propName] instanceof expectedClass)) {
        var expectedClassName = expectedClass.name || ANONYMOUS;
        var actualClassName = getClassName(props[propName]);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + actualClassName + '` supplied to `' + componentName + '`, expected ') + ('instance of `' + expectedClassName + '`.'));
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createEnumTypeChecker(expectedValues) {
    if (!Array.isArray(expectedValues)) {
      if (process.env.NODE_ENV !== 'production') {
        if (arguments.length > 1) {
          printWarning$1(
            'Invalid arguments supplied to oneOf, expected an array, got ' + arguments.length + ' arguments. ' +
            'A common mistake is to write oneOf(x, y, z) instead of oneOf([x, y, z]).'
          );
        } else {
          printWarning$1('Invalid argument supplied to oneOf, expected an array.');
        }
      }
      return emptyFunctionThatReturnsNull;
    }

    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      for (var i = 0; i < expectedValues.length; i++) {
        if (is(propValue, expectedValues[i])) {
          return null;
        }
      }

      var valuesString = JSON.stringify(expectedValues, function replacer(key, value) {
        var type = getPreciseType(value);
        if (type === 'symbol') {
          return String(value);
        }
        return value;
      });
      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of value `' + String(propValue) + '` ' + ('supplied to `' + componentName + '`, expected one of ' + valuesString + '.'));
    }
    return createChainableTypeChecker(validate);
  }

  function createObjectOfTypeChecker(typeChecker) {
    function validate(props, propName, componentName, location, propFullName) {
      if (typeof typeChecker !== 'function') {
        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside objectOf.');
      }
      var propValue = props[propName];
      var propType = getPropType(propValue);
      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an object.'));
      }
      for (var key in propValue) {
        if (has(propValue, key)) {
          var error = typeChecker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret_1);
          if (error instanceof Error) {
            return error;
          }
        }
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createUnionTypeChecker(arrayOfTypeCheckers) {
    if (!Array.isArray(arrayOfTypeCheckers)) {
      process.env.NODE_ENV !== 'production' ? printWarning$1('Invalid argument supplied to oneOfType, expected an instance of array.') : void 0;
      return emptyFunctionThatReturnsNull;
    }

    for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
      var checker = arrayOfTypeCheckers[i];
      if (typeof checker !== 'function') {
        printWarning$1(
          'Invalid argument supplied to oneOfType. Expected an array of check functions, but ' +
          'received ' + getPostfixForTypeWarning(checker) + ' at index ' + i + '.'
        );
        return emptyFunctionThatReturnsNull;
      }
    }

    function validate(props, propName, componentName, location, propFullName) {
      var expectedTypes = [];
      for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
        var checker = arrayOfTypeCheckers[i];
        var checkerResult = checker(props, propName, componentName, location, propFullName, ReactPropTypesSecret_1);
        if (checkerResult == null) {
          return null;
        }
        if (checkerResult.data && has(checkerResult.data, 'expectedType')) {
          expectedTypes.push(checkerResult.data.expectedType);
        }
      }
      var expectedTypesMessage = (expectedTypes.length > 0) ? ', expected one of type [' + expectedTypes.join(', ') + ']': '';
      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`' + expectedTypesMessage + '.'));
    }
    return createChainableTypeChecker(validate);
  }

  function createNodeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      if (!isNode(props[propName])) {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`, expected a ReactNode.'));
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function invalidValidatorError(componentName, location, propFullName, key, type) {
    return new PropTypeError(
      (componentName || 'React class') + ': ' + location + ' type `' + propFullName + '.' + key + '` is invalid; ' +
      'it must be a function, usually from the `prop-types` package, but received `' + type + '`.'
    );
  }

  function createShapeTypeChecker(shapeTypes) {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      var propType = getPropType(propValue);
      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
      }
      for (var key in shapeTypes) {
        var checker = shapeTypes[key];
        if (typeof checker !== 'function') {
          return invalidValidatorError(componentName, location, propFullName, key, getPreciseType(checker));
        }
        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret_1);
        if (error) {
          return error;
        }
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createStrictShapeTypeChecker(shapeTypes) {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      var propType = getPropType(propValue);
      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
      }
      // We need to check all keys in case some are required but missing from props.
      var allKeys = objectAssign({}, props[propName], shapeTypes);
      for (var key in allKeys) {
        var checker = shapeTypes[key];
        if (has(shapeTypes, key) && typeof checker !== 'function') {
          return invalidValidatorError(componentName, location, propFullName, key, getPreciseType(checker));
        }
        if (!checker) {
          return new PropTypeError(
            'Invalid ' + location + ' `' + propFullName + '` key `' + key + '` supplied to `' + componentName + '`.' +
            '\nBad object: ' + JSON.stringify(props[propName], null, '  ') +
            '\nValid keys: ' + JSON.stringify(Object.keys(shapeTypes), null, '  ')
          );
        }
        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret_1);
        if (error) {
          return error;
        }
      }
      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function isNode(propValue) {
    switch (typeof propValue) {
      case 'number':
      case 'string':
      case 'undefined':
        return true;
      case 'boolean':
        return !propValue;
      case 'object':
        if (Array.isArray(propValue)) {
          return propValue.every(isNode);
        }
        if (propValue === null || isValidElement(propValue)) {
          return true;
        }

        var iteratorFn = getIteratorFn(propValue);
        if (iteratorFn) {
          var iterator = iteratorFn.call(propValue);
          var step;
          if (iteratorFn !== propValue.entries) {
            while (!(step = iterator.next()).done) {
              if (!isNode(step.value)) {
                return false;
              }
            }
          } else {
            // Iterator will provide entry [k,v] tuples rather than values.
            while (!(step = iterator.next()).done) {
              var entry = step.value;
              if (entry) {
                if (!isNode(entry[1])) {
                  return false;
                }
              }
            }
          }
        } else {
          return false;
        }

        return true;
      default:
        return false;
    }
  }

  function isSymbol(propType, propValue) {
    // Native Symbol.
    if (propType === 'symbol') {
      return true;
    }

    // falsy value can't be a Symbol
    if (!propValue) {
      return false;
    }

    // 19.4.3.5 Symbol.prototype[@@toStringTag] === 'Symbol'
    if (propValue['@@toStringTag'] === 'Symbol') {
      return true;
    }

    // Fallback for non-spec compliant Symbols which are polyfilled.
    if (typeof Symbol === 'function' && propValue instanceof Symbol) {
      return true;
    }

    return false;
  }

  // Equivalent of `typeof` but with special handling for array and regexp.
  function getPropType(propValue) {
    var propType = typeof propValue;
    if (Array.isArray(propValue)) {
      return 'array';
    }
    if (propValue instanceof RegExp) {
      // Old webkits (at least until Android 4.0) return 'function' rather than
      // 'object' for typeof a RegExp. We'll normalize this here so that /bla/
      // passes PropTypes.object.
      return 'object';
    }
    if (isSymbol(propType, propValue)) {
      return 'symbol';
    }
    return propType;
  }

  // This handles more types than `getPropType`. Only used for error messages.
  // See `createPrimitiveTypeChecker`.
  function getPreciseType(propValue) {
    if (typeof propValue === 'undefined' || propValue === null) {
      return '' + propValue;
    }
    var propType = getPropType(propValue);
    if (propType === 'object') {
      if (propValue instanceof Date) {
        return 'date';
      } else if (propValue instanceof RegExp) {
        return 'regexp';
      }
    }
    return propType;
  }

  // Returns a string that is postfixed to a warning about an invalid type.
  // For example, "undefined" or "of type array"
  function getPostfixForTypeWarning(value) {
    var type = getPreciseType(value);
    switch (type) {
      case 'array':
      case 'object':
        return 'an ' + type;
      case 'boolean':
      case 'date':
      case 'regexp':
        return 'a ' + type;
      default:
        return type;
    }
  }

  // Returns class name of the object, if any.
  function getClassName(propValue) {
    if (!propValue.constructor || !propValue.constructor.name) {
      return ANONYMOUS;
    }
    return propValue.constructor.name;
  }

  ReactPropTypes.checkPropTypes = checkPropTypes_1;
  ReactPropTypes.resetWarningCache = checkPropTypes_1.resetWarningCache;
  ReactPropTypes.PropTypes = ReactPropTypes;

  return ReactPropTypes;
};

function emptyFunction() {}
function emptyFunctionWithReset() {}
emptyFunctionWithReset.resetWarningCache = emptyFunction;

var factoryWithThrowingShims = function() {
  function shim(props, propName, componentName, location, propFullName, secret) {
    if (secret === ReactPropTypesSecret_1) {
      // It is still safe when called from React.
      return;
    }
    var err = new Error(
      'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
      'Use PropTypes.checkPropTypes() to call them. ' +
      'Read more at http://fb.me/use-check-prop-types'
    );
    err.name = 'Invariant Violation';
    throw err;
  }  shim.isRequired = shim;
  function getShim() {
    return shim;
  }  // Important!
  // Keep this list in sync with production version in `./factoryWithTypeCheckers.js`.
  var ReactPropTypes = {
    array: shim,
    bigint: shim,
    bool: shim,
    func: shim,
    number: shim,
    object: shim,
    string: shim,
    symbol: shim,

    any: shim,
    arrayOf: getShim,
    element: shim,
    elementType: shim,
    instanceOf: getShim,
    node: shim,
    objectOf: getShim,
    oneOf: getShim,
    oneOfType: getShim,
    shape: getShim,
    exact: getShim,

    checkPropTypes: emptyFunctionWithReset,
    resetWarningCache: emptyFunction
  };

  ReactPropTypes.PropTypes = ReactPropTypes;

  return ReactPropTypes;
};

var propTypes = createCommonjsModule(function (module) {
/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

if (process.env.NODE_ENV !== 'production') {
  var ReactIs = reactIs;

  // By explicitly using `prop-types` you are opting into new development behavior.
  // http://fb.me/prop-types-in-prod
  var throwOnDirectAccess = true;
  module.exports = factoryWithTypeCheckers(ReactIs.isElement, throwOnDirectAccess);
} else {
  // By explicitly using `prop-types` you are opting into new production behavior.
  // http://fb.me/prop-types-in-prod
  module.exports = factoryWithThrowingShims();
}
});

const stringOrNumber = propTypes.oneOfType([propTypes.string, propTypes.number]);
// media types
const types$1 = {
    all: propTypes.bool,
    grid: propTypes.bool,
    aural: propTypes.bool,
    braille: propTypes.bool,
    handheld: propTypes.bool,
    print: propTypes.bool,
    projection: propTypes.bool,
    screen: propTypes.bool,
    tty: propTypes.bool,
    tv: propTypes.bool,
    embossed: propTypes.bool
};
// properties that match media queries
const matchers = {
    orientation: propTypes.oneOf(['portrait', 'landscape']),
    scan: propTypes.oneOf(['progressive', 'interlace']),
    aspectRatio: propTypes.string,
    deviceAspectRatio: propTypes.string,
    height: stringOrNumber,
    deviceHeight: stringOrNumber,
    width: stringOrNumber,
    deviceWidth: stringOrNumber,
    color: propTypes.bool,
    colorIndex: propTypes.bool,
    monochrome: propTypes.bool,
    resolution: stringOrNumber,
    type: Object.keys(types$1)
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { type, ...featureMatchers } = matchers;
// media features
const features = {
    minAspectRatio: propTypes.string,
    maxAspectRatio: propTypes.string,
    minDeviceAspectRatio: propTypes.string,
    maxDeviceAspectRatio: propTypes.string,
    minHeight: stringOrNumber,
    maxHeight: stringOrNumber,
    minDeviceHeight: stringOrNumber,
    maxDeviceHeight: stringOrNumber,
    minWidth: stringOrNumber,
    maxWidth: stringOrNumber,
    minDeviceWidth: stringOrNumber,
    maxDeviceWidth: stringOrNumber,
    minColor: propTypes.number,
    maxColor: propTypes.number,
    minColorIndex: propTypes.number,
    maxColorIndex: propTypes.number,
    minMonochrome: propTypes.number,
    maxMonochrome: propTypes.number,
    minResolution: stringOrNumber,
    maxResolution: stringOrNumber,
    ...featureMatchers
};
const all = { ...types$1, ...features };
var mq = {
    all: all,
    types: types$1,
    matchers: matchers,
    features: features
};

const negate = (cond) => `not ${cond}`;
const keyVal = (k, v) => {
    const realKey = hyphenateStyleName(k);
    // px shorthand
    if (typeof v === 'number') {
        v = `${v}px`;
    }
    if (v === true) {
        return realKey;
    }
    if (v === false) {
        return negate(realKey);
    }
    return `(${realKey}: ${v})`;
};
const join = (conds) => conds.join(' and ');
const toQuery = (obj) => {
    const rules = [];
    Object.keys(mq.all).forEach((k) => {
        const v = obj[k];
        if (v != null) {
            rules.push(keyVal(k, v));
        }
    });
    return join(rules);
};

const Context = createContext(undefined);

const makeQuery = (settings) => settings.query || toQuery(settings);
const hyphenateKeys = (obj) => {
    if (!obj)
        return undefined;
    const keys = Object.keys(obj);
    return keys.reduce((result, key) => {
        result[hyphenateStyleName(key)] = obj[key];
        return result;
    }, {});
};
const useIsUpdate = () => {
    const ref = useRef(false);
    useEffect(() => {
        ref.current = true;
    }, []);
    return ref.current;
};
const useDevice = (deviceFromProps) => {
    const deviceFromContext = useContext(Context);
    const getDevice = () => hyphenateKeys(deviceFromProps) || hyphenateKeys(deviceFromContext);
    const [device, setDevice] = useState(getDevice);
    useEffect(() => {
        const newDevice = getDevice();
        if (!shallowEqualObjects(device, newDevice)) {
            setDevice(newDevice);
        }
    }, [deviceFromProps, deviceFromContext]);
    return device;
};
const useQuery = (settings) => {
    const getQuery = () => makeQuery(settings);
    const [query, setQuery] = useState(getQuery);
    useEffect(() => {
        const newQuery = getQuery();
        if (query !== newQuery) {
            setQuery(newQuery);
        }
    }, [settings]);
    return query;
};
const useMatchMedia = (query, device) => {
    const getMatchMedia = () => matchmediaquery(query, device || {}, !!device);
    const [mq, setMq] = useState(getMatchMedia);
    const isUpdate = useIsUpdate();
    useEffect(() => {
        if (isUpdate) {
            // skip on mounting, it has already been set
            const newMq = getMatchMedia();
            setMq(newMq);
            return () => {
                if (newMq) {
                    newMq.dispose();
                }
            };
        }
    }, [query, device]);
    return mq;
};
const useMatches = (mediaQuery) => {
    const [matches, setMatches] = useState(mediaQuery.matches);
    useEffect(() => {
        const updateMatches = (ev) => {
            setMatches(ev.matches);
        };
        mediaQuery.addListener(updateMatches);
        setMatches(mediaQuery.matches);
        return () => {
            mediaQuery.removeListener(updateMatches);
        };
    }, [mediaQuery]);
    return matches;
};
const useMediaQuery = (settings, device, onChange) => {
    const deviceSettings = useDevice(device);
    const query = useQuery(settings);
    if (!query)
        throw new Error('Invalid or missing MediaQuery!');
    const mq = useMatchMedia(query, deviceSettings);
    const matches = useMatches(mq);
    const isUpdate = useIsUpdate();
    useEffect(() => {
        if (isUpdate && onChange) {
            onChange(matches);
        }
    }, [matches]);
    useEffect(() => () => {
        if (mq) {
            mq.dispose();
        }
    }, []);
    return matches;
};
//# sourceMappingURL=index.js.map

var _g, _defs;
function _extends$1() {
  _extends$1 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends$1.apply(this, arguments);
}
function SvgRedirection(props) {
  return /*#__PURE__*/createElement("svg", _extends$1({
    width: 144,
    height: 207,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g || (_g = /*#__PURE__*/createElement("g", {
    clipPath: "url(#redirection_svg__clip0_1470_24465)"
  }, /*#__PURE__*/createElement("path", {
    d: "M103.749 194.637c0 1.204-.805 2.546-2.73 3.931-1.902 1.368-4.718 2.642-8.287 3.728-7.127 2.169-17.033 3.526-28.019 3.526-10.985 0-20.891-1.357-28.018-3.526-3.57-1.086-6.386-2.36-8.287-3.728-1.925-1.385-2.73-2.727-2.73-3.931 0-1.205.805-2.546 2.73-3.932 1.901-1.368 4.717-2.641 8.287-3.728 7.127-2.169 17.033-3.525 28.018-3.525 10.986 0 20.892 1.356 28.019 3.525 3.57 1.087 6.385 2.36 8.287 3.728 1.925 1.386 2.73 2.727 2.73 3.932z",
    stroke: "#2F6DE0",
    strokeWidth: 2
  }), /*#__PURE__*/createElement("path", {
    d: "M48.608 2.884h32.213l-5.92 192.539H55.31L48.608 2.883z",
    fill: "#fff",
    stroke: "#2F6DE0",
    strokeWidth: 2
  }), /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M3.612 29.788a2 2 0 01.516-3.313l28.708-12.901v-.003h.005l.05-.022-.004.03 76.746 11.958a2 2 0 011.669 2.284l-5.187 33.291a2 2 0 01-2.285 1.669L27.084 50.823l-.004.029-.04-.036h-.007l.001-.005L3.612 29.788z",
    fill: "#2F6DE0"
  }), /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M8.633 30.605c-1.148-.967-.862-2.808.527-3.38L35.37 16.434v-.003h.005l.047-.019-.004.026 71.585 11.154a2 2 0 011.668 2.284l-4.437 28.482a2 2 0 01-2.284 1.668L30.366 48.872l-.004.025-.038-.032h-.006v-.004L8.634 30.605z",
    fill: "#fff"
  }), /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M9.427 31.386c-1.18-.966-.888-2.846.531-3.408l25.79-10.202.001-.002h.005l.046-.018-.004.025 71.013 11.064a2 2 0 011.668 2.284l-4.242 27.229a2 2 0 01-2.284 1.668L30.938 48.96l-.004.025-.038-.031-.005-.001v-.003L9.427 31.386z",
    fill: "#9CBEFF"
  }), /*#__PURE__*/createElement("path", {
    d: "M62.797 34.981c-1.8 1.407-2.487 4.334-1.409 6.569 1.078 2.235 3.44 2.78 5.24 1.372 1.8-1.406 2.487-4.334 1.409-6.568-1.078-2.235-3.44-2.78-5.24-1.373z",
    fill: "#fff",
    stroke: "#2F6DE0",
    strokeWidth: 3
  }), /*#__PURE__*/createElement("path", {
    d: "M62.896 37.117c-.653.511-.879 2.468-.32 3.625.557 1.157 1.992 1.707 2.646 1.197.653-.51 1.43-2.79.871-3.946-.558-1.157-2.544-1.386-3.197-.876z",
    fill: "#9CBEFF"
  }), /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M141.918 78.154c1.385-.83 1.268-2.873-.203-3.539l-33.548-15.173v-.003h-.006l-.06-.027.002.03-96.923 5.564a2 2 0 00-1.882 2.111l1.931 33.638a2 2 0 002.112 1.882l96.922-5.563.002.03.054-.034h.008v-.004l31.591-18.912z",
    fill: "#2F6DE0"
  }), /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M135.607 78.54c1.453-.814 1.331-2.945-.206-3.589l-30.751-12.87v-.004h-.006l-.056-.022.002.026-90.438 5.191a2 2 0 00-1.882 2.111l1.652 28.779a2 2 0 002.111 1.882l90.438-5.191.002.026.051-.03h.007v-.003l29.076-16.305z",
    fill: "#fff"
  }), /*#__PURE__*/createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M134.524 79.25c1.483-.808 1.359-2.977-.208-3.61l-30.312-12.25v-.003h-.006l-.055-.022.002.026-89.72 5.15a2 2 0 00-1.882 2.11l1.58 27.512a2 2 0 002.11 1.882l89.72-5.15.002.025.051-.028h.007l-.001-.003 28.712-15.638z",
    fill: "#9CBEFF"
  }), /*#__PURE__*/createElement("path", {
    d: "M69.267 85.078a4.908 4.908 0 10-7.803-5.956 4.908 4.908 0 007.803 5.956z",
    fill: "#fff",
    stroke: "#2F6DE0",
    strokeWidth: 3
  }), /*#__PURE__*/createElement("path", {
    d: "M68.301 80.397c.739.563.693 2.615-.207 3.794-.9 1.18-2.805 1.684-3.543 1.12-.739-.564-1.334-2.976-.434-4.155.9-1.18 3.446-1.323 4.184-.76z",
    fill: "#9CBEFF"
  }))), _defs || (_defs = /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
    id: "redirection_svg__clip0_1470_24465"
  }, /*#__PURE__*/createElement("path", {
    fill: "#fff",
    transform: "translate(0 .89)",
    d: "M0 0h144v206H0z"
  })))));
}

var MenuProps = {
  disableScrollLock: true,
  PaperProps: {
    sx: {
      bgcolor: 'white',
      '& .MuiMenuItem-root': {
        paddingTop: 1
      }
    },
    style: {
      width: 328,
      maxHeight: 400,
      marginTop: '7px',
      borderRadius: '8px'
    }
  }
};
var TooltipStyles = {
  bgcolor: '#101828',
  borderRadius: '8px',
  fontFamily: 'Inter, sans-serif',
  textAlign: 'center',
  fontSize: '12px',
  lineHeight: '14.4px',
  fontWeight: '400',
  maxWidth: '190px',
  fontStyle: 'italic',
  padding: '8px 12px',
  marginBottom: 0,
  marginRight: '16px',
  '& .MuiTooltip-arrow': {
    color: '#101828'
  }
};
var SelectStyles = {
  height: '66px',
  fontSize: '16px',
  fontFamily: 'Inter,sans-serif',
  fontWeight: '600',
  backgroundColor: 'white',
  color: '#526166'
};

var quoter_destinations = {
  br: [{
    label: 'África',
    id: 6,
    code: 'afr'
  }, {
    label: 'América Central',
    id: 5,
    code: 'amc'
  }, {
    label: 'América do Norte',
    id: 16,
    code: 'amn'
  }, {
    label: 'América do Sul',
    id: 5,
    code: 'ams'
  }, {
    label: 'Asia',
    id: 9,
    code: 'asi'
  }, {
    label: 'Europa',
    id: 10,
    code: 'eur'
  }, {
    label: 'Marítimo Internacional',
    id: 17,
    code: 'mri'
  }, {
    label: 'Oceanía',
    id: 2,
    code: 'oce'
  }, {
    label: 'Oriente Medio',
    id: 17,
    code: 'orm'
  }, {
    label: 'Vários Destinos',
    id: 17,
    code: 'vds'
  }],
  global: [{
    label: 'Europa',
    id: 10,
    code: 'eur'
  }, {
    label: 'Sudamérica y Caribe',
    id: 5,
    code: 'syc'
  }, {
    label: 'Norteamérica',
    id: 16,
    code: 'amn'
  }, {
    label: 'Asia',
    id: 9,
    code: 'asi'
  }, {
    label: 'Oceanía',
    id: 2,
    code: 'oce'
  }, {
    label: 'África',
    id: 6,
    code: 'afr'
  }, {
    label: 'Varios Destinos',
    id: 17,
    code: 'vds'
  }]
};
var quoter_destinations_blocked = {
  br: new Map([]),
  global: new Map([])
};

var IconLocation = function IconLocation() {
  return /*#__PURE__*/React__default.createElement(SvgIcon, null, /*#__PURE__*/React__default.createElement("svg", {
    style: {
      marginTop: '0px'
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: "21",
    height: "21",
    viewBox: "0 0 21 21",
    fill: "none"
  }, /*#__PURE__*/React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M5.29391 4.55758C8.02757 1.82391 12.4597 1.82391 15.1934 4.55758C17.9271 7.29125 17.9271 11.7234 15.1934 14.4571L10.2437 19.4068L5.29391 14.4571C2.56023 11.7234 2.56023 7.29125 5.29391 4.55758ZM10.2437 11.5073C11.3482 11.5073 12.2437 10.6119 12.2437 9.50732C12.2437 8.40275 11.3482 7.50732 10.2437 7.50732C9.13908 7.50732 8.24365 8.40275 8.24365 9.50732C8.24365 10.6119 9.13908 11.5073 10.2437 11.5073Z",
    fill: "black",
    fillOpacity: "0.6"
  })));
};

var getLocalStorageData = function getLocalStorageData(market) {
  if (market === void 0) {
    market = 'global';
  }
  var destinations = quoter_destinations[market];
  var urlProductQuery = window.localStorage.getItem('pQuery');
  var key, value;
  if (urlProductQuery) {
    var ls_data = {};
    urlProductQuery.split('&').forEach(function (elem) {
      key = elem.split('=')[0].charAt(0) === '?' ? elem.split('=')[0].slice(1) : elem.split('=')[0];
      value = elem.split('=')[1];
      ls_data[key] = value;
    });
    var region_id = ls_data.region_id;
    var destination = destinations.find(function (d) {
      return d.id === parseInt(region_id);
    });
    ls_data['dates'] = {
      date_from: ls_data.date_from,
      date_to: ls_data.date_to
    };
    ls_data['destination'] = destination;
    return ls_data;
  } else {
    return false;
  }
};

var Destiny = function Destiny() {
  var _useContext = useContext(StoreContext),
    store = _useContext[0],
    dispatch = _useContext[1];
  var config = store.config,
    quotation = store.quotation;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(true);
  var _useState2 = useState(true),
    tooltipVisibility = _useState2[0];
  var destinations = quoter_destinations[config.market];
  var destinations_blocked = quoter_destinations_blocked[config.market];
  var _React$useState = React__default.useState(''),
    destination = _React$useState[0],
    setDestination = _React$useState[1];
  var landscapeSelectStyles = _extends({}, SelectStyles, {
    fontSize: '14px',
    lineHeight: '1.6'
  });
  useEffect(function () {
    if (config.local_storage_data) {
      var lsd = getLocalStorageData(config.market);
      if (lsd) {
        var _lsd$destination = lsd.destination,
          label = _lsd$destination.label,
          code = _lsd$destination.code;
        var _destination = label !== destination && destination ? destination : code;
        handleDestination(_destination);
      }
    }
  }, [config]);
  var handleDestination = function handleDestination(_destino) {
    setDestination(_destino);
  };
  var _useState3 = useState(false),
    openDestiny = _useState3[0],
    setOpenDestiny = _useState3[1];
  useEffect(function () {
    if (destination) {
      var dest_selected = destinations.filter(function (d) {
        return d.code === destination;
      });
      var _dest_selected$ = dest_selected[0],
        label = _dest_selected$.label,
        id = _dest_selected$.id,
        code = _dest_selected$.code;
      var destino = {
        value: id,
        label: label,
        code: code
      };
      var _visibility, _title;
      if (destinations_blocked.get(code) && tooltipVisibility) {
        _visibility = true;
        _title = t('quoter.Destiny.noCoverage', {
          destination: t("quoter.Destiny.countriesBlocked." + code)
        });
      } else {
        _visibility = false;
        _title = '';
      }
      dispatch({
        type: types.setQuotation,
        payload: _extends({}, quotation, {
          destination: destino,
          tooltip: _extends({}, quotation.tooltip, {
            visibility: _visibility,
            title: _title
          })
        })
      });
    }
  }, [destination]);
  useEffect(function () {
    if (openDestiny && config.view_mode !== 'landscape') {
      document.body.style.cssText = "overflow: hidden";
      document.getElementsByTagName('html')[0].style.overflow = 'hidden';
    } else {
      document.body.removeAttribute('style');
      document.getElementsByTagName('html')[0].removeAttribute('style');
    }
    if (openDestiny) {
      dispatch({
        type: types.setQuotation,
        payload: _extends({}, quotation, {
          tooltip: _extends({}, quotation.tooltip, {
            visibility: false,
            title: ''
          })
        })
      });
    }
  }, [openDestiny]);
  var handleDestinyOpen = function handleDestinyOpen() {
    setOpenDestiny(function (openDestiny) {
      return !openDestiny;
    });
  };
  return /*#__PURE__*/React__default.createElement("div", {
    id: "quoter-destination"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "tooltip_container"
  }, /*#__PURE__*/React__default.createElement(Tooltip, {
    title: quotation === null || quotation === void 0 ? void 0 : quotation.tooltip.title,
    TransitionProps: {
      timeout: 0
    },
    disableHoverListener: true,
    disableFocusListener: true,
    open: quotation === null || quotation === void 0 ? void 0 : quotation.tooltip.visibility,
    arrow: true,
    placement: "top-end",
    componentsProps: {
      tooltip: {
        sx: TooltipStyles
      }
    }
  }, /*#__PURE__*/React__default.createElement("div", null, /*#__PURE__*/React__default.createElement("div", {
    className: 'destination-container' + (quotation.destination.value === 0 && quotation.submitted ? ' border-error-field' : '') + ((config === null || config === void 0 ? void 0 : config.view_mode) === 'landscape' ? ' quoter-w-[258px]' : ' quote-w-[328px]')
  }, /*#__PURE__*/React__default.createElement(FormControl, {
    variant: "outlined",
    sx: {
      width: '100%'
    }
  }, /*#__PURE__*/React__default.createElement("label", {
    className: 'dest_label font-inter font-bold text-xs' + (config.viewmode === 'landscape' ? ' quoter-left-[46px]' : ' quoter-left-[16px] quoter-mt-[-3px]')
  }, t('quoter.Destiny.inputLabel')), /*#__PURE__*/React__default.createElement(Select, {
    onClose: handleDestinyOpen,
    onOpen: handleDestinyOpen,
    className: "select_destiny",
    style: (config === null || config === void 0 ? void 0 : config.view_mode) === 'landscape' ? landscapeSelectStyles : SelectStyles,
    sx: {
      width: '100%'
    },
    displayEmpty: true,
    value: destination,
    onChange: function onChange(_ref) {
      var value = _ref.target.value;
      return handleDestination(value);
    },
    input: /*#__PURE__*/React__default.createElement(OutlinedInput, null),
    renderValue: function renderValue(value) {
      return /*#__PURE__*/React__default.createElement(Box, {
        sx: {
          display: 'flex',
          gap: 1
        }
      }, /*#__PURE__*/React__default.createElement(IconLocation, null), /*#__PURE__*/React__default.createElement("span", {
        className: "quoter-font-sans" + destination ? 'quoter-font-bold quoter-normal' : 'quoter-font-normal quoter-italic'
      }, value.length === 0 ? t('quoter.Destiny.inputPlaceHolder') : t("quoter.Destiny.destinations." + value)));
    },
    MenuProps: MenuProps,
    inputProps: {
      'aria-label': 'Without label'
    }
  }, destinations && destinations.map(function (d, idx) {
    return /*#__PURE__*/React__default.createElement(MenuItem, {
      className: "quoter-text-grey-primary",
      key: "dest-id-" + idx,
      value: d.code
    }, t('quoter.Destiny.destinations.' + d.code));
  })))), quotation.destination.value === 0 && quotation.submitted && /*#__PURE__*/React__default.createElement("div", {
    className: "quoter-text-left quoter-font-bold quoter-text-xs quoter-text-error quoter-font-sans quoter-pt-1"
  }, t('quoter.Destiny.errorMessage'))))));
};

var DialogTitleStyles = {
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  height: '100px',
  backgroundColor: '#fff',
  fontSize: '20px',
  fontWeight: 600,
  position: 'sticky',
  zIndex: '9999',
  textAlign: 'center',
  top: 0,
  left: 0,
  right: 0,
  color: '#081316'
};

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeCeil = Math.ceil,
    nativeMax = Math.max;

/**
 * The base implementation of `_.range` and `_.rangeRight` which doesn't
 * coerce arguments.
 *
 * @private
 * @param {number} start The start of the range.
 * @param {number} end The end of the range.
 * @param {number} step The value to increment or decrement by.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Array} Returns the range of numbers.
 */
function baseRange(start, end, step, fromRight) {
  var index = -1,
      length = nativeMax(nativeCeil((end - start) / (step || 1)), 0),
      result = Array(length);

  while (length--) {
    result[fromRight ? length : ++index] = start;
    start += step;
  }
  return result;
}

var _baseRange = baseRange;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || (value !== value && other !== other);
}

var eq_1 = eq;

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = _freeGlobal || freeSelf || Function('return this')();

var _root = root;

/** Built-in value references. */
var Symbol$1 = _root.Symbol;

var _Symbol = Symbol$1;

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/** Built-in value references. */
var symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty$1.call(value, symToStringTag),
      tag = value[symToStringTag];

  try {
    value[symToStringTag] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag] = tag;
    } else {
      delete value[symToStringTag];
    }
  }
  return result;
}

var _getRawTag = getRawTag;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString$1.call(value);
}

var _objectToString = objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag$1 = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag$1 && symToStringTag$1 in Object(value))
    ? _getRawTag(value)
    : _objectToString(value);
}

var _baseGetTag = baseGetTag;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject;

/** `Object#toString` result references. */
var asyncTag = '[object AsyncFunction]',
    funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  if (!isObject_1(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = _baseGetTag(value);
  return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
}

var isFunction_1 = isFunction;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength(value) {
  return typeof value == 'number' &&
    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
}

var isLength_1 = isLength;

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike(value) {
  return value != null && isLength_1(value.length) && !isFunction_1(value);
}

var isArrayLike_1 = isArrayLike;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER$1 = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER$1 : length;

  return !!length &&
    (type == 'number' ||
      (type != 'symbol' && reIsUint.test(value))) &&
        (value > -1 && value % 1 == 0 && value < length);
}

var _isIndex = isIndex;

/**
 * Checks if the given arguments are from an iteratee call.
 *
 * @private
 * @param {*} value The potential iteratee value argument.
 * @param {*} index The potential iteratee index or key argument.
 * @param {*} object The potential iteratee object argument.
 * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
 *  else `false`.
 */
function isIterateeCall(value, index, object) {
  if (!isObject_1(object)) {
    return false;
  }
  var type = typeof index;
  if (type == 'number'
        ? (isArrayLike_1(object) && _isIndex(index, object.length))
        : (type == 'string' && index in object)
      ) {
    return eq_1(object[index], value);
  }
  return false;
}

var _isIterateeCall = isIterateeCall;

/** Used to match a single whitespace character. */
var reWhitespace = /\s/;

/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
 * character of `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the index of the last non-whitespace character.
 */
function trimmedEndIndex(string) {
  var index = string.length;

  while (index-- && reWhitespace.test(string.charAt(index))) {}
  return index;
}

var _trimmedEndIndex = trimmedEndIndex;

/** Used to match leading whitespace. */
var reTrimStart = /^\s+/;

/**
 * The base implementation of `_.trim`.
 *
 * @private
 * @param {string} string The string to trim.
 * @returns {string} Returns the trimmed string.
 */
function baseTrim(string) {
  return string
    ? string.slice(0, _trimmedEndIndex(string) + 1).replace(reTrimStart, '')
    : string;
}

var _baseTrim = baseTrim;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike;

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike_1(value) && _baseGetTag(value) == symbolTag);
}

var isSymbol_1 = isSymbol;

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol_1(value)) {
    return NAN;
  }
  if (isObject_1(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject_1(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = _baseTrim(value);
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

var toNumber_1 = toNumber;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0,
    MAX_INTEGER = 1.7976931348623157e+308;

/**
 * Converts `value` to a finite number.
 *
 * @static
 * @memberOf _
 * @since 4.12.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted number.
 * @example
 *
 * _.toFinite(3.2);
 * // => 3.2
 *
 * _.toFinite(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toFinite(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toFinite('3.2');
 * // => 3.2
 */
function toFinite(value) {
  if (!value) {
    return value === 0 ? value : 0;
  }
  value = toNumber_1(value);
  if (value === INFINITY || value === -INFINITY) {
    var sign = (value < 0 ? -1 : 1);
    return sign * MAX_INTEGER;
  }
  return value === value ? value : 0;
}

var toFinite_1 = toFinite;

/**
 * Creates a `_.range` or `_.rangeRight` function.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new range function.
 */
function createRange(fromRight) {
  return function(start, end, step) {
    if (step && typeof step != 'number' && _isIterateeCall(start, end, step)) {
      end = step = undefined;
    }
    // Ensure the sign of `-0` is preserved.
    start = toFinite_1(start);
    if (end === undefined) {
      end = start;
      start = 0;
    } else {
      end = toFinite_1(end);
    }
    step = step === undefined ? (start < end ? 1 : -1) : toFinite_1(step);
    return _baseRange(start, end, step, fromRight);
  };
}

var _createRange = createRange;

/**
 * Creates an array of numbers (positive and/or negative) progressing from
 * `start` up to, but not including, `end`. A step of `-1` is used if a negative
 * `start` is specified without an `end` or `step`. If `end` is not specified,
 * it's set to `start` with `start` then set to `0`.
 *
 * **Note:** JavaScript follows the IEEE-754 standard for resolving
 * floating-point values which can produce unexpected results.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {number} [start=0] The start of the range.
 * @param {number} end The end of the range.
 * @param {number} [step=1] The value to increment or decrement by.
 * @returns {Array} Returns the range of numbers.
 * @see _.inRange, _.rangeRight
 * @example
 *
 * _.range(4);
 * // => [0, 1, 2, 3]
 *
 * _.range(-4);
 * // => [0, -1, -2, -3]
 *
 * _.range(1, 5);
 * // => [1, 2, 3, 4]
 *
 * _.range(0, 20, 5);
 * // => [0, 5, 10, 15]
 *
 * _.range(0, -4, -1);
 * // => [0, -1, -2, -3]
 *
 * _.range(1, 4, 0);
 * // => [1, 1, 1]
 *
 * _.range(0);
 * // => []
 */
var range = _createRange();

var range_1 = range;

var localeData = require('dayjs/plugin/localeData');
dayjs.extend(localeData);
var setMinDaysToQuote = function setMinDaysToQuote(date_from, min_days) {
  if (min_days === void 0) {
    min_days = 3;
  }
  var min_date_collection = [];
  var data_dates = [];
  for (var i = 1; i <= min_days; i++) {
    var data_date = dayjs(date_from).add(i, 'day').format('YYYY-MM-DD');
    min_date_collection.push("[data-date=\"" + data_date + "\"]:not(.day-cell--faded)");
    data_dates.push(data_date);
  }
  document.querySelectorAll(min_date_collection.join(',')).forEach(function (elem) {
    elem.classList.add('day-cell--disabled');
  });
  return data_dates;
};
var pad = function pad(value) {
  var val = parseInt(value);
  return val <= 9 ? '0' + val : value;
};
var jsonToQueryString = function jsonToQueryString(json) {
  return '?' + Object.keys(json).map(function (key) {
    return encodeURIComponent(key) + '=' + encodeURIComponent(json[key]);
  }).join('&');
};
function formatDate(date, language) {
  if (language === void 0) {
    language = 'es';
  }
  switch (language) {
    case 'es':
      dayjs.locale(_extends({}, es, {
        weekStart: 1
      }));
      break;
    case 'pt':
      dayjs.locale(_extends({}, pt, {
        weekStart: 1
      }));
      break;
    default:
      dayjs.locale(_extends({}, es, {
        weekStart: 1
      }));
      break;
  }
  var _dayjs_date = dayjs(date);
  var weekdays = dayjs.weekdays();
  var f_date = capitalizeFirstLetter(weekdays[_dayjs_date.day()].substring(0, 3)) + ", " + pad(_dayjs_date.date()) + " de " + capitalizeFirstLetter(_dayjs_date.format('MMMM')).substring(0, 3) + " " + _dayjs_date.year();
  return f_date;
}
function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

var CalendarWeeks = function CalendarWeeks(_ref) {
  var date = _ref.date,
    weekDays = _ref.weekDays,
    displayWeekdays = _ref.displayWeekdays;
  var month = date.month,
    year = date.year;
  return /*#__PURE__*/React__default.createElement(Fragment$1, null, /*#__PURE__*/React__default.createElement("div", {
    className: "month quoter-capitalize quoter-mb-5 md:quoter-text-center"
  }, month, " ", year), displayWeekdays && /*#__PURE__*/React__default.createElement("div", {
    className: "week-container"
  }, weekDays.map(function (d) {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "week-cell",
      key: d
    }, d.slice(0, 3));
  })));
};

var CalendarMonth = function CalendarMonth(_ref) {
  var _month$data, _month$data2;
  var data_month = _ref.data_month,
    dates = _ref.dates,
    selectDate = _ref.selectDate,
    limit = _ref.limit;
  var _useContext = useContext(StoreContext),
    store = _useContext[0];
  var config = store.config,
    breakpoint = store.breakpoint;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState({
      data: null
    }),
    month = _useState[0],
    setMonth = _useState[1];
  useEffect(function () {
    var data = {
      dayObj: data_month,
      todayObj: dayjs(),
      weekDays: dayjs.weekdays()
    };
    data.thisYear = data.dayObj.year();
    data.thisMonth = data.dayObj.month();
    data.daysInMonth = data.dayObj.daysInMonth();
    data.dayObjOf1 = dayjs(data.thisYear + "-" + (data.thisMonth + 1) + "-1");
    data.weekDayOf1 = data.dayObjOf1.day();
    data.dayObjOfLast = dayjs(data.thisYear + "-" + (data.thisMonth + 1) + "-" + data.daysInMonth);
    data.weekDayOfLast = data.dayObjOfLast.day();
    setMonth(_extends({}, month, {
      data: data
    }));
    if (breakpoint === 'xs' || breakpoint === 'sm' || breakpoint === 'md') {
      if (dayjs(dates.date_from).format('YYYY-MM') === data_month.format('YYYY-MM')) {
        setTimeout(function () {
          var id = "date-" + dayjs(dates.date_from).format('YY') + "-" + pad(dayjs(dates.date_from).format('MM'));
          var active_month = document.getElementById(id);
          active_month.scrollIntoView();
        }, 500);
      }
    }
  }, [data_month]);
  var getID = function getID() {
    return "date-" + data_month.format('YY') + "-" + data_month.format('MM');
  };
  return /*#__PURE__*/React__default.createElement(Fragment$1, null, month.data && /*#__PURE__*/React__default.createElement("div", {
    className: "calendar md:quoter-relative md:quoter-w-[344px] md:quoter-max-w-[344px]",
    id: getID()
  }, (month === null || month === void 0 ? void 0 : month.data) && /*#__PURE__*/React__default.createElement(CalendarWeeks, {
    className: "quoter-mb-7",
    date: {
      month: t("quoter.Dates.months." + (data_month === null || data_month === void 0 ? void 0 : data_month.format('M'))),
      year: data_month === null || data_month === void 0 ? void 0 : data_month.format('YYYY')
    },
    displayWeekdays: config.dates.weekdays,
    weekDays: month === null || month === void 0 ? void 0 : month.data.weekDays
  }), /*#__PURE__*/React__default.createElement("div", {
    className: "day-container"
  }, range_1(month === null || month === void 0 ? void 0 : (_month$data = month.data) === null || _month$data === void 0 ? void 0 : _month$data.weekDayOf1).map(function (i) {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "day-cell day-cell--faded",
      key: i
    }, month === null || month === void 0 ? void 0 : month.data.dayObjOf1.subtract((month === null || month === void 0 ? void 0 : month.data.weekDayOf1) - i, 'day').date());
  }), range_1(month === null || month === void 0 ? void 0 : (_month$data2 = month.data) === null || _month$data2 === void 0 ? void 0 : _month$data2.daysInMonth).map(function (i) {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "day-cell day-cell--in-month " + (i + 1 === month.data.todayObj.date() && month.data.thisMonth === month.data.todayObj.month() && month.data.thisYear === month.data.todayObj.year() ? ' day-cell--today' : '') + (month.data.thisMonth === month.data.todayObj.month() && month.data.thisYear === month.data.todayObj.year() && i + 1 < month.data.todayObj.date() || dayjs(data_month.format('YYYY-MM') + '-' + pad(i + 1)).isAfter(limit === null || limit === void 0 ? void 0 : limit.first_date) ? 'day-cell--faded' : '') + (data_month.format('YYYY-MM') + '-' + pad(parseInt(i + 1)) === dates.date_from ? ' day-cell--selected day-cell-range-start' : '') + (data_month.format('YYYY-MM') + '-' + pad(parseInt(i + 1)) === dates.date_to ? ' day-cell--selected day-cell-range-end' : '') + (dates.excluded.includes(data_month.format('YYYY-MM') + '-' + pad(i + 1)) ? ' day-cell--disabled' : '') + (dates.date_from && dates.date_to && dayjs(data_month.format('YYYY-MM') + '-' + parseInt(i + 1)).isBefore(dates.date_to) && dayjs(data_month.format('YYYY-MM') + '-' + parseInt(i + 1)).isAfter(dates.date_from) ? ' day-cell--range' : ''),
      key: i,
      "data-date": data_month.format('YYYY-MM') + "-" + pad(i + 1),
      onClick: function onClick(event) {
        return selectDate(event);
      }
    }, ' ', i + 1);
  }), range_1(6 - month.data.weekDayOfLast).map(function (i) {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "day-cell day-cell--faded",
      key: i
    }, month.data.dayObjOfLast.add(i + 1, 'day').date());
  }))));
};

var CalendarMobile = function CalendarMobile(_ref) {
  var open = _ref.open,
    onClose = _ref.onClose,
    handleDates = _ref.handleDates;
  var _useContext = useContext(StoreContext),
    store = _useContext[0];
  var config = store.config,
    quotation = store.quotation;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t,
    i18n = _useTranslation.i18n;
  var _useState = useState({
      first_date: null,
      last_date: null
    }),
    limitDate = _useState[0],
    setLimitDate = _useState[1];
  var _setDateLimit = function _setDateLimit(newDates) {
    var date_from = newDates.date_from;
    var today = dayjs();
    var max_days_quotation = config.max_days_quotation;
    var first_day_limit = dayjs(date_from).add(max_days_quotation, "day").format("YYYY-MM-DD");
    var last_day_limit = today.add(3, "year").endOf('month').format("YYYY-MM-DD");
    setLimitDate({
      first_date: dayjs(first_day_limit),
      last_date: dayjs(last_day_limit)
    });
  };
  var _useState2 = useState({
      first: dayjs().set('date', 1),
      sec: dayjs().set('date', 1).add(1, 'month'),
      month_sec_visibility: true
    }),
    months = _useState2[0];
  var _useState3 = useState([]),
    new_months = _useState3[0],
    setNewDate = _useState3[1];
  var _useState4 = useState({
      date_from: '',
      date_to: '',
      excluded: []
    }),
    _dates = _useState4[0],
    _setDates = _useState4[1];
  useState(function () {
    var available_months = config.max_years_quotation * 12;
    var _months = [];
    for (var index = 0; index <= available_months; index++) {
      _months.push(months.first.set('date', 1).add(new_months.length + index, 'month'));
    }
    setNewDate(_months);
    if (quotation.dates.date_from && quotation.dates.date_to) {
      var selected_date = dayjs(quotation.dates.date_from).format('YYYY-MM-DD');
      var _newDates = _extends({}, _dates, {
        date_from: quotation.dates.date_from,
        date_to: quotation.dates.date_to,
        excluded: setMinDaysToQuote(selected_date, config.min_days_quotation)
      });
      _setDates(_newDates);
    }
  }, []);
  var selectDate = function selectDate(data) {
    var selected_date = data.target.getAttribute('data-date');
    if (!_dates.date_from) {
      var _newDates = _extends({}, _dates, {
        date_from: selected_date,
        excluded: setMinDaysToQuote(selected_date, config.min_days_quotation)
      });
      _setDateLimit(_newDates);
      _setDates(_newDates);
    }
    if (_dates.date_from && !_dates.date_to) {
      var _newDates2 = {
        date_from: '',
        date_to: '',
        excluded: []
      };
      if (_dates.date_from !== selected_date && dayjs(selected_date).isAfter(_dates.date_from)) {
        _newDates2 = _extends({}, _dates, {
          date_to: selected_date
        });
      } else {
        setLimitDate({
          first_date: null,
          last_date: null
        });
      }
      _setDates(_newDates2);
    }
    if (_dates.date_from && _dates.date_to) {
      var _newDates3 = {
        date_from: '',
        date_to: '',
        excluded: []
      };
      setLimitDate({
        first_date: null,
        last_date: null
      });
      _setDates(_newDates3);
    }
  };
  var applyDates = function applyDates() {
    var date_from = _dates.date_from,
      date_to = _dates.date_to;
    var _f_date_from = config.market === 'br' || i18n.language === 'pt-BR' ? formatDate(date_from, 'pt') : formatDate(date_from);
    var _f_date_to = config.market === 'br' || i18n.language === 'pt-BR' ? formatDate(date_to, 'pt') : formatDate(date_to);
    if (date_from && date_to) {
      var dates = _extends({}, _dates, {
        f_date_from: _f_date_from,
        f_date_to: _f_date_to
      });
      onClose(dates);
    }
    var body = document.querySelector('body');
    body.removeAttribute('style');
  };
  return /*#__PURE__*/React__default.createElement(Dialog, {
    open: open,
    onClose: onClose,
    sx: {
      zIndex: '9999999999999999'
    }
  }, /*#__PURE__*/React__default.createElement(DialogTitle, {
    style: DialogTitleStyles
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "dialog-close-btn",
    onClick: onClose
  }, /*#__PURE__*/React__default.createElement("svg", {
    width: "25",
    height: "25",
    viewBox: "0 0 25 25",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M5.66202 5.30856C6.13065 4.83993 6.89045 4.83993 7.35908 5.30856L12.5105 10.46L17.662 5.30856C18.1306 4.83993 18.8904 4.83993 19.3591 5.30856C19.8277 5.77719 19.8277 6.53699 19.3591 7.00562L14.2076 12.1571L19.3591 17.3086C19.8277 17.7772 19.8277 18.537 19.3591 19.0056C18.8904 19.4743 18.1306 19.4743 17.662 19.0056L12.5105 13.8541L7.35908 19.0056C6.89045 19.4743 6.13065 19.4743 5.66202 19.0056C5.19339 18.537 5.19339 17.7772 5.66202 17.3086L10.8135 12.1571L5.66202 7.00562C5.19339 6.53699 5.19339 5.77719 5.66202 5.30856Z",
    fill: "#1B2225"
  }))), /*#__PURE__*/React__default.createElement("div", null, t('quoter.Dates.DialogSelectTitle'))), /*#__PURE__*/React__default.createElement("div", {
    className: "dialog-body quoter-mb-64"
  }, /*#__PURE__*/React__default.createElement("div", null, new_months.map(function (month, i) {
    return /*#__PURE__*/React__default.createElement(CalendarMonth, {
      disabled: month.isAfter(dayjs(limitDate.first_date)) ? true : false,
      limit: limitDate,
      data_month: month,
      dates: _dates,
      key: i,
      selectDate: selectDate,
      handleDates: handleDates
    });
  }))), _dates.date_from && _dates.date_to && /*#__PURE__*/React__default.createElement(DialogActions, {
    style: {
      position: 'sticky',
      zIndex: 999,
      bottom: 0,
      background: '#fff',
      padding: '0 27px',
      height: '66px'
    }
  }, /*#__PURE__*/React__default.createElement("button", {
    onClick: applyDates,
    type: "button",
    className: "btn-submit-dates quoter-cursor-pointer"
  }, t('quoter.Dates.confirmDates'))));
};

var CalendarDesktop = function CalendarDesktop(_ref) {
  var onClose = _ref.onClose,
    handleDates = _ref.handleDates;
  var _useContext = useContext(StoreContext),
    store = _useContext[0];
  var config = store.config,
    quotation = store.quotation;
  var view_mode = config.view_mode;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t,
    i18n = _useTranslation.i18n;
  var _useState = useState([]),
    monthsMap = _useState[0],
    setMonthsMap = _useState[1];
  var _useState2 = useState({
      first_date: null,
      last_date: null
    }),
    limitDate = _useState2[0],
    setLimitDate = _useState2[1];
  var _useState3 = useState(0),
    monthPointer = _useState3[0],
    setMonthPointer = _useState3[1];
  var _useState4 = useState({
      date_from: '',
      date_to: '',
      excluded: []
    }),
    _dates = _useState4[0],
    _setDates = _useState4[1];
  var _setDateLimit = function _setDateLimit(newDates) {
    var date_from = newDates.date_from;
    var today = dayjs();
    var max_days_quotation = config.max_days_quotation;
    var first_day_limit = dayjs(date_from).add(max_days_quotation, "day").format("YYYY-MM-DD");
    var last_day_limit = today.add(3, "year").endOf('month').format("YYYY-MM-DD");
    setLimitDate({
      first_date: dayjs(first_day_limit),
      last_date: dayjs(last_day_limit)
    });
  };
  useEffect(function () {
    if (quotation.dates.date_from && quotation.dates.date_to) {
      var selected_date = dayjs(quotation.dates.date_from).format('YYYY-MM-DD');
      var _newDates = _extends({}, _dates, {
        date_from: quotation.dates.date_from,
        date_to: quotation.dates.date_to,
        excluded: setMinDaysToQuote(selected_date, config.min_days_quotation)
      });
      _setDates(_newDates);
    }
  }, []);
  var handleMonth = function handleMonth(mode) {
    var active_months = [];
    if (mode === 'next') {
      if (monthsMap.get(monthPointer + 1) && monthsMap.get(monthPointer + 2)) {
        var pre_date_1 = monthsMap.get(monthPointer + 1).isBefore(dayjs().set('date', 1).add(config.max_years_quotation, 'years'));
        var pre_date_2 = monthsMap.get(monthPointer + 2).isBefore(dayjs().set('date', 1).add(config.max_years_quotation, 'years'));
        if (pre_date_1 && pre_date_2) {
          active_months.push(monthsMap.get(monthPointer + 1));
          if (pre_date_2) {
            active_months.push(monthsMap.get(monthPointer + 2));
          }
          setMonthPointer(function (monthPointer) {
            return monthPointer + 1;
          });
          setActiveMonths(active_months);
        }
      }
    }
    if (mode === 'prev') {
      if (monthPointer !== 0 && monthPointer !== 1) {
        active_months.push(monthsMap.get(monthPointer - 2));
        active_months.push(monthsMap.get(monthPointer - 1));
        setMonthPointer(function (monthPointer) {
          return monthPointer - 1;
        });
      } else {
        active_months.push(monthsMap.get(0));
        active_months.push(monthsMap.get(1));
        setMonthPointer(0);
      }
      setActiveMonths(active_months);
    }
  };
  var selectDate = function selectDate(data) {
    console.log('selectDate', data.target.getAttribute('data-date'));
    var selected_date = data.target.getAttribute('data-date');
    if (!_dates.date_from) {
      var _newDates = _extends({}, _dates, {
        date_from: selected_date,
        excluded: setMinDaysToQuote(selected_date, config.min_days_quotation)
      });
      _setDates(_newDates);
      _setDateLimit(_newDates);
    }
    if (_dates.date_from && !_dates.date_to) {
      var _newDates2 = {
        date_from: '',
        date_to: '',
        excluded: []
      };
      if (_dates.date_from !== selected_date && dayjs(selected_date).isAfter(_dates.date_from)) {
        _newDates2 = _extends({}, _dates, {
          date_to: selected_date
        });
      } else {
        setLimitDate({
          first_date: null,
          last_date: null
        });
      }
      _setDates(_newDates2);
    }
    if (_dates.date_from && _dates.date_to) {
      var _newDates3 = {
        date_from: '',
        date_to: '',
        excluded: []
      };
      setLimitDate({
        first_date: null,
        last_date: null
      });
      _setDates(_newDates3);
    }
  };
  var initial_value = [dayjs(), dayjs().add(1, 'month')];
  var _useState5 = useState(initial_value),
    activeMonths = _useState5[0],
    setActiveMonths = _useState5[1];
  useEffect(function () {
    var available_months = config.max_years_quotation * 12;
    var _months = new Map();
    var today = dayjs();
    for (var index = 0; index <= available_months; index++) {
      _months.set(index, today.set('date', 1).add(index, 'month'));
    }
    setMonthsMap(_months);
    if (quotation.dates.date_from && quotation.dates.date_to) {
      var idx,
        idx_to,
        pointer = 0;
      for (var _iterator = _createForOfIteratorHelperLoose(_months), _step; !(_step = _iterator()).done;) {
        var _step$value = _step.value,
          key = _step$value[0],
          value = _step$value[1];
        if (dayjs(value).format('YYYY-MM') === dayjs(quotation.dates.date_from).format('YYYY-MM')) {
          idx = key;
          if (dayjs(quotation.dates.date_to).format('YYYY-MM') === dayjs(quotation.dates.date_from).format('YYYY-MM')) {
            if (key === 12) {
              idx = key - 1;
              idx_to = key;
              pointer = key - 1;
            } else {
              idx = key;
              idx_to = key + 1;
              pointer = key;
            }
          } else {
            idx_to = key + 1;
            pointer = key;
          }
          break;
        }
      }
      setActiveMonths([_months.get(idx), _months.get(idx_to)]);
      setMonthPointer(pointer);
    }
  }, []);
  var closeDesktopDialog = function closeDesktopDialog() {
    onClose(_dates);
  };
  var closeDesktop = function closeDesktop() {
    onClose();
  };
  var applyDates = function applyDates() {
    var date_from = _dates.date_from,
      date_to = _dates.date_to;
    var _f_date_from = config.market === 'br' || i18n.language === 'pt-BR' ? formatDate(date_from, 'pt') : formatDate(date_from);
    var _f_date_to = config.market === 'br' || i18n.language === 'pt-BR' ? formatDate(date_to, 'pt') : formatDate(date_to);
    if (date_from && date_to) {
      var dates = _extends({}, _dates, {
        f_date_from: _f_date_from,
        f_date_to: _f_date_to
      });
      onClose(dates);
    }
    var body = document.querySelector('body');
    body.removeAttribute('style');
  };
  return /*#__PURE__*/React__default.createElement(Fragment$1, null, /*#__PURE__*/React__default.createElement(OutsideClickHandler, {
    onOutsideClick: function onOutsideClick() {
      return closeDesktopDialog();
    }
  }, /*#__PURE__*/React__default.createElement("div", {
    className: 'calendar-desktop ' + (view_mode === 'landscape' ? 'quoter-left-[-26px] quoter-mt-[8px] quoter-absolute' : 'md:quoter-top-[90px] md:quoter-absolute ')
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "calendar-desktop-header"
  }, t('quoter.Dates.DialogSelectTitle'), /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    onClick: closeDesktop
  }, /*#__PURE__*/React__default.createElement("svg", {
    width: "25",
    height: "25",
    viewBox: "0 0 25 25",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M5.66202 5.30856C6.13065 4.83993 6.89045 4.83993 7.35908 5.30856L12.5105 10.46L17.662 5.30856C18.1306 4.83993 18.8904 4.83993 19.3591 5.30856C19.8277 5.77719 19.8277 6.53699 19.3591 7.00562L14.2076 12.1571L19.3591 17.3086C19.8277 17.7772 19.8277 18.537 19.3591 19.0056C18.8904 19.4743 18.1306 19.4743 17.662 19.0056L12.5105 13.8541L7.35908 19.0056C6.89045 19.4743 6.13065 19.4743 5.66202 19.0056C5.19339 18.537 5.19339 17.7772 5.66202 17.3086L10.8135 12.1571L5.66202 7.00562C5.19339 6.53699 5.19339 5.77719 5.66202 5.30856Z",
    fill: "#1B2225"
  }))), /*#__PURE__*/React__default.createElement("div", {
    className: "calendar-navigation"
  }, /*#__PURE__*/React__default.createElement("button", {
    onClick: function onClick() {
      return handleMonth('prev');
    },
    type: "button"
  }, /*#__PURE__*/React__default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "25",
    height: "24",
    viewBox: "0 0 25 24",
    fill: "none"
  }, /*#__PURE__*/React__default.createElement("path", {
    d: "M15.5195 6L9.51953 12L15.5195 18",
    stroke: "#2F6DE0",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }))), /*#__PURE__*/React__default.createElement("button", null, /*#__PURE__*/React__default.createElement("svg", {
    onClick: function onClick() {
      return handleMonth('next');
    },
    xmlns: "http://www.w3.org/2000/svg",
    width: "25",
    height: "24",
    viewBox: "0 0 25 24",
    fill: "none"
  }, /*#__PURE__*/React__default.createElement("path", {
    d: "M9.51953 18L15.5195 12L9.51953 6",
    stroke: "#2F6DE0",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })))), /*#__PURE__*/React__default.createElement("div", {
    className: "calendar-desktop-footer"
  }, /*#__PURE__*/React__default.createElement("button", {
    disabled: !_dates.date_from || !_dates.date_to,
    onClick: applyDates,
    type: "button",
    className: "btn-submit-dates quoter-cursor-pointer"
  }, t('quoter.Dates.confirmDates')))), /*#__PURE__*/React__default.createElement("div", {
    className: "calendar-desktop-body quoter-flex"
  }, activeMonths.length > 0 && activeMonths.map(function (month, i) {
    var disabled = month.isAfter(dayjs(limitDate.first_date)) ? true : false;
    return /*#__PURE__*/React__default.createElement(CalendarMonth, {
      disabled: month.isAfter(dayjs(limitDate.first_date)) ? true : false,
      limit: limitDate,
      data_month: month,
      dates: _dates,
      key: i,
      selectDate: selectDate,
      handleDates: handleDates
    });
  })))));
};

var Calendar = function Calendar(_ref) {
  var open = _ref.open,
    onClose = _ref.onClose;
  var _useContext = useContext(StoreContext),
    store = _useContext[0];
  var config = store.config,
    quotation = store.quotation,
    breakpoint = store.breakpoint;
  var dates = quotation.dates;
  var _useTranslation = useTranslation();
  var _useState = useState({
      date_from: '',
      date_to: '',
      excluded: []
    });
  var _useState2 = useState({
      date_from: '',
      date_to: '',
      excluded: []
    }),
    _dates = _useState2[0],
    _setDates = _useState2[1];
  var localeData = require('dayjs/plugin/localeData');
  dayjs.extend(localeData);
  switch (config.language) {
    case 'es':
      dayjs.locale(_extends({}, es, {
        weekStart: 1
      }));
      break;
    case 'pt':
      dayjs.locale(_extends({}, pt, {
        weekStart: 1
      }));
      break;
    default:
      dayjs.locale(_extends({}, es, {
        weekStart: 1
      }));
      break;
  }
  var handleDates = function handleDates(data) {
    var selected_date = data.target.getAttribute('data-date');
    if (!_dates.date_from) {
      var _newDates = _extends({}, dates, {
        date_from: selected_date,
        excluded: setMinDaysToQuote(selected_date, config.min_days_quotation)
      });
      _setDates(_newDates);
    }
    if (_dates.date_from && !_dates.date_to) {
      var _newDates2 = {
        date_from: '',
        date_to: '',
        excluded: []
      };
      if (_dates.date_from !== selected_date && dayjs(selected_date).isAfter(_dates.date_from)) {
        _newDates2 = _extends({}, _dates, {
          date_to: selected_date
        });
      }
      _setDates(_newDates2);
    }
    if (_dates.date_from && _dates.date_to) {
      var _newDates3 = {
        date_from: '',
        date_to: '',
        excluded: []
      };
      _setDates(_newDates3);
    }
  };
  return /*#__PURE__*/React__default.createElement(Fragment$1, null, breakpoint == 'xs' || breakpoint == 'sm' || breakpoint == 'md' || breakpoint == 'lg' ? /*#__PURE__*/React__default.createElement(CalendarMobile, {
    open: open,
    onClose: onClose,
    handleDates: handleDates,
    datesSelected: _dates
  }) : /*#__PURE__*/React__default.createElement(CalendarDesktop, {
    open: open,
    onClose: onClose,
    handleDates: handleDates,
    datesSelected: _dates
  }));
};

var Dates = function Dates() {
  var _useContext = useContext(StoreContext),
    store = _useContext[0],
    dispatch = _useContext[1];
  var config = store.config,
    quotation = store.quotation;
  var view_mode = config.view_mode;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t,
    i18n = _useTranslation.i18n;
  var _useState = useState(false),
    open = _useState[0],
    setOpen = _useState[1];
  var _useState2 = useState({
      f_date_from: '',
      f_date_to: ''
    }),
    f_dates = _useState2[0],
    setDates = _useState2[1];
  useEffect(function () {
    if (config.local_storage_data) {
      var lsd = getLocalStorageData();
      if (lsd && (quotation.dates.date_from === lsd.dates.date_from && quotation.dates.date_to === lsd.dates.date_to || quotation.dates.date_from === '' && quotation.dates.date_to === '')) {
        var _lsd$dates = lsd.dates,
          date_from = _lsd$dates.date_from,
          date_to = _lsd$dates.date_to;
        var _f_date_from = config.market === 'br' || i18n.language === 'pt-BR' ? formatDate(date_from, 'pt') : formatDate(date_from);
        var _f_date_to = config.market === 'br' || i18n.language === 'pt-BR' ? formatDate(date_to, 'pt') : formatDate(date_to);
        handleClose({
          date_from: lsd.dates.date_from,
          date_to: lsd.dates.date_to,
          f_date_from: _f_date_from,
          f_date_to: _f_date_to
        });
      }
    }
  }, [f_dates.f_date_from, f_dates.f_date_to, config]);
  var handleClickOpen = function handleClickOpen() {
    if (quotation.tooltip.visibility) {
      dispatch({
        type: types.setQuotation,
        payload: _extends({}, quotation, {
          tooltip: {
            visibility: false,
            title: ''
          }
        })
      });
    }
    setOpen(true);
  };
  var handleClose = function handleClose(value) {
    if (value && value.f_date_from && value.f_date_to) {
      var f_date_from = value.f_date_from,
        f_date_to = value.f_date_to;
      setDates({
        f_date_from: f_date_from,
        f_date_to: f_date_to
      });
      delete value.f_date_from;
      delete value.f_date_to;
      dispatch({
        type: types.setQuotation,
        payload: _extends({}, quotation, {
          dates: {
            date_from: value.date_from,
            date_to: value.date_to
          }
        })
      });
    }
    setOpen(false);
  };
  return /*#__PURE__*/React__default.createElement("div", {
    className: 'quoter-flex quoter-relative' + (view_mode === 'landscape' ? ' quoter-flex-col' : ' quoter-flex-col')
  }, /*#__PURE__*/React__default.createElement("div", {
    id: "quoter-dates-container",
    className: 'quoter-dates-container ' + (view_mode !== 'landscape' ? 'quoter-mt-[10px] quoter-max-w-[328px] quoter-w-full' : 'quoter-w-[380px] quoter-ml-[10px]') + ((!quotation.dates.date_from || !quotation.dates.date_to) && quotation.submitted ? ' border-error-field' : ' border-white')
  }, /*#__PURE__*/React__default.createElement("div", {
    onClick: handleClickOpen,
    className: "quoter-dates-block quoter-flex-col"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: 'dates-label quoter-md:ml-0'
  }, t('quoter.Dates.inputLabel')), /*#__PURE__*/React__default.createElement("div", {
    className: "quoter-flex quoter-items-center"
  }, view_mode !== 'landscape' && /*#__PURE__*/React__default.createElement(SvgIcon, null, /*#__PURE__*/React__default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "21",
    height: "21",
    viewBox: "0 0 21 21",
    fill: "none"
  }, /*#__PURE__*/React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M6.81055 2.10541C6.25826 2.10541 5.81055 2.55312 5.81055 3.10541V4.10541H4.81055C3.70598 4.10541 2.81055 5.00084 2.81055 6.10541V16.1054C2.81055 17.21 3.70598 18.1054 4.81055 18.1054H16.8105C17.9151 18.1054 18.8105 17.21 18.8105 16.1054V6.10541C18.8105 5.00084 17.9151 4.10541 16.8105 4.10541H15.8105V3.10541C15.8105 2.55312 15.3628 2.10541 14.8105 2.10541C14.2583 2.10541 13.8105 2.55312 13.8105 3.10541V4.10541H7.81055V3.10541C7.81055 2.55312 7.36283 2.10541 6.81055 2.10541ZM6.81055 7.10541C6.25826 7.10541 5.81055 7.55312 5.81055 8.10541C5.81055 8.65769 6.25826 9.10541 6.81055 9.10541H14.8105C15.3628 9.10541 15.8105 8.65769 15.8105 8.10541C15.8105 7.55312 15.3628 7.10541 14.8105 7.10541H6.81055Z",
    fill: "#414D58"
  }))), /*#__PURE__*/React__default.createElement("div", {
    className: 'dates-input ' + (view_mode !== 'landscape' ? 'quoter-ml-[10px]' : 'quoter-ml-0')
  }, /*#__PURE__*/React__default.createElement("div", {
    className: 'dates-input-value' + (f_dates.f_date_from === '' ? '' : ' filled') + (view_mode === 'landscape' ? ' landscape' : '')
  }, f_dates.f_date_from === '' ? t('quoter.Dates.placeholders.date_from') : f_dates.f_date_from)))), /*#__PURE__*/React__default.createElement("div", {
    onClick: handleClickOpen,
    className: "quoter-dates-block quoter-flex-col"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "dates-label"
  }, t('quoter.Dates.inputLabel')), /*#__PURE__*/React__default.createElement("div", {
    className: "quoter-flex quoter-items-center"
  }, view_mode !== 'landscape' && /*#__PURE__*/React__default.createElement(SvgIcon, null, /*#__PURE__*/React__default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "21",
    height: "21",
    viewBox: "0 0 21 21",
    fill: "none"
  }, /*#__PURE__*/React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M6.81055 2.10541C6.25826 2.10541 5.81055 2.55312 5.81055 3.10541V4.10541H4.81055C3.70598 4.10541 2.81055 5.00084 2.81055 6.10541V16.1054C2.81055 17.21 3.70598 18.1054 4.81055 18.1054H16.8105C17.9151 18.1054 18.8105 17.21 18.8105 16.1054V6.10541C18.8105 5.00084 17.9151 4.10541 16.8105 4.10541H15.8105V3.10541C15.8105 2.55312 15.3628 2.10541 14.8105 2.10541C14.2583 2.10541 13.8105 2.55312 13.8105 3.10541V4.10541H7.81055V3.10541C7.81055 2.55312 7.36283 2.10541 6.81055 2.10541ZM6.81055 7.10541C6.25826 7.10541 5.81055 7.55312 5.81055 8.10541C5.81055 8.65769 6.25826 9.10541 6.81055 9.10541H14.8105C15.3628 9.10541 15.8105 8.65769 15.8105 8.10541C15.8105 7.55312 15.3628 7.10541 14.8105 7.10541H6.81055Z",
    fill: "#414D58"
  }))), /*#__PURE__*/React__default.createElement("div", {
    className: 'dates-input ' + (view_mode !== 'landscape' ? 'quoter-ml-[10px]' : 'quoter-ml-0')
  }, /*#__PURE__*/React__default.createElement("div", {
    className: 'dates-input-value' + (f_dates.f_date_to === '' ? '' : ' filled') + (view_mode === 'landscape' ? ' landscape' : '')
  }, f_dates.f_date_to === '' ? t('quoter.Dates.placeholders.date_to') : f_dates.f_date_to))))), (quotation.dates.date_from.length === 0 || quotation.dates.date_to.length === 0) && quotation.submitted && /*#__PURE__*/React__default.createElement("div", {
    className: 'quoter-text-left quoter-font-bold quoter-text-xs quoter-text-error quoter-font-sans quoter-pt-1' + (view_mode === 'landscape' ? ' quoter-ml-4' : '')
  }, t('quoter.Dates.errorMessage')), open && /*#__PURE__*/React__default.createElement(Calendar, {
    open: open,
    onClose: handleClose
  }));
};

var PaxBlock = function PaxBlock(props) {
  var _useContext = useContext(StoreContext),
    store = _useContext[0];
  var config = store.config;
  var id = props.id,
    view_mode = props.view_mode,
    ages_1 = props.ages_1,
    ages_2 = props.ages_2,
    quotation_submitted = props.quotation_submitted,
    setPaxAge = props.setPaxAge;
  var ages = new Map([[1, ages_1], [2, ages_2]]);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  return /*#__PURE__*/React__default.createElement("div", {
    className: 'pax-block ' + (view_mode !== 'landscape' ? ' quoter-w-full sm:quoter-w-[228px]' : '') + (view_mode === 'landscape' ? ' quoter-mt-2' : '') + (view_mode !== 'landscape' && id === 1 ? ' quoter-mb-[10px]' : '') + (!ages_1 && !ages_2 && quotation_submitted ? ' border-error-field' : ' quoter-border-white')
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "label quoter-font-bold pax-label quoter-p-0"
  }, t('quoter.paxs.pax'), /*#__PURE__*/React__default.createElement("span", {
    className: "pax-limit-msg"
  }, t("quoter.paxs.paxAgeLimit_" + id, {
    age1: config.market === 'br' ? 64 : 74,
    age2: config.market === 'br' ? 65 : 75
  }))), /*#__PURE__*/React__default.createElement("div", {
    className: "quoter-flex quoter-items-center quoter-w-full quoter-mt-3"
  }, /*#__PURE__*/React__default.createElement(SvgIcon, null, /*#__PURE__*/React__default.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "21",
    height: "21",
    viewBox: "0 0 21 21",
    fill: "none"
  }, /*#__PURE__*/React__default.createElement("path", {
    d: "M10.8105 9.60541C12.4674 9.60541 13.8105 8.26226 13.8105 6.60541C13.8105 4.94855 12.4674 3.60541 10.8105 3.60541C9.15369 3.60541 7.81055 4.94855 7.81055 6.60541C7.81055 8.26226 9.15369 9.60541 10.8105 9.60541Z",
    fill: "#414D58"
  }), /*#__PURE__*/React__default.createElement("path", {
    d: "M3.81055 18.6054C3.81055 14.7394 6.94455 11.6054 10.8105 11.6054C14.6765 11.6054 17.8105 14.7394 17.8105 18.6054H3.81055Z",
    fill: "#414D58"
  }))), /*#__PURE__*/React__default.createElement("div", {
    className: "paxs-input-container"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "pax-input"
  }, /*#__PURE__*/React__default.createElement("button", {
    onClick: function onClick() {
      setPaxAge('minus', id);
    },
    className: "pax-btn"
  }, "-"), /*#__PURE__*/React__default.createElement("div", {
    className: 'pax-counter' + (!ages.get(id) ? ' quoter-font-normal' : ' quoter-font-semibold') + (!ages.get(id) ? ' quoter-italic' : ' quoter-normal')
  }, ages.get(id)), /*#__PURE__*/React__default.createElement("button", {
    onClick: function onClick() {
      setPaxAge('plus', id);
    },
    className: "pax-btn"
  }, "+")))));
};

var Paxs = function Paxs() {
  var _useContext = useContext(StoreContext),
    store = _useContext[0],
    dispatch = _useContext[1];
  var config = store.config,
    quotation = store.quotation;
  var view_mode = config.view_mode;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(0),
    ages_1 = _useState[0],
    setAges1 = _useState[1];
  var _useState2 = useState(0),
    ages_2 = _useState2[0],
    setAges2 = _useState2[1];
  var _useState3 = useState({
      destination: false,
      dates: false,
      ages: false
    });
  var _useState4 = useState(false);
  useEffect(function () {
    if (config.local_storage_data) {
      var lsd = getLocalStorageData();
      if (lsd) {
        var d_ages_1 = lsd.hasOwnProperty('ages_1') ? lsd.ages_1 : 0;
        var d_ages_2 = lsd.hasOwnProperty('ages_2') ? lsd.ages_2 : 0;
        dispatch({
          type: types.setQuotation,
          payload: _extends({}, quotation, {
            paxs: {
              ages_1: d_ages_1,
              ages_2: d_ages_2
            }
          })
        });
        setAges1(parseInt(d_ages_1));
        setAges2(parseInt(d_ages_2));
      }
    }
  }, [config]);
  useEffect(function () {
    dispatch({
      type: types.setQuotation,
      payload: _extends({}, quotation, {
        paxs: {
          ages_1: ages_1,
          ages_2: ages_2
        }
      })
    });
  }, [ages_1, ages_2]);
  function setPaxAge(mode, group_pax) {
    if (mode === void 0) {
      mode = 'plus';
    }
    if (quotation.tooltip.visibility) {
      dispatch({
        type: types.setQuotation,
        payload: _extends({}, quotation, {
          tooltip: {
            visibility: false,
            title: ''
          }
        })
      });
    }
    var _ages_1 = ages_1;
    var _ages_2 = ages_2;
    if (mode === 'plus') {
      var pax_limit = config.pax_limit;
      if (_ages_1 + _ages_2 < pax_limit) {
        if (group_pax === 1) {
          if (_ages_1 < pax_limit) {
            setAges1(_ages_1 + 1);
          }
        }
        if (group_pax === 2) {
          if (_ages_1 < pax_limit) {
            setAges2(_ages_2 + 1);
          }
        }
      }
    } else {
      if (_ages_1 > 0 && group_pax === 1 || _ages_2 > 0 && group_pax === 2) {
        if (group_pax === 1) {
          setAges1(_ages_1 - 1);
        } else {
          setAges2(_ages_2 - 1);
        }
      }
    }
  }
  var _useState5 = useState(false),
    PaxDialog = _useState5[0],
    setPaxDialog = _useState5[1];
  var togglePaxDialog = function togglePaxDialog() {
    var new_state = !PaxDialog;
    setPaxDialog(new_state);
  };
  return /*#__PURE__*/React__default.createElement(Fragment$1, null, /*#__PURE__*/React__default.createElement("div", {
    id: "paxs-container",
    className: 'paxs quoter-relative ' + (view_mode === 'landscape' ? 'quoter-flex-col  quoter-w-[136px] quoter-ml-[10px]' : 'quoter-flex-col quoter-mt-[10px] sm:quoter-mt-0 sm:quoter-w-[228px] sm:quoter-items-start quoter-w-[328px]')
  }, view_mode === 'landscape' && /*#__PURE__*/React__default.createElement("div", {
    onClick: togglePaxDialog,
    className: 'pax-block quoter-cursor-pointer quoter-w-full' + (!ages_1 && !ages_2 && quotation.submitted ? ' border-error-field' : ' quoter-border-white')
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "label quoter-font-bold pax-label quoter-p-0 quoter-mt-[1px]"
  }, t('quoter.paxs.pax')), /*#__PURE__*/React__default.createElement("div", {
    className: "quoter-flex quoter-items-center quoter-mt-[4px] quoter-w-[163px]"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "pax-input"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "quoter-font-bold quoter-text-grey-primary quoter-font-sans quoter-mt-2 quoter-text-sm"
  }, ages_1 + ages_2, " ", t('quoter.paxs.inputPlaceHolder'), ages_1 + ages_2 > 1 && /*#__PURE__*/React__default.createElement("span", null, "s"))))), (view_mode !== 'landscape' || PaxDialog) && /*#__PURE__*/React__default.createElement("div", {
    className: view_mode === 'landscape' ? 'quoter-flex-col quoter-p-[10px] quoter-w-[252px] quoter-absolute quoter-bg-blue-quoter quoter-left-[-40px] quoter-top-[75px] quoter-rounded-lg' : 'quoter-flex-row quoter-w-full'
  }, view_mode === 'landscape' ? /*#__PURE__*/React__default.createElement(OutsideClickHandler, {
    onOutsideClick: function onOutsideClick() {
      return togglePaxDialog();
    }
  }, [1, 2].map(function (group_pax) {
    return /*#__PURE__*/React__default.createElement(PaxBlock, {
      key: group_pax,
      view_mode: view_mode,
      id: group_pax,
      ages_1: ages_1,
      ages_2: ages_2,
      groupAgePax: config.age_groups,
      quotation_submitted: quotation.submitted,
      setPaxAge: setPaxAge
    });
  })) : [1, 2].map(function (group_pax) {
    return /*#__PURE__*/React__default.createElement(PaxBlock, {
      key: group_pax,
      view_mode: view_mode,
      id: group_pax,
      groupAgePax: config.age_groups,
      ages_1: ages_1,
      ages_2: ages_2,
      quotation_submitted: quotation.submitted,
      setPaxAge: setPaxAge
    });
  }), !ages_1 && !ages_2 && quotation.submitted && /*#__PURE__*/React__default.createElement("div", {
    className: "quoter-pt-1 quoter-text-left quoter-font-bold quoter-text-xs quoter-text-error quoter-font-sans quoter-w-full"
  }, t('quoter.paxs.errorMessage')), view_mode === 'landscape' && /*#__PURE__*/React__default.createElement("button", {
    onClick: togglePaxDialog,
    className: "quoter-h-[48px] quoter-w-full quoter-font-semibold quoter-font-sans quoter-text-md quoter-mt-2 quoter-rounded-md toggle-paxs",
    type: "button"
  }, t('quoter.paxs.accept')))));
};

var QuoterProcess = function QuoterProcess(quotation, config) {
  delete quotation.tooltip;
  delete quotation.submitted;
  var dates = {
    date_from: quotation.dates.date_from,
    date_to: quotation.dates.date_to,
    total_days: quotation.total_days,
    date_from_raw: quotation.dates.date_from_raw,
    date_to_raw: quotation.dates.date_to_raw
  };
  var dest = {
    label: quotation.destination.label,
    value: quotation.destination.value
  };
  var paxs = {
    ages_1: quotation.paxs.ages_1,
    ages_2: quotation.paxs.ages_2
  };
  var query = {
    region_id: dest.value,
    country_code: window.localStorage.getItem('country_code'),
    country_id: parseInt(window.localStorage.getItem('country_id')),
    days: parseInt(dates.total_days),
    entity_id: parseInt(window.localStorage.getItem('entity_id')),
    to_year: isNaN(parseInt(dates.date_to.split('-')[0])) ? 0 : parseInt(dates.date_to.split('-')[0]),
    insured: parseInt(paxs.ages_1 + paxs.ages_2),
    date_from: dates.date_from,
    date_to: dates.date_to
  };
  if (window.localStorage.getItem('voucher')) {
    query['voucher'] = window.localStorage.getItem('voucher');
  }
  if (window.localStorage.getItem('pQuery')) {
    window.localStorage.removeItem('pQuery');
  }
  var age_groups = config.age_groups;
  var query_age = '';
  var input_age = '';
  if (paxs.ages_1 > 0) {
    query['ages_1'] = parseInt(paxs.ages_1);
    for (var i = 1; i <= paxs.ages_1; i++) {
      query_age += "&age[]=" + age_groups.age_1;
      input_age += age_groups.age_1 + ",";
    }
  }
  if (paxs.ages_2 > 0) {
    query['ages_2'] = parseInt(paxs.ages_2);
    for (var _i = 1; _i <= paxs.ages_2; _i++) {
      query_age += "&age[]=" + age_groups.age_2;
      input_age += age_groups.age_2 + ",";
    }
  }
  var urlParameters = jsonToQueryString(query);
  input_age = input_age.slice(0, -1);
  query['input_age'] = input_age;
  window.localStorage.setItem('date_from', dates.date_from_raw);
  window.localStorage.setItem('date_to', dates.date_to_raw);
  window.localStorage.setItem('input_age', input_age);
  window.localStorage.setItem('pQuery', urlParameters + query_age);
  var redirect_global = quotation.redirect_global ? quotation.redirect_global : false;
  if (quotation.config.market === 'br' || redirect_global) {
    var to = query.region_id;
    var month = new Date(window.localStorage.getItem('month')).getMonth() + 1;
    var auxQuery = "to=" + to + "&month=" + month + "&date_from=" + query.date_from + "&date_to=" + query.date_to + "&quote_type=default&input_age=" + input_age;
    var pQuery = window.localStorage.getItem('pQuery');
    var urlQuery = pQuery + "&market=" + config.market + "&lang=" + config.language + "&" + auxQuery;
    if (redirect_global) {
      window.location.href = config.url_global + "/" + urlQuery;
    } else {
      window.location.href = "" + config.redirection_url;
    }
  } else {
    if (config.redirection_url && config.redirection) {
      var url_2_redirect = '';
      if (config.market === 'global') {
        var url = window.location.href;
        var geo = url.split('/')[3];
        if (geo) {
          geo = geo.slice(0, 2);
        }
        url_2_redirect += "/" + geo;
      }
      url_2_redirect += config.redirection_url;
      window.location.href = url_2_redirect;
    }
  }
};

var App = function App(_ref) {
  var _config$seo, _config$seo2;
  var quoterData = _ref.quoterData;
  var _useContext = useContext(StoreContext),
    store = _useContext[0],
    dispatch = _useContext[1];
  var _useState = useState(false),
    dialog_visibility = _useState[0],
    setDialogVisibility = _useState[1];
  var config = store.config,
    quotation = store.quotation;
  var view_mode = config.view_mode;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  useEffect(function () {
    console.log('quotation', quotation);
  }, [quotation]);
  useEffect(function () {
    if (config.geolocation) {
      if (!window.localStorage.getItem('country_id') && !window.localStorage.getItem('country_code') && !window.localStorage.getItem('country_name') || config.market === 'br') {
        var url = 'https://app.assist-365.com/api/geolocation';
        if (config.market === 'br') {
          url += '?flag=true&country_code=BR';
        }
        try {
          fetch(url).then(function (res) {
            return res.json();
          }).then(function (res) {
            window.localStorage.setItem('country_code', res.country);
            window.localStorage.setItem('country_name', res.country_name);
            window.localStorage.setItem('country_id', res.country_id);
            window.localStorage.setItem('entity_id', res.entity);
            window.localStorage.setItem('seller_id', res.seller);
            window.localStorage.setItem('currency_symbol', res.currency_symbol);
          });
        } catch (error) {
          console.error(error);
        }
      }
    }
    var lang = config.language !== 'auto' ? config.language : 'es';
    config.language = lang;
    console.log(config.local_storage_data);
    if (config.local_storage_data) {
      var lsd = getLocalStorageData();
      if (lsd) {
        try {
          dispatch({
            type: types.setQuotation,
            payload: _extends({}, quotation, {
              destination: {
                value: lsd.destination.id,
                label: lsd.destination.label,
                code: lsd.destination.code
              },
              dates: lsd.dates,
              paxs: {
                ages_1: lsd.ages_1,
                ages_2: lsd.ages_2
              }
            })
          });
        } catch (error) {
          console.error(error);
        }
      }
      console.log('lsd ----->', lsd);
      quoterData(_extends({}, lsd, {
        fetch_products: false
      }));
    }
    console.warn('1000');
  }, [config]);
  function closeDialog() {
    setDialogVisibility(false);
  }
  var gotoGlobalSite = function gotoGlobalSite() {
    setDialogVisibility(false);
    submit_quotation({
      redirect_global: true
    });
  };
  function submit_quotation(_ref2) {
    var _ref2$redirect_global = _ref2.redirect_global,
      redirect_global = _ref2$redirect_global === void 0 ? false : _ref2$redirect_global;
    dispatch({
      type: types.setQuotation,
      payload: _extends({}, quotation, {
        tooltip: _extends({}, quotation.tooltip, {
          visibility: false,
          title: ''
        }),
        submitted: true
      })
    });
    if (quotation.destination.value !== 0 && (quotation.paxs.ages_1 !== 0 || quotation.paxs.ages_2 !== 0) && quotation.dates.date_from !== '' && quotation.dates.date_to !== '') {
      var quotation_total_days = dayjs(quotation.dates.date_to).diff(dayjs(quotation.dates.date_from), 'day');
      quotation_total_days = isNaN(quotation_total_days) ? 0 : quotation_total_days;
      var insured = quotation.paxs.ages_1 + quotation.paxs.ages_2;
      if (quotation_total_days >= config.days_limit && config.market === 'br' && redirect_global === false) {
        setDialogVisibility(true);
      } else {
        QuoterProcess(_extends({}, quotation, {
          total_days: quotation_total_days,
          dates: _extends({}, quotation.dates, {
            date_from_raw: dayjs(quotation.dates.date_from).toDate(),
            date_to_raw: dayjs(quotation.dates.date_to).toDate()
          }),
          redirect_global: redirect_global,
          config: config
        }), config);
        quoterData(_extends({}, quotation, {
          insured: insured,
          fetch_products: true
        }));
      }
    }
  }
  return /*#__PURE__*/React__default.createElement(Fragment$1, null, /*#__PURE__*/React__default.createElement(Dialog, {
    open: dialog_visibility,
    onClose: closeDialog,
    sx: {
      '& .MuiDialog-container': {
        '& .MuiPaper-root': {
          width: '100%',
          maxWidth: '298px',
          padding: '0px 40px',
          fontFamily: 'Inter',
          color: '#2454AD',
          lineHeight: '19px',
          fontSize: '16px',
          fontWeight: 700,
          textAlign: 'center'
        }
      }
    }
  }, /*#__PURE__*/React__default.createElement(DialogTitle, null, /*#__PURE__*/React__default.createElement("div", {
    className: "dialog-close-btn quoter-top-4",
    onClick: closeDialog
  }, /*#__PURE__*/React__default.createElement("svg", {
    width: "25",
    height: "25",
    viewBox: "0 0 25 25",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M5.66202 5.30856C6.13065 4.83993 6.89045 4.83993 7.35908 5.30856L12.5105 10.46L17.662 5.30856C18.1306 4.83993 18.8904 4.83993 19.3591 5.30856C19.8277 5.77719 19.8277 6.53699 19.3591 7.00562L14.2076 12.1571L19.3591 17.3086C19.8277 17.7772 19.8277 18.537 19.3591 19.0056C18.8904 19.4743 18.1306 19.4743 17.662 19.0056L12.5105 13.8541L7.35908 19.0056C6.89045 19.4743 6.13065 19.4743 5.66202 19.0056C5.19339 18.537 5.19339 17.7772 5.66202 17.3086L10.8135 12.1571L5.66202 7.00562C5.19339 6.53699 5.19339 5.77719 5.66202 5.30856Z",
    fill: "#1B2225"
  })))), /*#__PURE__*/React__default.createElement("div", {
    className: "dialog-notification-body"
  }, /*#__PURE__*/React__default.createElement(SvgRedirection, null), /*#__PURE__*/React__default.createElement("p", {
    className: "dialog-notification-content"
  }, "Para coberturas superiores a 120 dias, a cota\xE7\xE3o continuar\xE1 no site internacional da Assist 365"), /*#__PURE__*/React__default.createElement("button", {
    onClick: gotoGlobalSite,
    className: "dialog-notification-button"
  }, "Continuar"))), Object.keys(config).length > 0 && /*#__PURE__*/React__default.createElement("div", {
    className: 'main-quoter-container ' + (config.mode === 'normal' ? 'normal-mode' : 'compact-mode')
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "quoter-flex quoter-items-center quoter quoter-justify-center quoter-flex-col sm:quoter-flex-row"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: ' ' + (view_mode === 'landscape' ? 'quoter-flex ' : 'quoter-w-[328px] sm:quoter-w-[330px] sm:quoter-mr-[10px]')
  }, /*#__PURE__*/React__default.createElement(Destiny, null), /*#__PURE__*/React__default.createElement(Dates, null)), /*#__PURE__*/React__default.createElement("div", {
    className: '' + (view_mode === 'landscape' ? 'flex ' : 'w-[228px] ')
  }, /*#__PURE__*/React__default.createElement(Paxs, null)), view_mode === 'landscape' && /*#__PURE__*/React__default.createElement("div", {
    className: "submit-btn quoter-h-[70px] quoter-w-[228px] quoter-flex quoter-ml-4"
  }, /*#__PURE__*/React__default.createElement("button", {
    onClick: submit_quotation,
    className: 'quote-submit-btn'
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "quoter-pr-4 quoter-font-sans quoter-text-xl quoter-font-semibold"
  }, view_mode !== 'landscape' ? t('quoter.btnSubmit') : t('quoter.btnSubmitRequote')), /*#__PURE__*/React__default.createElement("svg", {
    className: "quoter-mt-1",
    xmlns: "http://www.w3.org/2000/svg",
    width: "21",
    height: "21",
    viewBox: "0 0 21 21",
    fill: "none"
  }, /*#__PURE__*/React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M11.0737 3.89836C11.4642 3.50784 12.0973 3.50784 12.4879 3.89836L18.4879 9.89836C18.8784 10.2889 18.8784 10.9221 18.4879 11.3126L12.4879 17.3126C12.0973 17.7031 11.4642 17.7031 11.0737 17.3126C10.6831 16.9221 10.6831 16.2889 11.0737 15.8984L15.3665 11.6055L3.78076 11.6055C3.22848 11.6055 2.78076 11.1578 2.78076 10.6055C2.78076 10.0532 3.22848 9.60547 3.78076 9.60547H15.3665L11.0737 5.31258C10.6831 4.92205 10.6831 4.28889 11.0737 3.89836Z",
    fill: "#323334"
  }))))), view_mode !== 'landscape' && /*#__PURE__*/React__default.createElement("div", {
    className: "submit-btn quoter-h-[60px] quoter-max-w-[328px] sm:quoter-max-w-[570px] quoter-mt-3"
  }, /*#__PURE__*/React__default.createElement("button", {
    onClick: submit_quotation,
    className: "quote-submit-btn quoter-h-[60px]",
    id: (_config$seo = config.seo) !== null && _config$seo !== void 0 && _config$seo.btn_quote_id ? (_config$seo2 = config.seo) === null || _config$seo2 === void 0 ? void 0 : _config$seo2.btn_quote_id : 'btn-quoter'
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "quoter-pr-4 quoter-font-sans quoter-text-xl quoter-font-semibold"
  }, t('quoter.btnSubmit')), /*#__PURE__*/React__default.createElement("svg", {
    className: "quoter-mt-1",
    xmlns: "http://www.w3.org/2000/svg",
    width: "21",
    height: "21",
    viewBox: "0 0 21 21",
    fill: "none"
  }, /*#__PURE__*/React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M11.0737 3.89836C11.4642 3.50784 12.0973 3.50784 12.4879 3.89836L18.4879 9.89836C18.8784 10.2889 18.8784 10.9221 18.4879 11.3126L12.4879 17.3126C12.0973 17.7031 11.4642 17.7031 11.0737 17.3126C10.6831 16.9221 10.6831 16.2889 11.0737 15.8984L15.3665 11.6055L3.78076 11.6055C3.22848 11.6055 2.78076 11.1578 2.78076 10.6055C2.78076 10.0532 3.22848 9.60547 3.78076 9.60547H15.3665L11.0737 5.31258C10.6831 4.92205 10.6831 4.28889 11.0737 3.89836Z",
    fill: "#323334"
  }))))));
};

const consoleLogger = {
  type: 'logger',
  log(args) {
    this.output('log', args);
  },
  warn(args) {
    this.output('warn', args);
  },
  error(args) {
    this.output('error', args);
  },
  output(type, args) {
    if (console && console[type]) console[type].apply(console, args);
  }
};
class Logger {
  constructor(concreteLogger) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    this.init(concreteLogger, options);
  }
  init(concreteLogger) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    this.prefix = options.prefix || 'i18next:';
    this.logger = concreteLogger || consoleLogger;
    this.options = options;
    this.debug = options.debug;
  }
  log() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return this.forward(args, 'log', '', true);
  }
  warn() {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    return this.forward(args, 'warn', '', true);
  }
  error() {
    for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
      args[_key3] = arguments[_key3];
    }
    return this.forward(args, 'error', '');
  }
  deprecate() {
    for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
      args[_key4] = arguments[_key4];
    }
    return this.forward(args, 'warn', 'WARNING DEPRECATED: ', true);
  }
  forward(args, lvl, prefix, debugOnly) {
    if (debugOnly && !this.debug) return null;
    if (typeof args[0] === 'string') args[0] = `${prefix}${this.prefix} ${args[0]}`;
    return this.logger[lvl](args);
  }
  create(moduleName) {
    return new Logger(this.logger, {
      ...{
        prefix: `${this.prefix}:${moduleName}:`
      },
      ...this.options
    });
  }
  clone(options) {
    options = options || this.options;
    options.prefix = options.prefix || this.prefix;
    return new Logger(this.logger, options);
  }
}
var baseLogger = new Logger();

class EventEmitter {
  constructor() {
    this.observers = {};
  }
  on(events, listener) {
    events.split(' ').forEach(event => {
      this.observers[event] = this.observers[event] || [];
      this.observers[event].push(listener);
    });
    return this;
  }
  off(event, listener) {
    if (!this.observers[event]) return;
    if (!listener) {
      delete this.observers[event];
      return;
    }
    this.observers[event] = this.observers[event].filter(l => l !== listener);
  }
  emit(event) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    if (this.observers[event]) {
      const cloned = [].concat(this.observers[event]);
      cloned.forEach(observer => {
        observer(...args);
      });
    }
    if (this.observers['*']) {
      const cloned = [].concat(this.observers['*']);
      cloned.forEach(observer => {
        observer.apply(observer, [event, ...args]);
      });
    }
  }
}

function defer() {
  let res;
  let rej;
  const promise = new Promise((resolve, reject) => {
    res = resolve;
    rej = reject;
  });
  promise.resolve = res;
  promise.reject = rej;
  return promise;
}
function makeString(object) {
  if (object == null) return '';
  return '' + object;
}
function copy(a, s, t) {
  a.forEach(m => {
    if (s[m]) t[m] = s[m];
  });
}
function getLastOfPath(object, path, Empty) {
  function cleanKey(key) {
    return key && key.indexOf('###') > -1 ? key.replace(/###/g, '.') : key;
  }
  function canNotTraverseDeeper() {
    return !object || typeof object === 'string';
  }
  const stack = typeof path !== 'string' ? [].concat(path) : path.split('.');
  while (stack.length > 1) {
    if (canNotTraverseDeeper()) return {};
    const key = cleanKey(stack.shift());
    if (!object[key] && Empty) object[key] = new Empty();
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      object = object[key];
    } else {
      object = {};
    }
  }
  if (canNotTraverseDeeper()) return {};
  return {
    obj: object,
    k: cleanKey(stack.shift())
  };
}
function setPath(object, path, newValue) {
  const {
    obj,
    k
  } = getLastOfPath(object, path, Object);
  obj[k] = newValue;
}
function pushPath(object, path, newValue, concat) {
  const {
    obj,
    k
  } = getLastOfPath(object, path, Object);
  obj[k] = obj[k] || [];
  if (concat) obj[k] = obj[k].concat(newValue);
  if (!concat) obj[k].push(newValue);
}
function getPath(object, path) {
  const {
    obj,
    k
  } = getLastOfPath(object, path);
  if (!obj) return undefined;
  return obj[k];
}
function getPathWithDefaults(data, defaultData, key) {
  const value = getPath(data, key);
  if (value !== undefined) {
    return value;
  }
  return getPath(defaultData, key);
}
function deepExtend(target, source, overwrite) {
  for (const prop in source) {
    if (prop !== '__proto__' && prop !== 'constructor') {
      if (prop in target) {
        if (typeof target[prop] === 'string' || target[prop] instanceof String || typeof source[prop] === 'string' || source[prop] instanceof String) {
          if (overwrite) target[prop] = source[prop];
        } else {
          deepExtend(target[prop], source[prop], overwrite);
        }
      } else {
        target[prop] = source[prop];
      }
    }
  }
  return target;
}
function regexEscape(str) {
  return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
}
var _entityMap = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '/': '&#x2F;'
};
function escape(data) {
  if (typeof data === 'string') {
    return data.replace(/[&<>"'\/]/g, s => _entityMap[s]);
  }
  return data;
}
const chars = [' ', ',', '?', '!', ';'];
function looksLikeObjectPath(key, nsSeparator, keySeparator) {
  nsSeparator = nsSeparator || '';
  keySeparator = keySeparator || '';
  const possibleChars = chars.filter(c => nsSeparator.indexOf(c) < 0 && keySeparator.indexOf(c) < 0);
  if (possibleChars.length === 0) return true;
  const r = new RegExp(`(${possibleChars.map(c => c === '?' ? '\\?' : c).join('|')})`);
  let matched = !r.test(key);
  if (!matched) {
    const ki = key.indexOf(keySeparator);
    if (ki > 0 && !r.test(key.substring(0, ki))) {
      matched = true;
    }
  }
  return matched;
}
function deepFind(obj, path) {
  let keySeparator = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '.';
  if (!obj) return undefined;
  if (obj[path]) return obj[path];
  const paths = path.split(keySeparator);
  let current = obj;
  for (let i = 0; i < paths.length; ++i) {
    if (!current) return undefined;
    if (typeof current[paths[i]] === 'string' && i + 1 < paths.length) {
      return undefined;
    }
    if (current[paths[i]] === undefined) {
      let j = 2;
      let p = paths.slice(i, i + j).join(keySeparator);
      let mix = current[p];
      while (mix === undefined && paths.length > i + j) {
        j++;
        p = paths.slice(i, i + j).join(keySeparator);
        mix = current[p];
      }
      if (mix === undefined) return undefined;
      if (mix === null) return null;
      if (path.endsWith(p)) {
        if (typeof mix === 'string') return mix;
        if (p && typeof mix[p] === 'string') return mix[p];
      }
      const joinedPath = paths.slice(i + j).join(keySeparator);
      if (joinedPath) return deepFind(mix, joinedPath, keySeparator);
      return undefined;
    }
    current = current[paths[i]];
  }
  return current;
}
function getCleanedCode(code) {
  if (code && code.indexOf('_') > 0) return code.replace('_', '-');
  return code;
}

class ResourceStore extends EventEmitter {
  constructor(data) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
      ns: ['translation'],
      defaultNS: 'translation'
    };
    super();
    this.data = data || {};
    this.options = options;
    if (this.options.keySeparator === undefined) {
      this.options.keySeparator = '.';
    }
    if (this.options.ignoreJSONStructure === undefined) {
      this.options.ignoreJSONStructure = true;
    }
  }
  addNamespaces(ns) {
    if (this.options.ns.indexOf(ns) < 0) {
      this.options.ns.push(ns);
    }
  }
  removeNamespaces(ns) {
    const index = this.options.ns.indexOf(ns);
    if (index > -1) {
      this.options.ns.splice(index, 1);
    }
  }
  getResource(lng, ns, key) {
    let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    const keySeparator = options.keySeparator !== undefined ? options.keySeparator : this.options.keySeparator;
    const ignoreJSONStructure = options.ignoreJSONStructure !== undefined ? options.ignoreJSONStructure : this.options.ignoreJSONStructure;
    let path = [lng, ns];
    if (key && typeof key !== 'string') path = path.concat(key);
    if (key && typeof key === 'string') path = path.concat(keySeparator ? key.split(keySeparator) : key);
    if (lng.indexOf('.') > -1) {
      path = lng.split('.');
    }
    const result = getPath(this.data, path);
    if (result || !ignoreJSONStructure || typeof key !== 'string') return result;
    return deepFind(this.data && this.data[lng] && this.data[lng][ns], key, keySeparator);
  }
  addResource(lng, ns, key, value) {
    let options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {
      silent: false
    };
    const keySeparator = options.keySeparator !== undefined ? options.keySeparator : this.options.keySeparator;
    let path = [lng, ns];
    if (key) path = path.concat(keySeparator ? key.split(keySeparator) : key);
    if (lng.indexOf('.') > -1) {
      path = lng.split('.');
      value = ns;
      ns = path[1];
    }
    this.addNamespaces(ns);
    setPath(this.data, path, value);
    if (!options.silent) this.emit('added', lng, ns, key, value);
  }
  addResources(lng, ns, resources) {
    let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {
      silent: false
    };
    for (const m in resources) {
      if (typeof resources[m] === 'string' || Object.prototype.toString.apply(resources[m]) === '[object Array]') this.addResource(lng, ns, m, resources[m], {
        silent: true
      });
    }
    if (!options.silent) this.emit('added', lng, ns, resources);
  }
  addResourceBundle(lng, ns, resources, deep, overwrite) {
    let options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {
      silent: false
    };
    let path = [lng, ns];
    if (lng.indexOf('.') > -1) {
      path = lng.split('.');
      deep = resources;
      resources = ns;
      ns = path[1];
    }
    this.addNamespaces(ns);
    let pack = getPath(this.data, path) || {};
    if (deep) {
      deepExtend(pack, resources, overwrite);
    } else {
      pack = {
        ...pack,
        ...resources
      };
    }
    setPath(this.data, path, pack);
    if (!options.silent) this.emit('added', lng, ns, resources);
  }
  removeResourceBundle(lng, ns) {
    if (this.hasResourceBundle(lng, ns)) {
      delete this.data[lng][ns];
    }
    this.removeNamespaces(ns);
    this.emit('removed', lng, ns);
  }
  hasResourceBundle(lng, ns) {
    return this.getResource(lng, ns) !== undefined;
  }
  getResourceBundle(lng, ns) {
    if (!ns) ns = this.options.defaultNS;
    if (this.options.compatibilityAPI === 'v1') return {
      ...{},
      ...this.getResource(lng, ns)
    };
    return this.getResource(lng, ns);
  }
  getDataByLanguage(lng) {
    return this.data[lng];
  }
  hasLanguageSomeTranslations(lng) {
    const data = this.getDataByLanguage(lng);
    const n = data && Object.keys(data) || [];
    return !!n.find(v => data[v] && Object.keys(data[v]).length > 0);
  }
  toJSON() {
    return this.data;
  }
}

var postProcessor = {
  processors: {},
  addPostProcessor(module) {
    this.processors[module.name] = module;
  },
  handle(processors, value, key, options, translator) {
    processors.forEach(processor => {
      if (this.processors[processor]) value = this.processors[processor].process(value, key, options, translator);
    });
    return value;
  }
};

const checkedLoadedFor = {};
class Translator extends EventEmitter {
  constructor(services) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    super();
    copy(['resourceStore', 'languageUtils', 'pluralResolver', 'interpolator', 'backendConnector', 'i18nFormat', 'utils'], services, this);
    this.options = options;
    if (this.options.keySeparator === undefined) {
      this.options.keySeparator = '.';
    }
    this.logger = baseLogger.create('translator');
  }
  changeLanguage(lng) {
    if (lng) this.language = lng;
  }
  exists(key) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
      interpolation: {}
    };
    if (key === undefined || key === null) {
      return false;
    }
    const resolved = this.resolve(key, options);
    return resolved && resolved.res !== undefined;
  }
  extractFromKey(key, options) {
    let nsSeparator = options.nsSeparator !== undefined ? options.nsSeparator : this.options.nsSeparator;
    if (nsSeparator === undefined) nsSeparator = ':';
    const keySeparator = options.keySeparator !== undefined ? options.keySeparator : this.options.keySeparator;
    let namespaces = options.ns || this.options.defaultNS || [];
    const wouldCheckForNsInKey = nsSeparator && key.indexOf(nsSeparator) > -1;
    const seemsNaturalLanguage = !this.options.userDefinedKeySeparator && !options.keySeparator && !this.options.userDefinedNsSeparator && !options.nsSeparator && !looksLikeObjectPath(key, nsSeparator, keySeparator);
    if (wouldCheckForNsInKey && !seemsNaturalLanguage) {
      const m = key.match(this.interpolator.nestingRegexp);
      if (m && m.length > 0) {
        return {
          key,
          namespaces
        };
      }
      const parts = key.split(nsSeparator);
      if (nsSeparator !== keySeparator || nsSeparator === keySeparator && this.options.ns.indexOf(parts[0]) > -1) namespaces = parts.shift();
      key = parts.join(keySeparator);
    }
    if (typeof namespaces === 'string') namespaces = [namespaces];
    return {
      key,
      namespaces
    };
  }
  translate(keys, options, lastKey) {
    if (typeof options !== 'object' && this.options.overloadTranslationOptionHandler) {
      options = this.options.overloadTranslationOptionHandler(arguments);
    }
    if (typeof options === 'object') options = {
      ...options
    };
    if (!options) options = {};
    if (keys === undefined || keys === null) return '';
    if (!Array.isArray(keys)) keys = [String(keys)];
    const returnDetails = options.returnDetails !== undefined ? options.returnDetails : this.options.returnDetails;
    const keySeparator = options.keySeparator !== undefined ? options.keySeparator : this.options.keySeparator;
    const {
      key,
      namespaces
    } = this.extractFromKey(keys[keys.length - 1], options);
    const namespace = namespaces[namespaces.length - 1];
    const lng = options.lng || this.language;
    const appendNamespaceToCIMode = options.appendNamespaceToCIMode || this.options.appendNamespaceToCIMode;
    if (lng && lng.toLowerCase() === 'cimode') {
      if (appendNamespaceToCIMode) {
        const nsSeparator = options.nsSeparator || this.options.nsSeparator;
        if (returnDetails) {
          return {
            res: `${namespace}${nsSeparator}${key}`,
            usedKey: key,
            exactUsedKey: key,
            usedLng: lng,
            usedNS: namespace,
            usedParams: this.getUsedParamsDetails(options)
          };
        }
        return `${namespace}${nsSeparator}${key}`;
      }
      if (returnDetails) {
        return {
          res: key,
          usedKey: key,
          exactUsedKey: key,
          usedLng: lng,
          usedNS: namespace,
          usedParams: this.getUsedParamsDetails(options)
        };
      }
      return key;
    }
    const resolved = this.resolve(keys, options);
    let res = resolved && resolved.res;
    const resUsedKey = resolved && resolved.usedKey || key;
    const resExactUsedKey = resolved && resolved.exactUsedKey || key;
    const resType = Object.prototype.toString.apply(res);
    const noObject = ['[object Number]', '[object Function]', '[object RegExp]'];
    const joinArrays = options.joinArrays !== undefined ? options.joinArrays : this.options.joinArrays;
    const handleAsObjectInI18nFormat = !this.i18nFormat || this.i18nFormat.handleAsObject;
    const handleAsObject = typeof res !== 'string' && typeof res !== 'boolean' && typeof res !== 'number';
    if (handleAsObjectInI18nFormat && res && handleAsObject && noObject.indexOf(resType) < 0 && !(typeof joinArrays === 'string' && resType === '[object Array]')) {
      if (!options.returnObjects && !this.options.returnObjects) {
        if (!this.options.returnedObjectHandler) {
          this.logger.warn('accessing an object - but returnObjects options is not enabled!');
        }
        const r = this.options.returnedObjectHandler ? this.options.returnedObjectHandler(resUsedKey, res, {
          ...options,
          ns: namespaces
        }) : `key '${key} (${this.language})' returned an object instead of string.`;
        if (returnDetails) {
          resolved.res = r;
          resolved.usedParams = this.getUsedParamsDetails(options);
          return resolved;
        }
        return r;
      }
      if (keySeparator) {
        const resTypeIsArray = resType === '[object Array]';
        const copy = resTypeIsArray ? [] : {};
        const newKeyToUse = resTypeIsArray ? resExactUsedKey : resUsedKey;
        for (const m in res) {
          if (Object.prototype.hasOwnProperty.call(res, m)) {
            const deepKey = `${newKeyToUse}${keySeparator}${m}`;
            copy[m] = this.translate(deepKey, {
              ...options,
              ...{
                joinArrays: false,
                ns: namespaces
              }
            });
            if (copy[m] === deepKey) copy[m] = res[m];
          }
        }
        res = copy;
      }
    } else if (handleAsObjectInI18nFormat && typeof joinArrays === 'string' && resType === '[object Array]') {
      res = res.join(joinArrays);
      if (res) res = this.extendTranslation(res, keys, options, lastKey);
    } else {
      let usedDefault = false;
      let usedKey = false;
      const needsPluralHandling = options.count !== undefined && typeof options.count !== 'string';
      const hasDefaultValue = Translator.hasDefaultValue(options);
      const defaultValueSuffix = needsPluralHandling ? this.pluralResolver.getSuffix(lng, options.count, options) : '';
      const defaultValueSuffixOrdinalFallback = options.ordinal && needsPluralHandling ? this.pluralResolver.getSuffix(lng, options.count, {
        ordinal: false
      }) : '';
      const defaultValue = options[`defaultValue${defaultValueSuffix}`] || options[`defaultValue${defaultValueSuffixOrdinalFallback}`] || options.defaultValue;
      if (!this.isValidLookup(res) && hasDefaultValue) {
        usedDefault = true;
        res = defaultValue;
      }
      if (!this.isValidLookup(res)) {
        usedKey = true;
        res = key;
      }
      const missingKeyNoValueFallbackToKey = options.missingKeyNoValueFallbackToKey || this.options.missingKeyNoValueFallbackToKey;
      const resForMissing = missingKeyNoValueFallbackToKey && usedKey ? undefined : res;
      const updateMissing = hasDefaultValue && defaultValue !== res && this.options.updateMissing;
      if (usedKey || usedDefault || updateMissing) {
        this.logger.log(updateMissing ? 'updateKey' : 'missingKey', lng, namespace, key, updateMissing ? defaultValue : res);
        if (keySeparator) {
          const fk = this.resolve(key, {
            ...options,
            keySeparator: false
          });
          if (fk && fk.res) this.logger.warn('Seems the loaded translations were in flat JSON format instead of nested. Either set keySeparator: false on init or make sure your translations are published in nested format.');
        }
        let lngs = [];
        const fallbackLngs = this.languageUtils.getFallbackCodes(this.options.fallbackLng, options.lng || this.language);
        if (this.options.saveMissingTo === 'fallback' && fallbackLngs && fallbackLngs[0]) {
          for (let i = 0; i < fallbackLngs.length; i++) {
            lngs.push(fallbackLngs[i]);
          }
        } else if (this.options.saveMissingTo === 'all') {
          lngs = this.languageUtils.toResolveHierarchy(options.lng || this.language);
        } else {
          lngs.push(options.lng || this.language);
        }
        const send = (l, k, specificDefaultValue) => {
          const defaultForMissing = hasDefaultValue && specificDefaultValue !== res ? specificDefaultValue : resForMissing;
          if (this.options.missingKeyHandler) {
            this.options.missingKeyHandler(l, namespace, k, defaultForMissing, updateMissing, options);
          } else if (this.backendConnector && this.backendConnector.saveMissing) {
            this.backendConnector.saveMissing(l, namespace, k, defaultForMissing, updateMissing, options);
          }
          this.emit('missingKey', l, namespace, k, res);
        };
        if (this.options.saveMissing) {
          if (this.options.saveMissingPlurals && needsPluralHandling) {
            lngs.forEach(language => {
              this.pluralResolver.getSuffixes(language, options).forEach(suffix => {
                send([language], key + suffix, options[`defaultValue${suffix}`] || defaultValue);
              });
            });
          } else {
            send(lngs, key, defaultValue);
          }
        }
      }
      res = this.extendTranslation(res, keys, options, resolved, lastKey);
      if (usedKey && res === key && this.options.appendNamespaceToMissingKey) res = `${namespace}:${key}`;
      if ((usedKey || usedDefault) && this.options.parseMissingKeyHandler) {
        if (this.options.compatibilityAPI !== 'v1') {
          res = this.options.parseMissingKeyHandler(this.options.appendNamespaceToMissingKey ? `${namespace}:${key}` : key, usedDefault ? res : undefined);
        } else {
          res = this.options.parseMissingKeyHandler(res);
        }
      }
    }
    if (returnDetails) {
      resolved.res = res;
      resolved.usedParams = this.getUsedParamsDetails(options);
      return resolved;
    }
    return res;
  }
  extendTranslation(res, key, options, resolved, lastKey) {
    var _this = this;
    if (this.i18nFormat && this.i18nFormat.parse) {
      res = this.i18nFormat.parse(res, {
        ...this.options.interpolation.defaultVariables,
        ...options
      }, options.lng || this.language || resolved.usedLng, resolved.usedNS, resolved.usedKey, {
        resolved
      });
    } else if (!options.skipInterpolation) {
      if (options.interpolation) this.interpolator.init({
        ...options,
        ...{
          interpolation: {
            ...this.options.interpolation,
            ...options.interpolation
          }
        }
      });
      const skipOnVariables = typeof res === 'string' && (options && options.interpolation && options.interpolation.skipOnVariables !== undefined ? options.interpolation.skipOnVariables : this.options.interpolation.skipOnVariables);
      let nestBef;
      if (skipOnVariables) {
        const nb = res.match(this.interpolator.nestingRegexp);
        nestBef = nb && nb.length;
      }
      let data = options.replace && typeof options.replace !== 'string' ? options.replace : options;
      if (this.options.interpolation.defaultVariables) data = {
        ...this.options.interpolation.defaultVariables,
        ...data
      };
      res = this.interpolator.interpolate(res, data, options.lng || this.language, options);
      if (skipOnVariables) {
        const na = res.match(this.interpolator.nestingRegexp);
        const nestAft = na && na.length;
        if (nestBef < nestAft) options.nest = false;
      }
      if (!options.lng && this.options.compatibilityAPI !== 'v1' && resolved && resolved.res) options.lng = resolved.usedLng;
      if (options.nest !== false) res = this.interpolator.nest(res, function () {
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }
        if (lastKey && lastKey[0] === args[0] && !options.context) {
          _this.logger.warn(`It seems you are nesting recursively key: ${args[0]} in key: ${key[0]}`);
          return null;
        }
        return _this.translate(...args, key);
      }, options);
      if (options.interpolation) this.interpolator.reset();
    }
    const postProcess = options.postProcess || this.options.postProcess;
    const postProcessorNames = typeof postProcess === 'string' ? [postProcess] : postProcess;
    if (res !== undefined && res !== null && postProcessorNames && postProcessorNames.length && options.applyPostProcessor !== false) {
      res = postProcessor.handle(postProcessorNames, res, key, this.options && this.options.postProcessPassResolved ? {
        i18nResolved: {
          ...resolved,
          usedParams: this.getUsedParamsDetails(options)
        },
        ...options
      } : options, this);
    }
    return res;
  }
  resolve(keys) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let found;
    let usedKey;
    let exactUsedKey;
    let usedLng;
    let usedNS;
    if (typeof keys === 'string') keys = [keys];
    keys.forEach(k => {
      if (this.isValidLookup(found)) return;
      const extracted = this.extractFromKey(k, options);
      const key = extracted.key;
      usedKey = key;
      let namespaces = extracted.namespaces;
      if (this.options.fallbackNS) namespaces = namespaces.concat(this.options.fallbackNS);
      const needsPluralHandling = options.count !== undefined && typeof options.count !== 'string';
      const needsZeroSuffixLookup = needsPluralHandling && !options.ordinal && options.count === 0 && this.pluralResolver.shouldUseIntlApi();
      const needsContextHandling = options.context !== undefined && (typeof options.context === 'string' || typeof options.context === 'number') && options.context !== '';
      const codes = options.lngs ? options.lngs : this.languageUtils.toResolveHierarchy(options.lng || this.language, options.fallbackLng);
      namespaces.forEach(ns => {
        if (this.isValidLookup(found)) return;
        usedNS = ns;
        if (!checkedLoadedFor[`${codes[0]}-${ns}`] && this.utils && this.utils.hasLoadedNamespace && !this.utils.hasLoadedNamespace(usedNS)) {
          checkedLoadedFor[`${codes[0]}-${ns}`] = true;
          this.logger.warn(`key "${usedKey}" for languages "${codes.join(', ')}" won't get resolved as namespace "${usedNS}" was not yet loaded`, 'This means something IS WRONG in your setup. You access the t function before i18next.init / i18next.loadNamespace / i18next.changeLanguage was done. Wait for the callback or Promise to resolve before accessing it!!!');
        }
        codes.forEach(code => {
          if (this.isValidLookup(found)) return;
          usedLng = code;
          const finalKeys = [key];
          if (this.i18nFormat && this.i18nFormat.addLookupKeys) {
            this.i18nFormat.addLookupKeys(finalKeys, key, code, ns, options);
          } else {
            let pluralSuffix;
            if (needsPluralHandling) pluralSuffix = this.pluralResolver.getSuffix(code, options.count, options);
            const zeroSuffix = `${this.options.pluralSeparator}zero`;
            const ordinalPrefix = `${this.options.pluralSeparator}ordinal${this.options.pluralSeparator}`;
            if (needsPluralHandling) {
              finalKeys.push(key + pluralSuffix);
              if (options.ordinal && pluralSuffix.indexOf(ordinalPrefix) === 0) {
                finalKeys.push(key + pluralSuffix.replace(ordinalPrefix, this.options.pluralSeparator));
              }
              if (needsZeroSuffixLookup) {
                finalKeys.push(key + zeroSuffix);
              }
            }
            if (needsContextHandling) {
              const contextKey = `${key}${this.options.contextSeparator}${options.context}`;
              finalKeys.push(contextKey);
              if (needsPluralHandling) {
                finalKeys.push(contextKey + pluralSuffix);
                if (options.ordinal && pluralSuffix.indexOf(ordinalPrefix) === 0) {
                  finalKeys.push(contextKey + pluralSuffix.replace(ordinalPrefix, this.options.pluralSeparator));
                }
                if (needsZeroSuffixLookup) {
                  finalKeys.push(contextKey + zeroSuffix);
                }
              }
            }
          }
          let possibleKey;
          while (possibleKey = finalKeys.pop()) {
            if (!this.isValidLookup(found)) {
              exactUsedKey = possibleKey;
              found = this.getResource(code, ns, possibleKey, options);
            }
          }
        });
      });
    });
    return {
      res: found,
      usedKey,
      exactUsedKey,
      usedLng,
      usedNS
    };
  }
  isValidLookup(res) {
    return res !== undefined && !(!this.options.returnNull && res === null) && !(!this.options.returnEmptyString && res === '');
  }
  getResource(code, ns, key) {
    let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    if (this.i18nFormat && this.i18nFormat.getResource) return this.i18nFormat.getResource(code, ns, key, options);
    return this.resourceStore.getResource(code, ns, key, options);
  }
  getUsedParamsDetails() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const optionsKeys = ['defaultValue', 'ordinal', 'context', 'replace', 'lng', 'lngs', 'fallbackLng', 'ns', 'keySeparator', 'nsSeparator', 'returnObjects', 'returnDetails', 'joinArrays', 'postProcess', 'interpolation'];
    const useOptionsReplaceForData = options.replace && typeof options.replace !== 'string';
    let data = useOptionsReplaceForData ? options.replace : options;
    if (useOptionsReplaceForData && typeof options.count !== 'undefined') {
      data.count = options.count;
    }
    if (this.options.interpolation.defaultVariables) {
      data = {
        ...this.options.interpolation.defaultVariables,
        ...data
      };
    }
    if (!useOptionsReplaceForData) {
      data = {
        ...data
      };
      for (const key of optionsKeys) {
        delete data[key];
      }
    }
    return data;
  }
  static hasDefaultValue(options) {
    const prefix = 'defaultValue';
    for (const option in options) {
      if (Object.prototype.hasOwnProperty.call(options, option) && prefix === option.substring(0, prefix.length) && undefined !== options[option]) {
        return true;
      }
    }
    return false;
  }
}

function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
class LanguageUtil {
  constructor(options) {
    this.options = options;
    this.supportedLngs = this.options.supportedLngs || false;
    this.logger = baseLogger.create('languageUtils');
  }
  getScriptPartFromCode(code) {
    code = getCleanedCode(code);
    if (!code || code.indexOf('-') < 0) return null;
    const p = code.split('-');
    if (p.length === 2) return null;
    p.pop();
    if (p[p.length - 1].toLowerCase() === 'x') return null;
    return this.formatLanguageCode(p.join('-'));
  }
  getLanguagePartFromCode(code) {
    code = getCleanedCode(code);
    if (!code || code.indexOf('-') < 0) return code;
    const p = code.split('-');
    return this.formatLanguageCode(p[0]);
  }
  formatLanguageCode(code) {
    if (typeof code === 'string' && code.indexOf('-') > -1) {
      const specialCases = ['hans', 'hant', 'latn', 'cyrl', 'cans', 'mong', 'arab'];
      let p = code.split('-');
      if (this.options.lowerCaseLng) {
        p = p.map(part => part.toLowerCase());
      } else if (p.length === 2) {
        p[0] = p[0].toLowerCase();
        p[1] = p[1].toUpperCase();
        if (specialCases.indexOf(p[1].toLowerCase()) > -1) p[1] = capitalize(p[1].toLowerCase());
      } else if (p.length === 3) {
        p[0] = p[0].toLowerCase();
        if (p[1].length === 2) p[1] = p[1].toUpperCase();
        if (p[0] !== 'sgn' && p[2].length === 2) p[2] = p[2].toUpperCase();
        if (specialCases.indexOf(p[1].toLowerCase()) > -1) p[1] = capitalize(p[1].toLowerCase());
        if (specialCases.indexOf(p[2].toLowerCase()) > -1) p[2] = capitalize(p[2].toLowerCase());
      }
      return p.join('-');
    }
    return this.options.cleanCode || this.options.lowerCaseLng ? code.toLowerCase() : code;
  }
  isSupportedCode(code) {
    if (this.options.load === 'languageOnly' || this.options.nonExplicitSupportedLngs) {
      code = this.getLanguagePartFromCode(code);
    }
    return !this.supportedLngs || !this.supportedLngs.length || this.supportedLngs.indexOf(code) > -1;
  }
  getBestMatchFromCodes(codes) {
    if (!codes) return null;
    let found;
    codes.forEach(code => {
      if (found) return;
      const cleanedLng = this.formatLanguageCode(code);
      if (!this.options.supportedLngs || this.isSupportedCode(cleanedLng)) found = cleanedLng;
    });
    if (!found && this.options.supportedLngs) {
      codes.forEach(code => {
        if (found) return;
        const lngOnly = this.getLanguagePartFromCode(code);
        if (this.isSupportedCode(lngOnly)) return found = lngOnly;
        found = this.options.supportedLngs.find(supportedLng => {
          if (supportedLng === lngOnly) return supportedLng;
          if (supportedLng.indexOf('-') < 0 && lngOnly.indexOf('-') < 0) return;
          if (supportedLng.indexOf(lngOnly) === 0) return supportedLng;
        });
      });
    }
    if (!found) found = this.getFallbackCodes(this.options.fallbackLng)[0];
    return found;
  }
  getFallbackCodes(fallbacks, code) {
    if (!fallbacks) return [];
    if (typeof fallbacks === 'function') fallbacks = fallbacks(code);
    if (typeof fallbacks === 'string') fallbacks = [fallbacks];
    if (Object.prototype.toString.apply(fallbacks) === '[object Array]') return fallbacks;
    if (!code) return fallbacks.default || [];
    let found = fallbacks[code];
    if (!found) found = fallbacks[this.getScriptPartFromCode(code)];
    if (!found) found = fallbacks[this.formatLanguageCode(code)];
    if (!found) found = fallbacks[this.getLanguagePartFromCode(code)];
    if (!found) found = fallbacks.default;
    return found || [];
  }
  toResolveHierarchy(code, fallbackCode) {
    const fallbackCodes = this.getFallbackCodes(fallbackCode || this.options.fallbackLng || [], code);
    const codes = [];
    const addCode = c => {
      if (!c) return;
      if (this.isSupportedCode(c)) {
        codes.push(c);
      } else {
        this.logger.warn(`rejecting language code not found in supportedLngs: ${c}`);
      }
    };
    if (typeof code === 'string' && (code.indexOf('-') > -1 || code.indexOf('_') > -1)) {
      if (this.options.load !== 'languageOnly') addCode(this.formatLanguageCode(code));
      if (this.options.load !== 'languageOnly' && this.options.load !== 'currentOnly') addCode(this.getScriptPartFromCode(code));
      if (this.options.load !== 'currentOnly') addCode(this.getLanguagePartFromCode(code));
    } else if (typeof code === 'string') {
      addCode(this.formatLanguageCode(code));
    }
    fallbackCodes.forEach(fc => {
      if (codes.indexOf(fc) < 0) addCode(this.formatLanguageCode(fc));
    });
    return codes;
  }
}

let sets = [{
  lngs: ['ach', 'ak', 'am', 'arn', 'br', 'fil', 'gun', 'ln', 'mfe', 'mg', 'mi', 'oc', 'pt', 'pt-BR', 'tg', 'tl', 'ti', 'tr', 'uz', 'wa'],
  nr: [1, 2],
  fc: 1
}, {
  lngs: ['af', 'an', 'ast', 'az', 'bg', 'bn', 'ca', 'da', 'de', 'dev', 'el', 'en', 'eo', 'es', 'et', 'eu', 'fi', 'fo', 'fur', 'fy', 'gl', 'gu', 'ha', 'hi', 'hu', 'hy', 'ia', 'it', 'kk', 'kn', 'ku', 'lb', 'mai', 'ml', 'mn', 'mr', 'nah', 'nap', 'nb', 'ne', 'nl', 'nn', 'no', 'nso', 'pa', 'pap', 'pms', 'ps', 'pt-PT', 'rm', 'sco', 'se', 'si', 'so', 'son', 'sq', 'sv', 'sw', 'ta', 'te', 'tk', 'ur', 'yo'],
  nr: [1, 2],
  fc: 2
}, {
  lngs: ['ay', 'bo', 'cgg', 'fa', 'ht', 'id', 'ja', 'jbo', 'ka', 'km', 'ko', 'ky', 'lo', 'ms', 'sah', 'su', 'th', 'tt', 'ug', 'vi', 'wo', 'zh'],
  nr: [1],
  fc: 3
}, {
  lngs: ['be', 'bs', 'cnr', 'dz', 'hr', 'ru', 'sr', 'uk'],
  nr: [1, 2, 5],
  fc: 4
}, {
  lngs: ['ar'],
  nr: [0, 1, 2, 3, 11, 100],
  fc: 5
}, {
  lngs: ['cs', 'sk'],
  nr: [1, 2, 5],
  fc: 6
}, {
  lngs: ['csb', 'pl'],
  nr: [1, 2, 5],
  fc: 7
}, {
  lngs: ['cy'],
  nr: [1, 2, 3, 8],
  fc: 8
}, {
  lngs: ['fr'],
  nr: [1, 2],
  fc: 9
}, {
  lngs: ['ga'],
  nr: [1, 2, 3, 7, 11],
  fc: 10
}, {
  lngs: ['gd'],
  nr: [1, 2, 3, 20],
  fc: 11
}, {
  lngs: ['is'],
  nr: [1, 2],
  fc: 12
}, {
  lngs: ['jv'],
  nr: [0, 1],
  fc: 13
}, {
  lngs: ['kw'],
  nr: [1, 2, 3, 4],
  fc: 14
}, {
  lngs: ['lt'],
  nr: [1, 2, 10],
  fc: 15
}, {
  lngs: ['lv'],
  nr: [1, 2, 0],
  fc: 16
}, {
  lngs: ['mk'],
  nr: [1, 2],
  fc: 17
}, {
  lngs: ['mnk'],
  nr: [0, 1, 2],
  fc: 18
}, {
  lngs: ['mt'],
  nr: [1, 2, 11, 20],
  fc: 19
}, {
  lngs: ['or'],
  nr: [2, 1],
  fc: 2
}, {
  lngs: ['ro'],
  nr: [1, 2, 20],
  fc: 20
}, {
  lngs: ['sl'],
  nr: [5, 1, 2, 3],
  fc: 21
}, {
  lngs: ['he', 'iw'],
  nr: [1, 2, 20, 21],
  fc: 22
}];
let _rulesPluralsTypes = {
  1: function (n) {
    return Number(n > 1);
  },
  2: function (n) {
    return Number(n != 1);
  },
  3: function (n) {
    return 0;
  },
  4: function (n) {
    return Number(n % 10 == 1 && n % 100 != 11 ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2);
  },
  5: function (n) {
    return Number(n == 0 ? 0 : n == 1 ? 1 : n == 2 ? 2 : n % 100 >= 3 && n % 100 <= 10 ? 3 : n % 100 >= 11 ? 4 : 5);
  },
  6: function (n) {
    return Number(n == 1 ? 0 : n >= 2 && n <= 4 ? 1 : 2);
  },
  7: function (n) {
    return Number(n == 1 ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2);
  },
  8: function (n) {
    return Number(n == 1 ? 0 : n == 2 ? 1 : n != 8 && n != 11 ? 2 : 3);
  },
  9: function (n) {
    return Number(n >= 2);
  },
  10: function (n) {
    return Number(n == 1 ? 0 : n == 2 ? 1 : n < 7 ? 2 : n < 11 ? 3 : 4);
  },
  11: function (n) {
    return Number(n == 1 || n == 11 ? 0 : n == 2 || n == 12 ? 1 : n > 2 && n < 20 ? 2 : 3);
  },
  12: function (n) {
    return Number(n % 10 != 1 || n % 100 == 11);
  },
  13: function (n) {
    return Number(n !== 0);
  },
  14: function (n) {
    return Number(n == 1 ? 0 : n == 2 ? 1 : n == 3 ? 2 : 3);
  },
  15: function (n) {
    return Number(n % 10 == 1 && n % 100 != 11 ? 0 : n % 10 >= 2 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2);
  },
  16: function (n) {
    return Number(n % 10 == 1 && n % 100 != 11 ? 0 : n !== 0 ? 1 : 2);
  },
  17: function (n) {
    return Number(n == 1 || n % 10 == 1 && n % 100 != 11 ? 0 : 1);
  },
  18: function (n) {
    return Number(n == 0 ? 0 : n == 1 ? 1 : 2);
  },
  19: function (n) {
    return Number(n == 1 ? 0 : n == 0 || n % 100 > 1 && n % 100 < 11 ? 1 : n % 100 > 10 && n % 100 < 20 ? 2 : 3);
  },
  20: function (n) {
    return Number(n == 1 ? 0 : n == 0 || n % 100 > 0 && n % 100 < 20 ? 1 : 2);
  },
  21: function (n) {
    return Number(n % 100 == 1 ? 1 : n % 100 == 2 ? 2 : n % 100 == 3 || n % 100 == 4 ? 3 : 0);
  },
  22: function (n) {
    return Number(n == 1 ? 0 : n == 2 ? 1 : (n < 0 || n > 10) && n % 10 == 0 ? 2 : 3);
  }
};
const nonIntlVersions = ['v1', 'v2', 'v3'];
const intlVersions = ['v4'];
const suffixesOrder = {
  zero: 0,
  one: 1,
  two: 2,
  few: 3,
  many: 4,
  other: 5
};
function createRules() {
  const rules = {};
  sets.forEach(set => {
    set.lngs.forEach(l => {
      rules[l] = {
        numbers: set.nr,
        plurals: _rulesPluralsTypes[set.fc]
      };
    });
  });
  return rules;
}
class PluralResolver {
  constructor(languageUtils) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    this.languageUtils = languageUtils;
    this.options = options;
    this.logger = baseLogger.create('pluralResolver');
    if ((!this.options.compatibilityJSON || intlVersions.includes(this.options.compatibilityJSON)) && (typeof Intl === 'undefined' || !Intl.PluralRules)) {
      this.options.compatibilityJSON = 'v3';
      this.logger.error('Your environment seems not to be Intl API compatible, use an Intl.PluralRules polyfill. Will fallback to the compatibilityJSON v3 format handling.');
    }
    this.rules = createRules();
  }
  addRule(lng, obj) {
    this.rules[lng] = obj;
  }
  getRule(code) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    if (this.shouldUseIntlApi()) {
      try {
        return new Intl.PluralRules(getCleanedCode(code), {
          type: options.ordinal ? 'ordinal' : 'cardinal'
        });
      } catch {
        return;
      }
    }
    return this.rules[code] || this.rules[this.languageUtils.getLanguagePartFromCode(code)];
  }
  needsPlural(code) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const rule = this.getRule(code, options);
    if (this.shouldUseIntlApi()) {
      return rule && rule.resolvedOptions().pluralCategories.length > 1;
    }
    return rule && rule.numbers.length > 1;
  }
  getPluralFormsOfKey(code, key) {
    let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    return this.getSuffixes(code, options).map(suffix => `${key}${suffix}`);
  }
  getSuffixes(code) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const rule = this.getRule(code, options);
    if (!rule) {
      return [];
    }
    if (this.shouldUseIntlApi()) {
      return rule.resolvedOptions().pluralCategories.sort((pluralCategory1, pluralCategory2) => suffixesOrder[pluralCategory1] - suffixesOrder[pluralCategory2]).map(pluralCategory => `${this.options.prepend}${options.ordinal ? `ordinal${this.options.prepend}` : ''}${pluralCategory}`);
    }
    return rule.numbers.map(number => this.getSuffix(code, number, options));
  }
  getSuffix(code, count) {
    let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    const rule = this.getRule(code, options);
    if (rule) {
      if (this.shouldUseIntlApi()) {
        return `${this.options.prepend}${options.ordinal ? `ordinal${this.options.prepend}` : ''}${rule.select(count)}`;
      }
      return this.getSuffixRetroCompatible(rule, count);
    }
    this.logger.warn(`no plural rule found for: ${code}`);
    return '';
  }
  getSuffixRetroCompatible(rule, count) {
    const idx = rule.noAbs ? rule.plurals(count) : rule.plurals(Math.abs(count));
    let suffix = rule.numbers[idx];
    if (this.options.simplifyPluralSuffix && rule.numbers.length === 2 && rule.numbers[0] === 1) {
      if (suffix === 2) {
        suffix = 'plural';
      } else if (suffix === 1) {
        suffix = '';
      }
    }
    const returnSuffix = () => this.options.prepend && suffix.toString() ? this.options.prepend + suffix.toString() : suffix.toString();
    if (this.options.compatibilityJSON === 'v1') {
      if (suffix === 1) return '';
      if (typeof suffix === 'number') return `_plural_${suffix.toString()}`;
      return returnSuffix();
    } else if (this.options.compatibilityJSON === 'v2') {
      return returnSuffix();
    } else if (this.options.simplifyPluralSuffix && rule.numbers.length === 2 && rule.numbers[0] === 1) {
      return returnSuffix();
    }
    return this.options.prepend && idx.toString() ? this.options.prepend + idx.toString() : idx.toString();
  }
  shouldUseIntlApi() {
    return !nonIntlVersions.includes(this.options.compatibilityJSON);
  }
}

function deepFindWithDefaults(data, defaultData, key) {
  let keySeparator = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '.';
  let ignoreJSONStructure = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : true;
  let path = getPathWithDefaults(data, defaultData, key);
  if (!path && ignoreJSONStructure && typeof key === 'string') {
    path = deepFind(data, key, keySeparator);
    if (path === undefined) path = deepFind(defaultData, key, keySeparator);
  }
  return path;
}
class Interpolator {
  constructor() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    this.logger = baseLogger.create('interpolator');
    this.options = options;
    this.format = options.interpolation && options.interpolation.format || (value => value);
    this.init(options);
  }
  init() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    if (!options.interpolation) options.interpolation = {
      escapeValue: true
    };
    const iOpts = options.interpolation;
    this.escape = iOpts.escape !== undefined ? iOpts.escape : escape;
    this.escapeValue = iOpts.escapeValue !== undefined ? iOpts.escapeValue : true;
    this.useRawValueToEscape = iOpts.useRawValueToEscape !== undefined ? iOpts.useRawValueToEscape : false;
    this.prefix = iOpts.prefix ? regexEscape(iOpts.prefix) : iOpts.prefixEscaped || '{{';
    this.suffix = iOpts.suffix ? regexEscape(iOpts.suffix) : iOpts.suffixEscaped || '}}';
    this.formatSeparator = iOpts.formatSeparator ? iOpts.formatSeparator : iOpts.formatSeparator || ',';
    this.unescapePrefix = iOpts.unescapeSuffix ? '' : iOpts.unescapePrefix || '-';
    this.unescapeSuffix = this.unescapePrefix ? '' : iOpts.unescapeSuffix || '';
    this.nestingPrefix = iOpts.nestingPrefix ? regexEscape(iOpts.nestingPrefix) : iOpts.nestingPrefixEscaped || regexEscape('$t(');
    this.nestingSuffix = iOpts.nestingSuffix ? regexEscape(iOpts.nestingSuffix) : iOpts.nestingSuffixEscaped || regexEscape(')');
    this.nestingOptionsSeparator = iOpts.nestingOptionsSeparator ? iOpts.nestingOptionsSeparator : iOpts.nestingOptionsSeparator || ',';
    this.maxReplaces = iOpts.maxReplaces ? iOpts.maxReplaces : 1000;
    this.alwaysFormat = iOpts.alwaysFormat !== undefined ? iOpts.alwaysFormat : false;
    this.resetRegExp();
  }
  reset() {
    if (this.options) this.init(this.options);
  }
  resetRegExp() {
    const regexpStr = `${this.prefix}(.+?)${this.suffix}`;
    this.regexp = new RegExp(regexpStr, 'g');
    const regexpUnescapeStr = `${this.prefix}${this.unescapePrefix}(.+?)${this.unescapeSuffix}${this.suffix}`;
    this.regexpUnescape = new RegExp(regexpUnescapeStr, 'g');
    const nestingRegexpStr = `${this.nestingPrefix}(.+?)${this.nestingSuffix}`;
    this.nestingRegexp = new RegExp(nestingRegexpStr, 'g');
  }
  interpolate(str, data, lng, options) {
    let match;
    let value;
    let replaces;
    const defaultData = this.options && this.options.interpolation && this.options.interpolation.defaultVariables || {};
    function regexSafe(val) {
      return val.replace(/\$/g, '$$$$');
    }
    const handleFormat = key => {
      if (key.indexOf(this.formatSeparator) < 0) {
        const path = deepFindWithDefaults(data, defaultData, key, this.options.keySeparator, this.options.ignoreJSONStructure);
        return this.alwaysFormat ? this.format(path, undefined, lng, {
          ...options,
          ...data,
          interpolationkey: key
        }) : path;
      }
      const p = key.split(this.formatSeparator);
      const k = p.shift().trim();
      const f = p.join(this.formatSeparator).trim();
      return this.format(deepFindWithDefaults(data, defaultData, k, this.options.keySeparator, this.options.ignoreJSONStructure), f, lng, {
        ...options,
        ...data,
        interpolationkey: k
      });
    };
    this.resetRegExp();
    const missingInterpolationHandler = options && options.missingInterpolationHandler || this.options.missingInterpolationHandler;
    const skipOnVariables = options && options.interpolation && options.interpolation.skipOnVariables !== undefined ? options.interpolation.skipOnVariables : this.options.interpolation.skipOnVariables;
    const todos = [{
      regex: this.regexpUnescape,
      safeValue: val => regexSafe(val)
    }, {
      regex: this.regexp,
      safeValue: val => this.escapeValue ? regexSafe(this.escape(val)) : regexSafe(val)
    }];
    todos.forEach(todo => {
      replaces = 0;
      while (match = todo.regex.exec(str)) {
        const matchedVar = match[1].trim();
        value = handleFormat(matchedVar);
        if (value === undefined) {
          if (typeof missingInterpolationHandler === 'function') {
            const temp = missingInterpolationHandler(str, match, options);
            value = typeof temp === 'string' ? temp : '';
          } else if (options && Object.prototype.hasOwnProperty.call(options, matchedVar)) {
            value = '';
          } else if (skipOnVariables) {
            value = match[0];
            continue;
          } else {
            this.logger.warn(`missed to pass in variable ${matchedVar} for interpolating ${str}`);
            value = '';
          }
        } else if (typeof value !== 'string' && !this.useRawValueToEscape) {
          value = makeString(value);
        }
        const safeValue = todo.safeValue(value);
        str = str.replace(match[0], safeValue);
        if (skipOnVariables) {
          todo.regex.lastIndex += value.length;
          todo.regex.lastIndex -= match[0].length;
        } else {
          todo.regex.lastIndex = 0;
        }
        replaces++;
        if (replaces >= this.maxReplaces) {
          break;
        }
      }
    });
    return str;
  }
  nest(str, fc) {
    let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    let match;
    let value;
    let clonedOptions;
    function handleHasOptions(key, inheritedOptions) {
      const sep = this.nestingOptionsSeparator;
      if (key.indexOf(sep) < 0) return key;
      const c = key.split(new RegExp(`${sep}[ ]*{`));
      let optionsString = `{${c[1]}`;
      key = c[0];
      optionsString = this.interpolate(optionsString, clonedOptions);
      const matchedSingleQuotes = optionsString.match(/'/g);
      const matchedDoubleQuotes = optionsString.match(/"/g);
      if (matchedSingleQuotes && matchedSingleQuotes.length % 2 === 0 && !matchedDoubleQuotes || matchedDoubleQuotes.length % 2 !== 0) {
        optionsString = optionsString.replace(/'/g, '"');
      }
      try {
        clonedOptions = JSON.parse(optionsString);
        if (inheritedOptions) clonedOptions = {
          ...inheritedOptions,
          ...clonedOptions
        };
      } catch (e) {
        this.logger.warn(`failed parsing options string in nesting for key ${key}`, e);
        return `${key}${sep}${optionsString}`;
      }
      delete clonedOptions.defaultValue;
      return key;
    }
    while (match = this.nestingRegexp.exec(str)) {
      let formatters = [];
      clonedOptions = {
        ...options
      };
      clonedOptions = clonedOptions.replace && typeof clonedOptions.replace !== 'string' ? clonedOptions.replace : clonedOptions;
      clonedOptions.applyPostProcessor = false;
      delete clonedOptions.defaultValue;
      let doReduce = false;
      if (match[0].indexOf(this.formatSeparator) !== -1 && !/{.*}/.test(match[1])) {
        const r = match[1].split(this.formatSeparator).map(elem => elem.trim());
        match[1] = r.shift();
        formatters = r;
        doReduce = true;
      }
      value = fc(handleHasOptions.call(this, match[1].trim(), clonedOptions), clonedOptions);
      if (value && match[0] === str && typeof value !== 'string') return value;
      if (typeof value !== 'string') value = makeString(value);
      if (!value) {
        this.logger.warn(`missed to resolve ${match[1]} for nesting ${str}`);
        value = '';
      }
      if (doReduce) {
        value = formatters.reduce((v, f) => this.format(v, f, options.lng, {
          ...options,
          interpolationkey: match[1].trim()
        }), value.trim());
      }
      str = str.replace(match[0], value);
      this.regexp.lastIndex = 0;
    }
    return str;
  }
}

function parseFormatStr(formatStr) {
  let formatName = formatStr.toLowerCase().trim();
  const formatOptions = {};
  if (formatStr.indexOf('(') > -1) {
    const p = formatStr.split('(');
    formatName = p[0].toLowerCase().trim();
    const optStr = p[1].substring(0, p[1].length - 1);
    if (formatName === 'currency' && optStr.indexOf(':') < 0) {
      if (!formatOptions.currency) formatOptions.currency = optStr.trim();
    } else if (formatName === 'relativetime' && optStr.indexOf(':') < 0) {
      if (!formatOptions.range) formatOptions.range = optStr.trim();
    } else {
      const opts = optStr.split(';');
      opts.forEach(opt => {
        if (!opt) return;
        const [key, ...rest] = opt.split(':');
        const val = rest.join(':').trim().replace(/^'+|'+$/g, '');
        if (!formatOptions[key.trim()]) formatOptions[key.trim()] = val;
        if (val === 'false') formatOptions[key.trim()] = false;
        if (val === 'true') formatOptions[key.trim()] = true;
        if (!isNaN(val)) formatOptions[key.trim()] = parseInt(val, 10);
      });
    }
  }
  return {
    formatName,
    formatOptions
  };
}
function createCachedFormatter(fn) {
  const cache = {};
  return function invokeFormatter(val, lng, options) {
    const key = lng + JSON.stringify(options);
    let formatter = cache[key];
    if (!formatter) {
      formatter = fn(getCleanedCode(lng), options);
      cache[key] = formatter;
    }
    return formatter(val);
  };
}
class Formatter {
  constructor() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    this.logger = baseLogger.create('formatter');
    this.options = options;
    this.formats = {
      number: createCachedFormatter((lng, opt) => {
        const formatter = new Intl.NumberFormat(lng, {
          ...opt
        });
        return val => formatter.format(val);
      }),
      currency: createCachedFormatter((lng, opt) => {
        const formatter = new Intl.NumberFormat(lng, {
          ...opt,
          style: 'currency'
        });
        return val => formatter.format(val);
      }),
      datetime: createCachedFormatter((lng, opt) => {
        const formatter = new Intl.DateTimeFormat(lng, {
          ...opt
        });
        return val => formatter.format(val);
      }),
      relativetime: createCachedFormatter((lng, opt) => {
        const formatter = new Intl.RelativeTimeFormat(lng, {
          ...opt
        });
        return val => formatter.format(val, opt.range || 'day');
      }),
      list: createCachedFormatter((lng, opt) => {
        const formatter = new Intl.ListFormat(lng, {
          ...opt
        });
        return val => formatter.format(val);
      })
    };
    this.init(options);
  }
  init(services) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
      interpolation: {}
    };
    const iOpts = options.interpolation;
    this.formatSeparator = iOpts.formatSeparator ? iOpts.formatSeparator : iOpts.formatSeparator || ',';
  }
  add(name, fc) {
    this.formats[name.toLowerCase().trim()] = fc;
  }
  addCached(name, fc) {
    this.formats[name.toLowerCase().trim()] = createCachedFormatter(fc);
  }
  format(value, format, lng) {
    let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    const formats = format.split(this.formatSeparator);
    const result = formats.reduce((mem, f) => {
      const {
        formatName,
        formatOptions
      } = parseFormatStr(f);
      if (this.formats[formatName]) {
        let formatted = mem;
        try {
          const valOptions = options && options.formatParams && options.formatParams[options.interpolationkey] || {};
          const l = valOptions.locale || valOptions.lng || options.locale || options.lng || lng;
          formatted = this.formats[formatName](mem, l, {
            ...formatOptions,
            ...options,
            ...valOptions
          });
        } catch (error) {
          this.logger.warn(error);
        }
        return formatted;
      } else {
        this.logger.warn(`there was no format function for ${formatName}`);
      }
      return mem;
    }, value);
    return result;
  }
}

function removePending(q, name) {
  if (q.pending[name] !== undefined) {
    delete q.pending[name];
    q.pendingCount--;
  }
}
class Connector extends EventEmitter {
  constructor(backend, store, services) {
    let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    super();
    this.backend = backend;
    this.store = store;
    this.services = services;
    this.languageUtils = services.languageUtils;
    this.options = options;
    this.logger = baseLogger.create('backendConnector');
    this.waitingReads = [];
    this.maxParallelReads = options.maxParallelReads || 10;
    this.readingCalls = 0;
    this.maxRetries = options.maxRetries >= 0 ? options.maxRetries : 5;
    this.retryTimeout = options.retryTimeout >= 1 ? options.retryTimeout : 350;
    this.state = {};
    this.queue = [];
    if (this.backend && this.backend.init) {
      this.backend.init(services, options.backend, options);
    }
  }
  queueLoad(languages, namespaces, options, callback) {
    const toLoad = {};
    const pending = {};
    const toLoadLanguages = {};
    const toLoadNamespaces = {};
    languages.forEach(lng => {
      let hasAllNamespaces = true;
      namespaces.forEach(ns => {
        const name = `${lng}|${ns}`;
        if (!options.reload && this.store.hasResourceBundle(lng, ns)) {
          this.state[name] = 2;
        } else if (this.state[name] < 0) ; else if (this.state[name] === 1) {
          if (pending[name] === undefined) pending[name] = true;
        } else {
          this.state[name] = 1;
          hasAllNamespaces = false;
          if (pending[name] === undefined) pending[name] = true;
          if (toLoad[name] === undefined) toLoad[name] = true;
          if (toLoadNamespaces[ns] === undefined) toLoadNamespaces[ns] = true;
        }
      });
      if (!hasAllNamespaces) toLoadLanguages[lng] = true;
    });
    if (Object.keys(toLoad).length || Object.keys(pending).length) {
      this.queue.push({
        pending,
        pendingCount: Object.keys(pending).length,
        loaded: {},
        errors: [],
        callback
      });
    }
    return {
      toLoad: Object.keys(toLoad),
      pending: Object.keys(pending),
      toLoadLanguages: Object.keys(toLoadLanguages),
      toLoadNamespaces: Object.keys(toLoadNamespaces)
    };
  }
  loaded(name, err, data) {
    const s = name.split('|');
    const lng = s[0];
    const ns = s[1];
    if (err) this.emit('failedLoading', lng, ns, err);
    if (data) {
      this.store.addResourceBundle(lng, ns, data);
    }
    this.state[name] = err ? -1 : 2;
    const loaded = {};
    this.queue.forEach(q => {
      pushPath(q.loaded, [lng], ns);
      removePending(q, name);
      if (err) q.errors.push(err);
      if (q.pendingCount === 0 && !q.done) {
        Object.keys(q.loaded).forEach(l => {
          if (!loaded[l]) loaded[l] = {};
          const loadedKeys = q.loaded[l];
          if (loadedKeys.length) {
            loadedKeys.forEach(n => {
              if (loaded[l][n] === undefined) loaded[l][n] = true;
            });
          }
        });
        q.done = true;
        if (q.errors.length) {
          q.callback(q.errors);
        } else {
          q.callback();
        }
      }
    });
    this.emit('loaded', loaded);
    this.queue = this.queue.filter(q => !q.done);
  }
  read(lng, ns, fcName) {
    let tried = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
    let wait = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : this.retryTimeout;
    let callback = arguments.length > 5 ? arguments[5] : undefined;
    if (!lng.length) return callback(null, {});
    if (this.readingCalls >= this.maxParallelReads) {
      this.waitingReads.push({
        lng,
        ns,
        fcName,
        tried,
        wait,
        callback
      });
      return;
    }
    this.readingCalls++;
    const resolver = (err, data) => {
      this.readingCalls--;
      if (this.waitingReads.length > 0) {
        const next = this.waitingReads.shift();
        this.read(next.lng, next.ns, next.fcName, next.tried, next.wait, next.callback);
      }
      if (err && data && tried < this.maxRetries) {
        setTimeout(() => {
          this.read.call(this, lng, ns, fcName, tried + 1, wait * 2, callback);
        }, wait);
        return;
      }
      callback(err, data);
    };
    const fc = this.backend[fcName].bind(this.backend);
    if (fc.length === 2) {
      try {
        const r = fc(lng, ns);
        if (r && typeof r.then === 'function') {
          r.then(data => resolver(null, data)).catch(resolver);
        } else {
          resolver(null, r);
        }
      } catch (err) {
        resolver(err);
      }
      return;
    }
    return fc(lng, ns, resolver);
  }
  prepareLoading(languages, namespaces) {
    let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    let callback = arguments.length > 3 ? arguments[3] : undefined;
    if (!this.backend) {
      this.logger.warn('No backend was added via i18next.use. Will not load resources.');
      return callback && callback();
    }
    if (typeof languages === 'string') languages = this.languageUtils.toResolveHierarchy(languages);
    if (typeof namespaces === 'string') namespaces = [namespaces];
    const toLoad = this.queueLoad(languages, namespaces, options, callback);
    if (!toLoad.toLoad.length) {
      if (!toLoad.pending.length) callback();
      return null;
    }
    toLoad.toLoad.forEach(name => {
      this.loadOne(name);
    });
  }
  load(languages, namespaces, callback) {
    this.prepareLoading(languages, namespaces, {}, callback);
  }
  reload(languages, namespaces, callback) {
    this.prepareLoading(languages, namespaces, {
      reload: true
    }, callback);
  }
  loadOne(name) {
    let prefix = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    const s = name.split('|');
    const lng = s[0];
    const ns = s[1];
    this.read(lng, ns, 'read', undefined, undefined, (err, data) => {
      if (err) this.logger.warn(`${prefix}loading namespace ${ns} for language ${lng} failed`, err);
      if (!err && data) this.logger.log(`${prefix}loaded namespace ${ns} for language ${lng}`, data);
      this.loaded(name, err, data);
    });
  }
  saveMissing(languages, namespace, key, fallbackValue, isUpdate) {
    let options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    let clb = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : () => {};
    if (this.services.utils && this.services.utils.hasLoadedNamespace && !this.services.utils.hasLoadedNamespace(namespace)) {
      this.logger.warn(`did not save key "${key}" as the namespace "${namespace}" was not yet loaded`, 'This means something IS WRONG in your setup. You access the t function before i18next.init / i18next.loadNamespace / i18next.changeLanguage was done. Wait for the callback or Promise to resolve before accessing it!!!');
      return;
    }
    if (key === undefined || key === null || key === '') return;
    if (this.backend && this.backend.create) {
      const opts = {
        ...options,
        isUpdate
      };
      const fc = this.backend.create.bind(this.backend);
      if (fc.length < 6) {
        try {
          let r;
          if (fc.length === 5) {
            r = fc(languages, namespace, key, fallbackValue, opts);
          } else {
            r = fc(languages, namespace, key, fallbackValue);
          }
          if (r && typeof r.then === 'function') {
            r.then(data => clb(null, data)).catch(clb);
          } else {
            clb(null, r);
          }
        } catch (err) {
          clb(err);
        }
      } else {
        fc(languages, namespace, key, fallbackValue, clb, opts);
      }
    }
    if (!languages || !languages[0]) return;
    this.store.addResource(languages[0], namespace, key, fallbackValue);
  }
}

function get() {
  return {
    debug: false,
    initImmediate: true,
    ns: ['translation'],
    defaultNS: ['translation'],
    fallbackLng: ['dev'],
    fallbackNS: false,
    supportedLngs: false,
    nonExplicitSupportedLngs: false,
    load: 'all',
    preload: false,
    simplifyPluralSuffix: true,
    keySeparator: '.',
    nsSeparator: ':',
    pluralSeparator: '_',
    contextSeparator: '_',
    partialBundledLanguages: false,
    saveMissing: false,
    updateMissing: false,
    saveMissingTo: 'fallback',
    saveMissingPlurals: true,
    missingKeyHandler: false,
    missingInterpolationHandler: false,
    postProcess: false,
    postProcessPassResolved: false,
    returnNull: false,
    returnEmptyString: true,
    returnObjects: false,
    joinArrays: false,
    returnedObjectHandler: false,
    parseMissingKeyHandler: false,
    appendNamespaceToMissingKey: false,
    appendNamespaceToCIMode: false,
    overloadTranslationOptionHandler: function handle(args) {
      let ret = {};
      if (typeof args[1] === 'object') ret = args[1];
      if (typeof args[1] === 'string') ret.defaultValue = args[1];
      if (typeof args[2] === 'string') ret.tDescription = args[2];
      if (typeof args[2] === 'object' || typeof args[3] === 'object') {
        const options = args[3] || args[2];
        Object.keys(options).forEach(key => {
          ret[key] = options[key];
        });
      }
      return ret;
    },
    interpolation: {
      escapeValue: true,
      format: (value, format, lng, options) => value,
      prefix: '{{',
      suffix: '}}',
      formatSeparator: ',',
      unescapePrefix: '-',
      nestingPrefix: '$t(',
      nestingSuffix: ')',
      nestingOptionsSeparator: ',',
      maxReplaces: 1000,
      skipOnVariables: true
    }
  };
}
function transformOptions(options) {
  if (typeof options.ns === 'string') options.ns = [options.ns];
  if (typeof options.fallbackLng === 'string') options.fallbackLng = [options.fallbackLng];
  if (typeof options.fallbackNS === 'string') options.fallbackNS = [options.fallbackNS];
  if (options.supportedLngs && options.supportedLngs.indexOf('cimode') < 0) {
    options.supportedLngs = options.supportedLngs.concat(['cimode']);
  }
  return options;
}

function noop() {}
function bindMemberFunctions(inst) {
  const mems = Object.getOwnPropertyNames(Object.getPrototypeOf(inst));
  mems.forEach(mem => {
    if (typeof inst[mem] === 'function') {
      inst[mem] = inst[mem].bind(inst);
    }
  });
}
class I18n extends EventEmitter {
  constructor() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let callback = arguments.length > 1 ? arguments[1] : undefined;
    super();
    this.options = transformOptions(options);
    this.services = {};
    this.logger = baseLogger;
    this.modules = {
      external: []
    };
    bindMemberFunctions(this);
    if (callback && !this.isInitialized && !options.isClone) {
      if (!this.options.initImmediate) {
        this.init(options, callback);
        return this;
      }
      setTimeout(() => {
        this.init(options, callback);
      }, 0);
    }
  }
  init() {
    var _this = this;
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let callback = arguments.length > 1 ? arguments[1] : undefined;
    if (typeof options === 'function') {
      callback = options;
      options = {};
    }
    if (!options.defaultNS && options.defaultNS !== false && options.ns) {
      if (typeof options.ns === 'string') {
        options.defaultNS = options.ns;
      } else if (options.ns.indexOf('translation') < 0) {
        options.defaultNS = options.ns[0];
      }
    }
    const defOpts = get();
    this.options = {
      ...defOpts,
      ...this.options,
      ...transformOptions(options)
    };
    if (this.options.compatibilityAPI !== 'v1') {
      this.options.interpolation = {
        ...defOpts.interpolation,
        ...this.options.interpolation
      };
    }
    if (options.keySeparator !== undefined) {
      this.options.userDefinedKeySeparator = options.keySeparator;
    }
    if (options.nsSeparator !== undefined) {
      this.options.userDefinedNsSeparator = options.nsSeparator;
    }
    function createClassOnDemand(ClassOrObject) {
      if (!ClassOrObject) return null;
      if (typeof ClassOrObject === 'function') return new ClassOrObject();
      return ClassOrObject;
    }
    if (!this.options.isClone) {
      if (this.modules.logger) {
        baseLogger.init(createClassOnDemand(this.modules.logger), this.options);
      } else {
        baseLogger.init(null, this.options);
      }
      let formatter;
      if (this.modules.formatter) {
        formatter = this.modules.formatter;
      } else if (typeof Intl !== 'undefined') {
        formatter = Formatter;
      }
      const lu = new LanguageUtil(this.options);
      this.store = new ResourceStore(this.options.resources, this.options);
      const s = this.services;
      s.logger = baseLogger;
      s.resourceStore = this.store;
      s.languageUtils = lu;
      s.pluralResolver = new PluralResolver(lu, {
        prepend: this.options.pluralSeparator,
        compatibilityJSON: this.options.compatibilityJSON,
        simplifyPluralSuffix: this.options.simplifyPluralSuffix
      });
      if (formatter && (!this.options.interpolation.format || this.options.interpolation.format === defOpts.interpolation.format)) {
        s.formatter = createClassOnDemand(formatter);
        s.formatter.init(s, this.options);
        this.options.interpolation.format = s.formatter.format.bind(s.formatter);
      }
      s.interpolator = new Interpolator(this.options);
      s.utils = {
        hasLoadedNamespace: this.hasLoadedNamespace.bind(this)
      };
      s.backendConnector = new Connector(createClassOnDemand(this.modules.backend), s.resourceStore, s, this.options);
      s.backendConnector.on('*', function (event) {
        for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          args[_key - 1] = arguments[_key];
        }
        _this.emit(event, ...args);
      });
      if (this.modules.languageDetector) {
        s.languageDetector = createClassOnDemand(this.modules.languageDetector);
        if (s.languageDetector.init) s.languageDetector.init(s, this.options.detection, this.options);
      }
      if (this.modules.i18nFormat) {
        s.i18nFormat = createClassOnDemand(this.modules.i18nFormat);
        if (s.i18nFormat.init) s.i18nFormat.init(this);
      }
      this.translator = new Translator(this.services, this.options);
      this.translator.on('*', function (event) {
        for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
          args[_key2 - 1] = arguments[_key2];
        }
        _this.emit(event, ...args);
      });
      this.modules.external.forEach(m => {
        if (m.init) m.init(this);
      });
    }
    this.format = this.options.interpolation.format;
    if (!callback) callback = noop;
    if (this.options.fallbackLng && !this.services.languageDetector && !this.options.lng) {
      const codes = this.services.languageUtils.getFallbackCodes(this.options.fallbackLng);
      if (codes.length > 0 && codes[0] !== 'dev') this.options.lng = codes[0];
    }
    if (!this.services.languageDetector && !this.options.lng) {
      this.logger.warn('init: no languageDetector is used and no lng is defined');
    }
    const storeApi = ['getResource', 'hasResourceBundle', 'getResourceBundle', 'getDataByLanguage'];
    storeApi.forEach(fcName => {
      this[fcName] = function () {
        return _this.store[fcName](...arguments);
      };
    });
    const storeApiChained = ['addResource', 'addResources', 'addResourceBundle', 'removeResourceBundle'];
    storeApiChained.forEach(fcName => {
      this[fcName] = function () {
        _this.store[fcName](...arguments);
        return _this;
      };
    });
    const deferred = defer();
    const load = () => {
      const finish = (err, t) => {
        if (this.isInitialized && !this.initializedStoreOnce) this.logger.warn('init: i18next is already initialized. You should call init just once!');
        this.isInitialized = true;
        if (!this.options.isClone) this.logger.log('initialized', this.options);
        this.emit('initialized', this.options);
        deferred.resolve(t);
        callback(err, t);
      };
      if (this.languages && this.options.compatibilityAPI !== 'v1' && !this.isInitialized) return finish(null, this.t.bind(this));
      this.changeLanguage(this.options.lng, finish);
    };
    if (this.options.resources || !this.options.initImmediate) {
      load();
    } else {
      setTimeout(load, 0);
    }
    return deferred;
  }
  loadResources(language) {
    let callback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
    let usedCallback = callback;
    const usedLng = typeof language === 'string' ? language : this.language;
    if (typeof language === 'function') usedCallback = language;
    if (!this.options.resources || this.options.partialBundledLanguages) {
      if (usedLng && usedLng.toLowerCase() === 'cimode' && (!this.options.preload || this.options.preload.length === 0)) return usedCallback();
      const toLoad = [];
      const append = lng => {
        if (!lng) return;
        if (lng === 'cimode') return;
        const lngs = this.services.languageUtils.toResolveHierarchy(lng);
        lngs.forEach(l => {
          if (l === 'cimode') return;
          if (toLoad.indexOf(l) < 0) toLoad.push(l);
        });
      };
      if (!usedLng) {
        const fallbacks = this.services.languageUtils.getFallbackCodes(this.options.fallbackLng);
        fallbacks.forEach(l => append(l));
      } else {
        append(usedLng);
      }
      if (this.options.preload) {
        this.options.preload.forEach(l => append(l));
      }
      this.services.backendConnector.load(toLoad, this.options.ns, e => {
        if (!e && !this.resolvedLanguage && this.language) this.setResolvedLanguage(this.language);
        usedCallback(e);
      });
    } else {
      usedCallback(null);
    }
  }
  reloadResources(lngs, ns, callback) {
    const deferred = defer();
    if (!lngs) lngs = this.languages;
    if (!ns) ns = this.options.ns;
    if (!callback) callback = noop;
    this.services.backendConnector.reload(lngs, ns, err => {
      deferred.resolve();
      callback(err);
    });
    return deferred;
  }
  use(module) {
    if (!module) throw new Error('You are passing an undefined module! Please check the object you are passing to i18next.use()');
    if (!module.type) throw new Error('You are passing a wrong module! Please check the object you are passing to i18next.use()');
    if (module.type === 'backend') {
      this.modules.backend = module;
    }
    if (module.type === 'logger' || module.log && module.warn && module.error) {
      this.modules.logger = module;
    }
    if (module.type === 'languageDetector') {
      this.modules.languageDetector = module;
    }
    if (module.type === 'i18nFormat') {
      this.modules.i18nFormat = module;
    }
    if (module.type === 'postProcessor') {
      postProcessor.addPostProcessor(module);
    }
    if (module.type === 'formatter') {
      this.modules.formatter = module;
    }
    if (module.type === '3rdParty') {
      this.modules.external.push(module);
    }
    return this;
  }
  setResolvedLanguage(l) {
    if (!l || !this.languages) return;
    if (['cimode', 'dev'].indexOf(l) > -1) return;
    for (let li = 0; li < this.languages.length; li++) {
      const lngInLngs = this.languages[li];
      if (['cimode', 'dev'].indexOf(lngInLngs) > -1) continue;
      if (this.store.hasLanguageSomeTranslations(lngInLngs)) {
        this.resolvedLanguage = lngInLngs;
        break;
      }
    }
  }
  changeLanguage(lng, callback) {
    var _this2 = this;
    this.isLanguageChangingTo = lng;
    const deferred = defer();
    this.emit('languageChanging', lng);
    const setLngProps = l => {
      this.language = l;
      this.languages = this.services.languageUtils.toResolveHierarchy(l);
      this.resolvedLanguage = undefined;
      this.setResolvedLanguage(l);
    };
    const done = (err, l) => {
      if (l) {
        setLngProps(l);
        this.translator.changeLanguage(l);
        this.isLanguageChangingTo = undefined;
        this.emit('languageChanged', l);
        this.logger.log('languageChanged', l);
      } else {
        this.isLanguageChangingTo = undefined;
      }
      deferred.resolve(function () {
        return _this2.t(...arguments);
      });
      if (callback) callback(err, function () {
        return _this2.t(...arguments);
      });
    };
    const setLng = lngs => {
      if (!lng && !lngs && this.services.languageDetector) lngs = [];
      const l = typeof lngs === 'string' ? lngs : this.services.languageUtils.getBestMatchFromCodes(lngs);
      if (l) {
        if (!this.language) {
          setLngProps(l);
        }
        if (!this.translator.language) this.translator.changeLanguage(l);
        if (this.services.languageDetector && this.services.languageDetector.cacheUserLanguage) this.services.languageDetector.cacheUserLanguage(l);
      }
      this.loadResources(l, err => {
        done(err, l);
      });
    };
    if (!lng && this.services.languageDetector && !this.services.languageDetector.async) {
      setLng(this.services.languageDetector.detect());
    } else if (!lng && this.services.languageDetector && this.services.languageDetector.async) {
      if (this.services.languageDetector.detect.length === 0) {
        this.services.languageDetector.detect().then(setLng);
      } else {
        this.services.languageDetector.detect(setLng);
      }
    } else {
      setLng(lng);
    }
    return deferred;
  }
  getFixedT(lng, ns, keyPrefix) {
    var _this3 = this;
    const fixedT = function (key, opts) {
      let options;
      if (typeof opts !== 'object') {
        for (var _len3 = arguments.length, rest = new Array(_len3 > 2 ? _len3 - 2 : 0), _key3 = 2; _key3 < _len3; _key3++) {
          rest[_key3 - 2] = arguments[_key3];
        }
        options = _this3.options.overloadTranslationOptionHandler([key, opts].concat(rest));
      } else {
        options = {
          ...opts
        };
      }
      options.lng = options.lng || fixedT.lng;
      options.lngs = options.lngs || fixedT.lngs;
      options.ns = options.ns || fixedT.ns;
      options.keyPrefix = options.keyPrefix || keyPrefix || fixedT.keyPrefix;
      const keySeparator = _this3.options.keySeparator || '.';
      let resultKey;
      if (options.keyPrefix && Array.isArray(key)) {
        resultKey = key.map(k => `${options.keyPrefix}${keySeparator}${k}`);
      } else {
        resultKey = options.keyPrefix ? `${options.keyPrefix}${keySeparator}${key}` : key;
      }
      return _this3.t(resultKey, options);
    };
    if (typeof lng === 'string') {
      fixedT.lng = lng;
    } else {
      fixedT.lngs = lng;
    }
    fixedT.ns = ns;
    fixedT.keyPrefix = keyPrefix;
    return fixedT;
  }
  t() {
    return this.translator && this.translator.translate(...arguments);
  }
  exists() {
    return this.translator && this.translator.exists(...arguments);
  }
  setDefaultNamespace(ns) {
    this.options.defaultNS = ns;
  }
  hasLoadedNamespace(ns) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    if (!this.isInitialized) {
      this.logger.warn('hasLoadedNamespace: i18next was not initialized', this.languages);
      return false;
    }
    if (!this.languages || !this.languages.length) {
      this.logger.warn('hasLoadedNamespace: i18n.languages were undefined or empty', this.languages);
      return false;
    }
    const lng = options.lng || this.resolvedLanguage || this.languages[0];
    const fallbackLng = this.options ? this.options.fallbackLng : false;
    const lastLng = this.languages[this.languages.length - 1];
    if (lng.toLowerCase() === 'cimode') return true;
    const loadNotPending = (l, n) => {
      const loadState = this.services.backendConnector.state[`${l}|${n}`];
      return loadState === -1 || loadState === 2;
    };
    if (options.precheck) {
      const preResult = options.precheck(this, loadNotPending);
      if (preResult !== undefined) return preResult;
    }
    if (this.hasResourceBundle(lng, ns)) return true;
    if (!this.services.backendConnector.backend || this.options.resources && !this.options.partialBundledLanguages) return true;
    if (loadNotPending(lng, ns) && (!fallbackLng || loadNotPending(lastLng, ns))) return true;
    return false;
  }
  loadNamespaces(ns, callback) {
    const deferred = defer();
    if (!this.options.ns) {
      if (callback) callback();
      return Promise.resolve();
    }
    if (typeof ns === 'string') ns = [ns];
    ns.forEach(n => {
      if (this.options.ns.indexOf(n) < 0) this.options.ns.push(n);
    });
    this.loadResources(err => {
      deferred.resolve();
      if (callback) callback(err);
    });
    return deferred;
  }
  loadLanguages(lngs, callback) {
    const deferred = defer();
    if (typeof lngs === 'string') lngs = [lngs];
    const preloaded = this.options.preload || [];
    const newLngs = lngs.filter(lng => preloaded.indexOf(lng) < 0);
    if (!newLngs.length) {
      if (callback) callback();
      return Promise.resolve();
    }
    this.options.preload = preloaded.concat(newLngs);
    this.loadResources(err => {
      deferred.resolve();
      if (callback) callback(err);
    });
    return deferred;
  }
  dir(lng) {
    if (!lng) lng = this.resolvedLanguage || (this.languages && this.languages.length > 0 ? this.languages[0] : this.language);
    if (!lng) return 'rtl';
    const rtlLngs = ['ar', 'shu', 'sqr', 'ssh', 'xaa', 'yhd', 'yud', 'aao', 'abh', 'abv', 'acm', 'acq', 'acw', 'acx', 'acy', 'adf', 'ads', 'aeb', 'aec', 'afb', 'ajp', 'apc', 'apd', 'arb', 'arq', 'ars', 'ary', 'arz', 'auz', 'avl', 'ayh', 'ayl', 'ayn', 'ayp', 'bbz', 'pga', 'he', 'iw', 'ps', 'pbt', 'pbu', 'pst', 'prp', 'prd', 'ug', 'ur', 'ydd', 'yds', 'yih', 'ji', 'yi', 'hbo', 'men', 'xmn', 'fa', 'jpr', 'peo', 'pes', 'prs', 'dv', 'sam', 'ckb'];
    const languageUtils = this.services && this.services.languageUtils || new LanguageUtil(get());
    return rtlLngs.indexOf(languageUtils.getLanguagePartFromCode(lng)) > -1 || lng.toLowerCase().indexOf('-arab') > 1 ? 'rtl' : 'ltr';
  }
  static createInstance() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let callback = arguments.length > 1 ? arguments[1] : undefined;
    return new I18n(options, callback);
  }
  cloneInstance() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let callback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
    const forkResourceStore = options.forkResourceStore;
    if (forkResourceStore) delete options.forkResourceStore;
    const mergedOptions = {
      ...this.options,
      ...options,
      ...{
        isClone: true
      }
    };
    const clone = new I18n(mergedOptions);
    if (options.debug !== undefined || options.prefix !== undefined) {
      clone.logger = clone.logger.clone(options);
    }
    const membersToCopy = ['store', 'services', 'language'];
    membersToCopy.forEach(m => {
      clone[m] = this[m];
    });
    clone.services = {
      ...this.services
    };
    clone.services.utils = {
      hasLoadedNamespace: clone.hasLoadedNamespace.bind(clone)
    };
    if (forkResourceStore) {
      clone.store = new ResourceStore(this.store.data, mergedOptions);
      clone.services.resourceStore = clone.store;
    }
    clone.translator = new Translator(clone.services, mergedOptions);
    clone.translator.on('*', function (event) {
      for (var _len4 = arguments.length, args = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
        args[_key4 - 1] = arguments[_key4];
      }
      clone.emit(event, ...args);
    });
    clone.init(mergedOptions, callback);
    clone.translator.options = mergedOptions;
    clone.translator.backendConnector.services.utils = {
      hasLoadedNamespace: clone.hasLoadedNamespace.bind(clone)
    };
    return clone;
  }
  toJSON() {
    return {
      options: this.options,
      store: this.store,
      language: this.language,
      languages: this.languages,
      resolvedLanguage: this.resolvedLanguage
    };
  }
}
const instance = I18n.createInstance();
instance.createInstance = I18n.createInstance;

var _destinations, _destinations2;
instance.use(initReactI18next).init({
  resources: {
    'pt-BR': {
      translations: {
        quoter: {
          btnSubmit: 'Cotar seguro',
          btnSubmitRequote: 'Cotar seguro',
          Destiny: {
            noCoverage: 'Não oferecemos seguro para {{destination}}',
            countriesBlocked: {
              afr: 'Sudão',
              amc: 'Cuba',
              ams: 'Venezuela',
              asi: 'Coréia do Norte, Irã, Síria',
              eur: 'Ucrânia (Crimeia, Donetsk e Lugansk)',
              syc: 'Venezuela'
            },
            inputPlaceHolder: 'Sua cobertura para',
            inputLabel: 'Destino',
            errorMessage: 'Escolha um destino',
            destinations: {
              afr: 'África',
              ams: 'América do Sul',
              amn: 'América do Norte',
              amc: 'América Central',
              asi: 'Ásia',
              eur: 'Europa',
              oce: 'Oceanía',
              syc: 'América do Sul e Caribe',
              vds: 'Vários Destinos',
              mri: 'Marítimo Internacional',
              orm: 'Oriente Médio'
            }
          },
          Dates: {
            inputLabel: 'Data',
            placeholders: {
              date_from: 'Início',
              date_to: 'Fim'
            },
            errorMessage: 'Informe data de inicio e fim',
            confirmDates: 'Confirmar as datas',
            DialogSelectTitle: 'Selecione suas datas',
            weekdays: {
              1: 'Segunda feira',
              2: 'Terça feira',
              3: 'Quarta feira',
              4: 'Quinta feira',
              5: 'Sexta feira',
              6: 'Sábado',
              7: 'Domingo'
            },
            months: {
              1: 'Janeiro',
              2: 'Fevereiro',
              3: 'Março',
              4: 'Abril',
              5: 'Maio',
              6: 'Junho',
              7: 'Julho',
              8: 'Agosto',
              9: 'Setembro',
              10: 'Outubro',
              11: 'Novembro',
              12: 'Dezembro'
            }
          },
          paxs: {
            inputPlaceHolder: 'Passageiro',
            pax: 'Passageiro',
            errorMessage: 'Selecione a quantidade de passageiros',
            accept: 'Continuar',
            paxAgeLimit_1: 'até {{age1}} anos',
            paxAgeLimit_2: 'de {{age2}} a 85 anos'
          }
        }
      }
    },
    es: {
      translations: {
        quoter: {
          btnSubmit: 'Cotizar seguro',
          btnSubmitRequote: 'Volver a cotizar',
          Destiny: {
            inputPlaceHolder: 'Tu cobertura para',
            inputLabel: 'Destino',
            noCoverage: 'No ofrecemos cobertura para {{destination}}',
            countriesBlocked: {
              afr: 'Sudán',
              amc: 'Honduras',
              ams: 'Venezuela',
              asi: 'Corea del Norte, Irán, Síria',
              eur: 'Ucrania (Crimeia, Donetsk y Lugansk)',
              syc: 'Venezuela'
            },
            errorMessage: 'Selecciona un destino',
            destinations: (_destinations = {
              afr: 'África',
              eur: 'Europa',
              syc: 'Sudamérica y Caribe',
              amn: 'Norteamérica',
              asi: 'Asia',
              oce: 'Oceanía'
            }, _destinations["afr"] = 'África', _destinations.vds = 'Varios Destinos', _destinations)
          },
          Dates: {
            inputLabel: 'Fecha',
            placeholders: {
              date_from: 'De inicio',
              date_to: 'De fin'
            },
            errorMessage: 'Debes ingresar fecha de inicio/fin',
            confirmDates: 'Confirmar fechas',
            DialogSelectTitle: 'Selecciona fechas',
            weekdays: {
              1: 'Lunes',
              2: 'Martes',
              3: 'Miércoles',
              4: 'Jueves',
              5: 'Viernes',
              6: 'Sábado',
              7: 'Domingo'
            },
            months: {
              1: 'Enero',
              2: 'Febrero',
              3: 'Marzo',
              4: 'Abril',
              5: 'Mayo',
              6: 'Junio',
              7: 'Julio',
              8: 'Agosto',
              9: 'Septiembre',
              10: 'Octubre',
              11: 'Noviembre',
              12: 'Diciembre'
            }
          },
          paxs: {
            inputPlaceHolder: 'Pasajero',
            pax: 'Pasajero',
            errorMessage: 'Debes ingresar pasajeros',
            accept: 'Aceptar',
            paxAgeLimit_1: 'hasta {{age1}} años',
            paxAgeLimit_2: 'de {{age2}} a 85 años'
          }
        }
      }
    },
    'es-AR': {
      translations: {
        quoter: {
          btnSubmit: 'Cotizar seguro',
          btnSubmitRequote: 'Volver a cotizar',
          Destiny: {
            inputPlaceHolder: 'Tu cobertura para',
            inputLabel: 'Destino',
            noCoverage: 'No ofrecemos cobertura para {{destination}}',
            countriesBlocked: {
              afr: 'Sudán',
              amc: 'Honduras',
              ams: 'Venezuela',
              asi: 'Corea del Norte, Irán, Síria',
              eur: 'Ucrania (Crimeia, Donetsk y Lugansk)',
              syc: 'Venezuela'
            },
            errorMessage: 'Selecciona un destino',
            destinations: (_destinations2 = {
              afr: 'África',
              eur: 'Europa',
              syc: 'Sudamérica y Caribe',
              amn: 'Norteamérica',
              asi: 'Asia',
              oce: 'Oceanía'
            }, _destinations2["afr"] = 'África', _destinations2.vds = 'Varios Destinos', _destinations2)
          },
          Dates: {
            inputLabel: 'Fecha',
            placeholders: {
              date_from: 'De inicio',
              date_to: 'De fin'
            },
            errorMessage: 'Debes ingresar fecha de inicio/fin',
            confirmDates: 'Confirmar fechas',
            DialogSelectTitle: 'Selecciona fechas',
            weekdays: {
              1: 'Lunes',
              2: 'Martes',
              3: 'Miércoles',
              4: 'Jueves',
              5: 'Viernes',
              6: 'Sábado',
              7: 'Domingo'
            },
            months: {
              1: 'Enero',
              2: 'Febrero',
              3: 'Marzo',
              4: 'Abril',
              5: 'Mayo',
              6: 'Junio',
              7: 'Julio',
              8: 'Agosto',
              9: 'Septiembre',
              10: 'Octubre',
              11: 'Noviembre',
              12: 'Diciembre'
            }
          },
          paxs: {
            inputPlaceHolder: 'Pasajero',
            pax: 'Pasajero',
            errorMessage: 'Debes ingresar pasajeros',
            accept: 'Aceptar',
            paxAgeLimit_1: 'hasta {{age1}} años',
            paxAgeLimit_2: 'de {{age2}} a 85 años'
          }
        }
      }
    }
  },
  lng: 'es-AR',
  fallbackLng: 'pt-BR',
  ns: ['translations'],
  defaultNS: 'translations'
});

var LanguageContext = createContext();
var LanguageProvider = function LanguageProvider(_ref) {
  var children = _ref.children;
  var getLanguageFromUrl = function getLanguageFromUrl() {
    var languageCode;
    if (window.localStorage.getItem('lang')) {
      languageCode = window.localStorage.getItem('lang') === 'pt' ? 'br' : 'ar';
    } else {
      var url = window.location.href;
      languageCode = url.split('/')[3];
    }
    switch (languageCode) {
      case 'br':
        return 'pt-BR';
      case 'ar':
        return 'es-AR';
      default:
        if (window.localStorage.getItem('country_id') === '155' || window.localStorage.getItem('lang') === 'pt') {
          return 'pt-BR';
        } else {
          return 'es';
        }
    }
  };
  var _useState = useState(getLanguageFromUrl),
    language = _useState[0],
    setLanguage = _useState[1];
  var changeLanguage = function changeLanguage(newLanguage) {
    instance.changeLanguage(newLanguage);
    setLanguage(newLanguage);
  };
  useEffect(function () {
    instance.changeLanguage(language);
  }, [language]);
  var contextValue = {
    language: language,
    setLanguage: changeLanguage
  };
  return /*#__PURE__*/React__default.createElement(LanguageContext.Provider, {
    value: contextValue
  }, children);
};

var QuoterWrapper = function QuoterWrapper(_ref) {
  var config = _ref.config,
    quoterData = _ref.quoterData;
  var _useContext = useContext(StoreContext),
    dispatch = _useContext[1];
  var isXS = useMediaQuery({
    query: '(max-width: 639px)'
  });
  var isSM = useMediaQuery({
    query: '(min-width: 640px) AND (max-width: 767px'
  });
  var isMD = useMediaQuery({
    query: '(min-width: 768px) AND (max-width: 1023px'
  });
  var isLG = useMediaQuery({
    query: '(min-width: 1024px) AND (max-width: 1280px'
  });
  var isXL = useMediaQuery({
    query: '(min-width: 1280px) AND (max-width: 1535px'
  });
  var is2XL = useMediaQuery({
    query: '(min-width: 1536px)'
  });
  useEffect(function () {
    var breakpoint = 'xs';
    if (isXS) {
      breakpoint = 'xs';
    }
    if (isSM) {
      breakpoint = 'sm';
    }
    if (isMD) {
      breakpoint = 'md';
    }
    if (isLG) {
      breakpoint = 'lg';
    }
    if (isXL) {
      breakpoint = 'xl';
    }
    if (is2XL) {
      breakpoint = '2xl';
    }
    dispatch({
      type: types.setBreakpoint,
      payload: breakpoint
    });
  }, [isXS, isSM, isMD, isLG, isXL, is2XL]);
  useEffect(function () {
    console.log('config from quoterwrapper ', config);
    if (config) {
      dispatch({
        type: types.setConfig,
        payload: config
      });
    }
  }, []);
  return /*#__PURE__*/React__default.createElement(LanguageProvider, null, /*#__PURE__*/React__default.createElement(I18nextProvider, {
    i18n: instance
  }, /*#__PURE__*/React__default.createElement(App, {
    quoterData: quoterData
  })));
};

var QuoterComponent = function QuoterComponent(props) {
  return /*#__PURE__*/React__default.createElement(StoreProvider, null, /*#__PURE__*/React__default.createElement(QuoterWrapper, {
    config: props.config,
    quoterData: props.quoterData,
    view_mode: props.view_mode
  }));
};

export { QuoterComponent };
//# sourceMappingURL=index.modern.js.map
